#!/usr/bin/env bun
/**
 * agentx — a Claude-Code-style CLI for the agentx runtime.
 *
 *   agentx                         # interactive REPL in the current dir
 *   agentx "fix the bug in src/x"  # headless: run once, print, exit
 *   agentx -p "…"                  # explicit headless (pipeable: text→stdout, logs→stderr)
 *   agentx -c "keep going"         # continue the most recent session
 *   agentx -r                      # resume — interactive session picker (or -r <id> for a specific one)
 *   agentx -m openai/gpt-… -C ./pkg --plan "refactor auth"
 *
 * Flags: -m/--model, -C/--cwd, -p/--print, -c/--continue, --resume <id>, --no-stream,
 *        --vfs/--sandbox, --shell, --plan, --ask, --yes, --subagents, --max-steps N, --max-tokens N, --timeout S, -h/--help.
 * Needs at least one provider key (ANTHROPIC_API_KEY / OPENAI_API_KEY / GOOGLE_API_KEY /
 * GROQ_API_KEY; bun auto-loads .env).
 * Filesystem modes: real disk by default (jailed so secrets stay invisible); `--vfs` runs over an
 * in-memory copy (disk untouched); `--shell` adds a real /bin/sh. Mutations prompt for approval when
 * interactive, auto-approve (with a notice) when unattended — `--ask`/`--yes` force the choice.
 * Auto-loads ./.agent/{skills,commands,memory}, ./AGENTS.md|CLAUDE.md, and persists the
 * conversation to ./.agent/sessions/<id>.json so it can be resumed.
 */
import { createInterface } from 'node:readline/promises';
import { existsSync, readFileSync, appendFileSync, mkdirSync, writeFileSync, readdirSync, statSync, unlinkSync } from 'node:fs';
import { homedir } from 'node:os';
import { spawnSync } from 'node:child_process';
import { grabClipboardImage, copyTextToClipboard } from './clipboard';
import { join, resolve, basename, extname, dirname } from 'node:path';
import { AIClient, DecideClient, listModels, listProviders, getProviderFromModel, getModelInfo, resolveModel, isModelSupported, disposeCursorSessions, disposeClaudeCodeSessions } from 'ai.libx.js';
import {
  PermissionPolicy, loadCommands, expandCommand, expandEntry, loadSkills, scanCommands, scanSkills, forComponent, composeHooks, reflectOnRun,
  AgentOptions, exitSessionTool,
  CommandExecutor, registerHeadlessCommands, mkdirp, contentText, imageRefPart, elideStaleImages, estimateTokens, remintImageRefs,
  type Agent, type ChatLike, type HostBridge, type Hooks, type IFilesystem, type CommandInfo, type SkillInfo, type RunResult, type AgentTool, type ToolUse, type UserQuestion, type Message, type ContentPart, type MessageContent, type ReasoningEffort,
} from '../src/index';
import { Scheduler, makeScheduleTools } from '../src/scheduler';
import { loadAgents, type AgentDef } from '../src/agents';
import { Agent as AgentClass } from '../src/Agent';
import { ModelRouter, ModelRouterOptions, resolveModelAlias, jevAvailable } from '../src/index';
import { mountMcpServer, mountMcpServers, type MountedMcp, type McpServerConfig } from '../src/mcp.client';
import { makeMcpToolSearchFromMounted } from '../src/mcp';
import { McpOAuth } from './mcpOAuth';
import { buildAgent, summarizeCall, providerOptionsFor, type CliOptions } from './core';
import { DuplexAgent, type TaskRecord } from '../src/duplex';
import { aecUnavailableHint, bindAckInstinct, bindOverlapInstinct, CutInAbsorber, detectedInputDevice, fakeVoiceParts, matchVoiceCommand, micPermissionStatus, replacedVoiceTurn, splitCut, takeCut, VoiceIO } from './voice';
import { forSpeech } from '../src/voice/engine';
import { JsonlDiagSink } from '../src/voice/diagSink';
import { renderEmotions } from '../src/voice/emotion';
import { Instinct, interruptionNote, type Decider } from '../src/voice/instinct';
import { loadConfig, type AgentConfig } from './config';
import { hooksFromConfig } from './hooks-config';
import { diffLines, statOf, formatDiff } from './diff';
import { SessionStore, titleOf, globalSessionLoad, globalSessionList, sessionRecap, type SessionData, type TurnEvent } from './session';
import { CheckpointStack, type Checkpoints } from './checkpoints';
import { GitCheckpoints } from './gitCheckpoints';
import { parsePermRules, describeRule, loadPersistedRules, loadClaudeSettings, persistRule, mergePerms, isTrusted, trustDir } from './permissions';
import { completeLine, type DirLister } from './complete';
import { createLineEditor, selectMenu, REWIND, isPrintable, type SelectItem, type PasteClassifier, type LineEditor } from './lineEditor';
import { keyInput, releaseKeyInput } from './stdinSource';
import { EditorState, applyKey, isMenuActive } from './lineEditor';
import { MarkdownStream, plainLine, wrapAnsi, renderMdBlock } from './markdown';
import { dotDirs } from './util';
import { OsScheduler, routeTrigger, type OsJobSpec } from './osScheduler';
import { TriggerServer, makeRemoteTriggerTool } from './remoteTrigger';
import type { Trigger } from '../src/scheduler';

// ---- tiny ANSI (auto-off when piped; honors NO_COLOR / FORCE_COLOR) ----
// Precedence: explicit FORCE_COLOR wins (the CI case — force color through a pipe); else NO_COLOR forces
// off (accessibility, https://no-color.org); else fall back to TTY detection.
const forceColor = process.env.FORCE_COLOR != null && process.env.FORCE_COLOR !== '' && process.env.FORCE_COLOR !== '0';
const useColor = forceColor || (!process.env.NO_COLOR && !!process.stdout.isTTY && !!process.stderr.isTTY);
const tty = process.stdout.isTTY && process.stderr.isTTY;
const C = (n: string) => (s: string) => (useColor ? `\x1b[${n}m${s}\x1b[0m` : String(s));
const dim = C('2'), cyan = C('36'), green = C('32'), red = C('31'), bold = C('1'), yellow = C('33');
const italic = C('3'), strike = C('9');
// OSC 8 hyperlink: clickable styled text, ugly URL hidden (matches CC). Falls back to "text (url)" when piped.
const link = (text: string, url: string) => (useColor ? `\x1b]8;;${url}\x1b\\${cyan(text)}\x1b]8;;\x1b\\` : `${text} (${url})`);
const mdPalette = { bold, dim, cyan, italic, strike, green, link }; // shared by MarkdownStream (live) + renderMdBlock (replay)
const err = (s: string) => process.stderr.write(s);
const log = forComponent('cli');

/** This CLI's version, read from package.json (so /version and the banner can't drift from the package). */
const VERSION: string = (() => {
  try { return JSON.parse(readFileSync(new URL('../package.json', import.meta.url), 'utf8')).version ?? '?'; }
  catch { return '?'; }
})();

/** Async update check — fetches the latest version from the npm registry. Resolves to a message or null. */
async function checkForUpdate(current: string): Promise<string | null> {
  if (current === '?') return null;
  const res = await fetch('https://registry.npmjs.org/@livx.cc/agentx/latest', {
    signal: AbortSignal.timeout(3000),
  });
  if (!res.ok) return null;
  const { version: latest } = await res.json() as { version: string };
  if (!latest || latest === current) return null;
  const [cM, cm, cp] = current.split('.').map(Number);
  const [lM, lm, lp] = latest.split('.').map(Number);
  if (cM > lM || (cM === lM && cm > lm) || (cM === lM && cm === lm && cp >= lp)) return null;
  return `Update available: ${current} → ${latest}. Run \`bun add -g @livx.cc/agentx\` to update.`;
}

/** Install the latest agentx globally via bun. Returns true on success. */
function installUpdate(): boolean {
  const r = spawnSync('bun', ['add', '-g', '@livx.cc/agentx'], { stdio: 'inherit' });
  return (r.status ?? 1) === 0;
}

// Mid-turn type-ahead ownership of the cursor's current line. renderStashBuf paints the typed steer/stash
// line (`  › …`) with NO trailing newline while the spinner is stopped; any chrome that prints next must
// clear it first or output glues onto it ("› it  ⚙ shell", "› i  <thinking>"). Chrome chokepoints call
// clearTypeaheadLine(); the spinner tail (self-clearing) and spinner.stop() keep the flag honest. The
// spinner restart repaints the type-ahead as its tail below the chrome. Set by renderStashBuf.
let typeaheadOnLine = false;
const clearTypeaheadLine = () => { if (typeaheadOnLine) { err('\r\x1b[2K'); typeaheadOnLine = false; } };

// ---- thinking spinner (TTY only; cleared the instant real output arrives) ----
const spinner = (() => {
  const frames = ['⠋', '⠙', '⠹', '⠸', '⠼', '⠴', '⠦', '⠧', '⠇', '⠏'];
  let timer: ReturnType<typeof setInterval> | undefined;
  let i = 0;
  let t0 = 0; // wall-clock start of the CURRENT spin run (elapsed survives stop/start within a turn via turnStart)
  return {
    /** Anchor for the elapsed counter; runTurn sets it once per turn so tool-call stop/start cycles don't reset it. */
    turnStart: 0,
    /** Mid-turn type-ahead, rendered as the frame's tail — the spinner repaints its row every 90ms,
     *  so the typed text must live INSIDE the frame or it gets erased on the next tick. */
    tail: undefined as (() => string) | undefined,
    get active() { return !!timer; },
    start(label = 'thinking…') {
      if (!tty || timer) return; // no-op when piped or already spinning
      t0 = this.turnStart || Date.now();
      timer = setInterval(() => {
        if (isMenuActive()) return; // a picker owns the screen — ticking would paint over it (and queue behind its output hold)
        const secs = Math.round((Date.now() - t0) / 1000);
        const tail = this.tail?.();
        err('\r\x1b[2K' + dim(`  ${frames[i = (i + 1) % frames.length]} ${label} ${secs ? `${secs}s · ` : ''}esc to interrupt`) + (tail ? '  ' + tail : ''));
      }, 90);
    },
    stop() { if (timer) { clearInterval(timer); timer = undefined; err('\r\x1b[2K'); typeaheadOnLine = false; } }, // the tail (type-ahead) went with it
  };
})();

/** Set the terminal tab/window title (OSC 0) — session topic, like CC. No-op when piped. */
const setTermTitle = (t: string) => { if (tty) err(`\x1b]0;${t.replace(/[\x00-\x1f]/g, ' ').slice(0, 80)}\x07`); };

// The in-flight turn's aborter (Esc / Ctrl-C / SIGINT call .abort()); null when idle at the prompt.
let activeTurn: AbortController | null = null;
/** Programmatic Esc — abort the in-flight turn (tests; the REPL keymap calls activeTurn directly). */
export const abortActiveTurn = (): void => activeTurn?.abort();
// Set by the ExitSession tool when the model decides the user wants to quit (voice: "ok bye").
let exitRequested = false;

// Input stash: type-ahead queue for input entered while a turn is running.
const inputStash: string[] = [];
// Mid-turn type-ahead editor (a full EditorState: backspace/arrows/kill-keys/bracketed paste all
// work while a turn streams). Built by the REPL (needs its paste classifiers); null when headless.
let stashEd: EditorState | null = null;
const stashText = (): string => stashEd?.buf ?? '';
// True from the synchronous instant a line submits until its dispatch settles — keys buffered
// behind the Enter in the same input burst land here (activeTurn is only set a few awaits later).
let dispatchPending = false;

// Latest TodoWrite plan (captured off the tool call) — pinned compactly into the idle footer.
let latestTodos: { content: string; status: string }[] = [];

// ---- arg parsing ----
/** Names a dispatched turn so a later one can REPLACE it (DuplexAgent.send opts — voice re-finalizations). */
type TurnOpts = { id?: string; replaces?: string };

interface Args {
  task?: string; model?: string; cwd?: string; stream: boolean; plan: boolean; ask: boolean; yes: boolean;
  vfs: boolean; shell: boolean | undefined; boddb?: string; seed: boolean;
  subagents: boolean; maxSteps?: number; maxTokens?: number; timeoutMs?: number; reasoning?: ReasoningEffort; help: boolean; version: boolean;
  duplex: boolean; voiceModel?: string; thinkModel?: string | false; voice: boolean;
  cont: boolean; resume?: string; sessionId?: string; fork?: boolean; outputFormat: 'text' | 'json' | 'stream-json'; inputFormat?: 'stream-json';
  allowedTools?: string[]; disallowedTools?: string[]; appendSystemPrompt?: string; voiceSystemPrompt?: string; addDirs?: string[];
  print?: boolean; debug?: boolean; scratch?: boolean; harden?: boolean; hardenNet?: boolean;
  worktree?: string; updateCheck?: boolean; update?: boolean; listModels?: boolean; autoRoute?: boolean;
}
/** Parse a numeric flag, failing fast on a missing/NaN/negative value so a typo can't silently disable a kill-switch. */
function numFlag(raw: string | undefined, flag: string): number {
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0) throw new Error(`invalid ${flag}: ${raw ?? '(missing value)'}`);
  return n;
}

/** Parse `--reasoning` value: off|low|medium|high, or a raw token budget. */
function parseReasoning(raw: string): ReasoningEffort {
  if (raw === 'off' || raw === 'low' || raw === 'medium' || raw === 'high') return raw;
  const n = Number(raw);
  if (Number.isFinite(n) && n > 0) return n;
  throw new Error(`invalid --reasoning: ${raw} (use off|low|medium|high or a token budget)`);
}

export function parseArgs(argv: string[]): Args {
  const a: Args = { stream: true, plan: false, ask: false, yes: false, vfs: false, shell: undefined, seed: false, subagents: false, help: false, version: false, cont: false, outputFormat: 'text', duplex: false, voice: false, scratch: true, updateCheck: true };
  const rest: string[] = [];
  // read the value that follows a flag, failing loudly if it's missing (instead of a surprise default)
  const val = (i: number, flag: string): string => { const v = argv[i]; if (v === undefined) throw new Error(`${flag} requires a value`); return v; };
  for (let i = 0; i < argv.length; i++) {
    const x = argv[i];
    if (x === '-h' || x === '--help') a.help = true;
    else if (x === '-v' || x === '--version') a.version = true;
    else if (x === '-m' || x === '--model') {
      // bare `-m` (no value / next is a flag), or `-m list|ls|?` → list all models and exit; else set the model.
      const nxt = argv[i + 1];
      if (nxt === undefined || nxt.startsWith('-') || nxt === 'list' || nxt === 'ls' || nxt === '?') {
        a.listModels = true;
        if (nxt === 'list' || nxt === 'ls' || nxt === '?') i++;
      } else a.model = argv[++i];
      // `-m auto`: per-turn routing (haiku|sonnet main + opus offload) — the main session starts on sonnet.
      if (a.model === 'auto') { a.autoRoute = true; a.model = autoModels().standard; }
    }
    else if (x === '-C' || x === '--cwd') a.cwd = val(++i, x);
    else if (x === '-p' || x === '--print') {
      // CC-style: `-p "task"` uses the inline value; bare `-p` (piped) reads the prompt from stdin.
      const nxt = argv[i + 1];
      if (nxt !== undefined && !nxt.startsWith('-')) a.task = argv[++i];
      a.print = true;
    }
    else if (x === '--debug' || x === '--verbose') a.debug = true;
    else if (x === '-c' || x === '--continue') a.cont = true;
    else if (x === '--resume' || x === '-r') {
      // CC-style: `-r <id>` resumes that session; bare `-r` (no id / next is a flag) opens the picker.
      const nxt = argv[i + 1];
      a.resume = nxt !== undefined && !nxt.startsWith('-') ? argv[++i] : ''; // '' = sentinel → interactive pick
    }
    else if (x === '--no-stream') a.stream = false;
    else if (x === '--plan') a.plan = true;
    else if (x === '--ask') a.ask = true;
    else if (x === '--yes' || x === '-y') a.yes = true;
    else if (x === '--vfs' || x === '--sandbox') a.vfs = true;
    else if (x === '--scratch') a.scratch = true;
    else if (x === '--no-scratch') a.scratch = false;
    else if (x === '--update') a.update = true;
    else if (x === '--no-update-check') a.updateCheck = false;
    else if (x === '--boddb') a.boddb = val(++i, x);
    else if (x === '--seed') a.seed = true;
    else if (x === '--shell') a.shell = true;
    else if (x === '--no-shell') a.shell = false;
    else if (x === '--harden') a.harden = true;
    else if (x === '--harden-net') { a.harden = true; a.hardenNet = true; } // hardened, but outbound network stays allowed
    else if (x === '--subagents') a.subagents = true;
    else if (x === '--duplex') a.duplex = true;
    else if (x === '--conversational' || x === '--convo' || x === '--voice') { a.voice = true; a.duplex = true; } // duplex + human conversational register (--convo/--voice = aliases)
    else if (x === '--voice-model') a.voiceModel = val(++i, x);
    else if (x === '--think-model') a.thinkModel = val(++i, x);
    else if (x === '--no-think') a.thinkModel = false;
    else if (x === '--allowedTools' || x === '--allowed-tools') a.allowedTools = val(++i, x).split(',').map((s) => s.trim()).filter(Boolean);
    else if (x === '--disallowedTools' || x === '--disallowed-tools') a.disallowedTools = val(++i, x).split(',').map((s) => s.trim()).filter(Boolean);
    else if (x === '--append-system-prompt') a.appendSystemPrompt = val(++i, x);
    else if (x === '--voice-system-prompt') a.voiceSystemPrompt = val(++i, x);
    else if (x === '--add-dir') (a.addDirs ??= []).push(val(++i, x));
    else if (x === '--reasoning') a.reasoning = parseReasoning(val(++i, x));
    else if (x === '--session-id') a.sessionId = val(++i, x);
    else if (x === '--fork-session') a.fork = true;
    else if (x === '--worktree' || x === '-w') a.worktree = val(++i, x);
    else if (x === '--max-steps') a.maxSteps = numFlag(argv[++i], '--max-steps');
    else if (x === '--max-tokens') a.maxTokens = numFlag(argv[++i], '--max-tokens');
    else if (x === '--timeout') a.timeoutMs = numFlag(argv[++i], '--timeout') * 1000; // seconds → ms
    else if (x === '--output-format') {
      const f = argv[++i];
      if (f !== 'text' && f !== 'json' && f !== 'stream-json') throw new Error(`invalid --output-format: ${f ?? '(missing)'} (use text|json|stream-json)`);
      a.outputFormat = f;
    }
    else if (x === '--input-format') {
      const f = argv[++i];
      if (f !== 'stream-json') throw new Error(`invalid --input-format: ${f ?? '(missing)'} (use stream-json)`);
      a.inputFormat = f;
    }
    else if (x === '--') { rest.push(...argv.slice(i + 1)); break; } // everything after `--` is prompt text
    else if (x.length > 1 && x.startsWith('-')) {
      // An unknown flag used to fall through into `rest` and become the PROMPT — so a typo'd or
      // wrong flag launched a run whose task was literally "-a my-task", which the agent then
      // answered with "what would you like me to do?". Fail loudly instead.
      throw new Error(`unknown flag: ${x} (see --help; to pass it as prompt text, put it after \`--\`)`);
    }
    else rest.push(x);
  }
  if (!a.task && rest.length) a.task = rest.join(' ');
  if (a.boddb && a.vfs) throw new Error('--boddb and --sandbox are mutually exclusive (both are non-disk filesystems; pick one)');
  if (a.seed && !a.boddb) throw new Error('--seed only applies with --boddb (it seeds the database from cwd on first run)');
  if (a.worktree && (a.vfs || a.boddb)) throw new Error('--worktree is a real-disk concept — incompatible with --vfs/--boddb');
  if (a.duplex && (a.task || a.print)) throw new Error('--duplex is interactive-only (a conversational mode) — drop the task/-p');
  if (a.inputFormat && (!a.duplex || a.outputFormat !== 'stream-json')) throw new Error('--input-format stream-json is headless duplex — use it with --duplex (or --voice) and --output-format stream-json');
  if (a.duplex && a.plan) throw new Error('--plan is not supported in --duplex (workers are non-interactive; a plan could never be approved)');
  if (a.voiceSystemPrompt && !a.duplex) throw new Error('--voice-system-prompt only applies with --duplex');
  if (a.voiceModel && !a.duplex) throw new Error('--voice-model only applies with --duplex');
  if (a.thinkModel !== undefined && !a.duplex) throw new Error('--think-model/--no-think only apply with --duplex');
  // --ask/--plan gate on a HUMAN. Detached (no TTY on stdin+stderr) there is none, so every mutating
  // call would deny (--ask) / no plan could ever be approved (--plan) — the run flails to its step cap
  // and reports failure for a reason nobody can see. Fail fast instead. (`-p` at a real terminal keeps
  // working: canPrompt is stdin+stderr, so a piped STDOUT still prompts.)
  if (!canPrompt && (a.ask || a.plan))
    throw new Error(`${a.ask ? '--ask' : '--plan'} needs an interactive terminal to approve on — none is attached (stdin/stderr are not a TTY). For an unattended run use --yes, or pre-authorize with rules in .agent/permissions.json.`);
  return a;
}

const HELP = `agentx — agent runtime CLI

  agentx                          interactive REPL (current dir)
  agentx "<task>"                 run once and exit
  agentx -p "<task>"              headless (stdout=answer, stderr=activity)
  echo "<task>" | agentx -p       headless, prompt read from stdin
  agentx -c "<task>"              continue the most recent session

Flags:
  -m, --model <id>     model (default auto: per-turn haiku/sonnet + opus offload when Jev is configured, else anthropic/claude-sonnet-4-6); bare -m or -m list = list all models
  -C, --cwd <dir>      working dir / project root (default .)
  -c, --continue       resume the most recent session in this dir
  -r, --resume [id]    resume a session — with an id, or bare for an interactive picker
  --no-stream          disable token streaming
                       (default: disk mode — full real filesystem access, like Claude Code)
  --vfs, --sandbox     sandbox mode: work over an in-memory copy of cwd — real disk is NEVER modified
  --no-scratch         disable scratch (on by default): paginate oversized tool output → recoverable
                       scratch files (peek via Grep/Read/Ask) instead of a lossy crop
  --boddb <dir>        database-backed workspace: files live in a persistent bod-db store at <dir>,
                       surviving across runs — real disk is NEVER modified (DB-native; add --seed below)
  --seed               with --boddb: hydrate the store from cwd on the first run (empty DB) only
  --shell              force real /bin/sh on (default in disk mode); --no-shell to use VFS bash only
  --harden             OS-sandbox the real shell (sandbox-exec/bwrap): writes confined to cwd+tmp,
                       outbound network blocked; --harden-net keeps network allowed
  --plan               plan mode: edits blocked until you approve a plan
  --ask                confirm each mutating tool (bash/Shell/Write/Edit/…) — needs a terminal to
                       approve on; rejected up front when detached (use --yes / permissions rules)
  --yes, -y            auto-approve mutating tools (no prompts) — for trusted/unattended runs
  --update             check for updates and install if available (no keys needed)
  --no-update-check    skip the automatic update check on startup
  --verbose, --debug   verbose logs (sets DEBUG=* — tool args, hook decisions, retries)
  --allowedTools <l>   comma-list of tools to allow w/o asking, e.g. "Edit,Shell(git *)"
  --disallowedTools <l> comma-list of tools to deny outright (wins over allow), e.g. "Shell(rm *)"
  --append-system-prompt <t>  extra instructions appended to the system prompt for this run
  --duplex             duplex mode: a fast voice model replies instantly and delegates real work
                       to a background worker agent (-m model); results are re-voiced when ready
  --conversational     duplex with a conversation-native register — short fast turns, fillers,
                       impulsive reactions, human pacing (implies --duplex; aliases: --convo, --voice)
                       with SONIOX_API_KEY + CARTESIA_API_KEY(+VOICE_ID) set: real voice I/O — mic in,
                       spoken replies out (echo-cancelled; speak over it to interrupt)
  --voice-model <id>   with --duplex: the fast voice model (default groq/openai/gpt-oss-120b)
  --think-model <id>   with --duplex: the premium deep-reasoning model (default anthropic/claude-opus-4-6)
  --no-think           with --duplex: disable the Think tier (Act handles everything)
  --voice-system-prompt <text>  with --duplex: appended to the voice (reflex) prompt only (--append-system-prompt → workers)
  --add-dir <path>     mount another directory into the workspace (repeatable; disk mode only)
  --subagents          allow the Task tool (spawn child agents)
  --reasoning <e>      extended thinking: off|low|medium|high or a token budget (anthropic/openai)
  --session-id <id>    use this id for the session (instead of an auto-generated one)
  --fork-session       with -r/-c: branch the resumed session into a new id (source left untouched)
  -w, --worktree <slug>  run in an isolated git worktree (.claude/worktrees/<slug>); auto-cleaned on exit if no changes
  --max-steps <n>      step budget (default 30)
  --max-tokens <n>     token budget kill-switch (default 200000)
  --timeout <sec>      wall-clock kill-switch (default 120)
  --input-format stream-json  with --duplex|--voice + --output-format stream-json: headless duplex for a host app
                       (NDJSON turns/barge-ins on stdin, reflex text + worker speech + task events on stdout)
  --output-format <f>  headless output: text (default) | json (one result object) | stream-json (NDJSON events: {type:text|thinking}… then {type:result})
  -v, --version        print version and exit
  -h, --help

Prompts may reference files with @path (e.g. "explain @src/Agent.ts") — they're inlined.

Exit codes (-p): 0 = done · 1 = failed · 2 = needs input — the agent hit a decision that was yours
to make and paused rather than guess. The question is on stderr (and in --output-format json under
"question"); answer it with: agentx --resume <sessionId> -p "<your answer>".

Providers: set any of ANTHROPIC_API_KEY / OPENAI_API_KEY / GOOGLE_API_KEY / GROQ_API_KEY.
Env files: .env (CWD, bun auto-loads) > install-dir .env > ~/.agent/.env (user-wide).
Bodify secrets: set BODIFY_API_KEY + BODIFY_APP_ID (in ~/.agent/.env) to pull keys from a Bodify app.
Config: ./.agent/config.{ts,js,json} (project) or ~/.agent/config.* (user).
  export default { model, maxSteps, reasoning, permissionMode, editorMode, tools, anthropicWebSearch, apiKeys, baseUrls, hooks, permissions, mcpServers, maxTokens,
                   timeoutMs, maxRepeats, maxToolCalls, keepToolOutputs, maxContextTokens,
                   learnFromMistakes, reflectOnFailure, budget: {…} }
  hooks: { preToolUse|postToolUse|onStop: [{ tool?, command, block? }] } — shell hooks
  permissions: { allow|ask|deny: ["Tool(glob)", …] } — persisted rules (deny>allow>ask; deny applies even with --yes)
  mcpServers: { name: { command, args, env } | { url, headers } } — auto-mounted MCP servers
  learnFromMistakes: capture recurring tool failures as memory lessons (needs .agent/memory)
  reflectOnFailure: on a bad headless run, reflect once via the model + persist a novel lesson
  Precedence: CLI flags > project config > user config > defaults.
Project instructions: ./AGENTS.md or ./CLAUDE.md are auto-loaded (scaffold with /init).
Auto-loaded from ./.agent/: commands/, skills/, memory/, agents/.

REPL shortcuts: !<cmd> runs a shell command inline · #<note> saves a memory · @path inlines a file
REPL slash commands: /help /version /tools /permissions /status /cost /context /transcript /doctor /cwd /model /reasoning /config /rename /compact /memory /rewind /undo /clear /sessions /resume /commands /skills /reload /mcp /init /export /paste /goal /update /exit (duplex: /act /think /tasks /voice /voice-model /think-model /voice-emotions /voice-reasoning)
REPL completion: type / (commands+skills) or @ (files) for a LIVE menu — ↑/↓ select, ⏎/Tab accept, Esc dismiss.
REPL multi-line: Option/Alt+Enter inserts a newline, or end a line with \\ to continue. Esc cancels a running turn / clears the input line; double-Esc jumps back to edit a previous message.
REPL shortcuts: Shift+Tab cycles permission posture (ask → accept-edits → plan) · Alt+T toggles reasoning · Alt+P switches model · Ctrl+O toggles verbose tool output · Ctrl+X Ctrl+E edits the buffer in $EDITOR · → or Tab accepts the dim history ghost-suggestion · Alt+S/Ctrl+S stash/unstash.
REPL steer/stash: type while a turn is running → Enter STEERS it (the text reaches the model at its next step, alongside the next tool result). Alt+S (or Ctrl+S) with text stashes it for AFTER the turn instead; on an empty prompt pops the next stashed entry for editing.
REPL editing (emacs/readline): Ctrl-A/E line start/end · Ctrl-B/F char · Alt-B/F or Alt/Ctrl-←/→ word · Ctrl-W kill word · Ctrl-U/K kill to start/end · Ctrl-Y yank · Ctrl-_ undo · Alt-D kill word fwd · Ctrl-L clear screen. Set editorMode:'vim' (or /config) for modal vim editing.
REPL paste: large/multi-line pastes collapse to a [Pasted #N "…" +N lines] preview (expands on send); a pasted image/file path attaches as [Image]/[File]; Ctrl-V or /paste grabs a clipboard image (macOS).`;

// ---- provider keys (env + config; env wins) ----
/** Newest supported model by releaseDate — the fallback when a requested model is unknown/dropped. */
function newestModel(): string {
  return listModels().slice().sort((a, b) => (getModelInfo(b)?.releaseDate ?? '').localeCompare(getModelInfo(a)?.releaseDate ?? ''))[0] ?? '';
}

/** `-m` / `-m list`: print every supported model, grouped by provider, newest-first. */
function printModels(): void {
  const byProvider = new Map<string, string[]>();
  for (const id of listModels()) {
    const provider = getProviderFromModel(id) ?? 'other';
    if (!byProvider.has(provider)) byProvider.set(provider, []);
    byProvider.get(provider)!.push(id);
  }
  for (const [provider, ids] of Array.from(byProvider.entries()).sort((a, b) => a[0].localeCompare(b[0]))) {
    console.log(`\n${provider}`);
    ids.sort((a, b) => (getModelInfo(b)?.releaseDate ?? '').localeCompare(getModelInfo(a)?.releaseDate ?? ''));
    for (const id of ids) {
      const info = getModelInfo(id);
      const meta = [info?.displayName, info?.releaseDate].filter(Boolean).join(' · ');
      console.log(`  ${id}${meta ? `  (${meta})` : ''}`);
    }
  }
}

/** Resolve a requested model to a supported id; if it's gone from ai.libx.js (stale flag/config OR an
 *  outdated built-in default), warn + fall back to the newest available so the first call never hard-errors. */
function resolveModelOrNewest(model: string | undefined): string {
  const want = model ?? new AgentOptions().model; // single source of truth for the default
  const resolved = resolveModel(want);
  if (isModelSupported(resolved)) return resolved;
  const fallback = newestModel();
  if (!fallback) return want; // no catalog at all → pass through, let the API surface it
  err(yellow(`  ⚠ model "${want}" not available — using ${fallback}\n`));
  return fallback;
}

/** Extra env-var names accepted per provider beyond the `${PROVIDER}_API_KEY` convention. */
const ENV_KEY_ALIASES: Record<string, string[]> = { google: ['GEMINI_API_KEY'] };

/** Load vars from a .env file into process.env (won't override existing). */
function loadEnvFile(file: string): void {
  if (!existsSync(file)) return;
  for (const line of readFileSync(file, 'utf8').split('\n')) {
    const m = line.match(/^\s*(?:export\s+)?([A-Za-z_][A-Za-z0-9_]*)\s*=\s*(.*)$/);
    if (!m || m[1] in process.env) continue;
    let val = m[2].trim();
    if ((val.startsWith('"') && val.endsWith('"')) || (val.startsWith("'") && val.endsWith("'"))) val = val.slice(1, -1);
    else val = val.replace(/\s+#.*$/, '').trim(); // strip inline comment only on unquoted values
    process.env[m[1]] = val;
  }
}

/** Fallback .env loader: CWD .env (bun), install-dir .env, then ~/.agent/.env (user-wide). Never overrides. */
function loadInstallEnv(): void {
  let dir = dirname(import.meta.path);
  for (let i = 0; i < 5 && !existsSync(join(dir, 'package.json')); i++) dir = dirname(dir);
  for (const name of ['.env', '.env.local']) {
    loadEnvFile(join(dir, name));
    loadEnvFile(join(homedir(), '.agent', name));
  }
}

/** Fetch secrets from a Bodify app's secrets store and inject into process.env (won't override existing).
 *  Needs BODIFY_API_KEY + BODIFY_APP_ID. Optional BODIFY_URL (defaults to https://bodify.bod.ee). */
async function loadBodifySecrets(): Promise<void> {
  const apiKey = process.env.BODIFY_API_KEY, appId = process.env.BODIFY_APP_ID;
  if (!apiKey || !appId) return;
  const base = process.env.BODIFY_URL || 'https://bodify.bod.ee';
  try {
    const res = await fetch(`${base}/api/apps/${encodeURIComponent(appId)}/env`, {
      headers: { Authorization: `Bearer ${apiKey}` },
      signal: AbortSignal.timeout(5000),
    });
    if (!res.ok) { console.error(`  bodify secrets: ${res.status} ${res.statusText}`); return; }
    const { values } = await res.json() as { values?: Record<string, string> };
    if (!values) return;
    for (const [k, v] of Object.entries(values)) {
      if (!(k in process.env)) process.env[k] = v;
    }
  } catch (e: any) {
    console.error(`  bodify secrets: ${e.message ?? e}`);
  }
}

/** Map env vars → provider api keys. DYNAMIC over `listProviders()` (convention `${PROVIDER}_API_KEY` +
 *  aliases), so a provider newly added to ai.libx.js is picked up automatically — no edit needed here. */
function apiKeysFromEnv(): Record<string, string> {
  const e = process.env, keys: Record<string, string> = {};
  for (const provider of listProviders()) {
    const names = [`${provider.toUpperCase()}_API_KEY`, ...(ENV_KEY_ALIASES[provider] ?? [])];
    const val = names.map((n) => e[n]).find(Boolean);
    if (val) keys[provider] = val;
  }
  return keys;
}

// ---- terminal host (human-in-the-loop prompts) ----
export function makeHost(format: 'text' | 'json' | 'stream-json' = 'text', opts?: { stream?: boolean }): HostBridge & { flushText: () => void; takeToolCount: () => number } {
  const streamJson = format === 'stream-json'; // emit NDJSON events on stdout as they arrive
  const cleanStdout = format === 'json';        // keep stdout for the single final result object
  // Render markdown only for the STREAMING host on a color-capable TTY; piped / NO_COLOR / json stay raw.
  // Gated on `opts.stream` so the throwaway confirm/permission hosts don't allocate a dead MarkdownStream
  // (and, below, don't each leak a process-lifetime SIGWINCH listener).
  const md = format === 'text' && useColor && opts?.stream ? new MarkdownStream(mdPalette, process.stdout.columns ?? 80) : null;
  if (md) process.on('SIGWINCH', () => md.resize(process.stdout.columns ?? 80)); // reflow streamed markdown on resize
  // Commit any pending streamed-markdown tail (terminating its line) — MUST run before any chrome prints
  // below the stream (tool headers, step notices, the done line), or the renderer's clear-math goes stale
  // and the next delta re-draws the old tail glued onto the new text.
  const flushText = () => { if (md && md.pending()) process.stdout.write(md.commit()); };
  // `thinking_delta` streams to stderr WITHOUT a trailing newline (a continuous dimmed reasoning flow).
  // Any other output that follows (the answer on stdout, a `· notice`, a ⚙ tool header) must first
  // close that open line — else it collides mid-word into the reasoning tail.
  let openReasonLine = false;
  const closeReasonLine = () => { if (openReasonLine) { process.stderr.write('\n'); openReasonLine = false; } };
  const activitySeen = new Set<string>(); // call ids that already printed a ⚙ header (dedup running→completed)
  // Distinct tool calls a SELF-EXECUTING backend reported this turn. claude-code/cursor run their own
  // tools, so those never enter the transcript the done-line counts — without this the summary reads
  // `0 tools` after a visibly tool-heavy turn. Can't reuse activitySeen: it clears every STEP
  // (turn_start) and drops ids on completion, so it tallies nothing.
  const turnActivityIds = new Set<string>();
  const toolNames = new Map<string, string>(); // stream-json: a tool_result carries only its id — name it from its tool_use
  let anonActivityCount = 0; // id-less activities (neither backend does this today) — counted once, on `running`
  return {
    flushText,
    /** Read + reset this turn's self-executing-backend tool tally (see turnActivityIds). */
    takeToolCount: () => { const n = turnActivityIds.size + anonActivityCount; turnActivityIds.clear(); anonActivityCount = 0; return n; },
    notify(e) {
      // Step boundaries draw NO chrome (CC parity — `· step N` between tools read as debug noise):
      // the spinner keeps ticking, and consecutive read-only lookups group straight across steps.
      if (e.kind === 'turn_start') { activitySeen.clear(); typeaheadOnLine = false; return; } // bound the dedup set; clear stale type-ahead ownership so it can't wrongly clear a line next step
      spinner.stop(); // real output arriving → clear the spinner first
      clearTypeaheadLine(); // …and any mid-turn type-ahead the spinner-stopped window let renderStashBuf paint, so chrome doesn't glue onto it
      // NB: flushLookupGroup() runs only on paths that PRINT — structural tool events flow through
      // here too, and flushing on those would break lookups apart instead of grouping them.
      if (e.kind === 'text_delta') {
        flushLookupGroup(); // a pending `read N files` summary lands before the text it precedes
        if (streamJson) process.stdout.write(JSON.stringify({ type: 'text', text: e.message }) + '\n');
        else if (md) { closeReasonLine(); process.stdout.write(md.feed(e.message)); } // line-buffered markdown render
        else { if (!cleanStdout) closeReasonLine(); (cleanStdout ? process.stderr : process.stdout).write(e.message); } // json keeps stdout clean → stderr
        return;
      }
      // extended-thinking (reasoning-native models): a dimmed stderr flow in text mode; a typed event in
      // stream-json; never on stdout in plain json (can't pollute the single result object).
      if (e.kind === 'thinking_delta') {
        if (streamJson) process.stdout.write(JSON.stringify({ type: 'thinking', text: e.message }) + '\n');
        else if (!cleanStdout) {
          // flush any buffered answer (stdout) before the dimmed thinking (stderr) so the two streams
          // stay in order — otherwise late-flushed markdown lands after the thinking it preceded.
          flushLookupGroup();
          flushText();
          process.stderr.write(dim(e.message));
          openReasonLine = true; // unterminated reasoning line — next output must close it
        }
        return;
      }
      // Display-only tool activity from a self-executing backend (Cursor CLI runs its OWN tools, so the
      // Agent never dispatches these — we only get told about them). Render a proper ⚙ header + ⎿ result
      // card from the structured name/input/output, not the bare `· → mcp` notice line.
      if (e.kind === 'tool_activity') {
        const a = e as Extract<typeof e, { kind: 'tool_activity' }>; // catch-all `{kind:string}` member defeats narrowing
        if (a.id) turnActivityIds.add(a.id); // dedup running→completed: one call, one tally
        else if (a.status === 'running') anonActivityCount++;
        flushLookupGroup();
        closeReasonLine();
        flushText();
        if (streamJson) { process.stdout.write(JSON.stringify({ type: 'tool_activity', id: a.id, name: a.name, input: a.input, output: a.output, status: a.status }) + '\n'); return; }
        // Cursor's native `mcp` call to `custom-user-tools` is only the TRANSPORT for a host tool the Agent
        // dispatches itself (and renders via its own ⚙/⎿ chrome) — drawing it too doubles every call as a raw
        // `mcp {"providerIdentifier":…}` row. Still tallied above and persisted as a delegated tool.
        if ((a.input as { providerIdentifier?: string } | undefined)?.providerIdentifier === 'custom-user-tools') { spinner.start(); return; }
        const clip = (s: string) => (s.length > 200 ? s.slice(0, 200) + '…' : s);
        const terminal = a.status === 'completed' || a.status === 'error';
        // ⚙ header once per call — on the first transition we see (running usually, but tolerate a lone completed).
        if (!terminal || !a.id || !activitySeen.has(a.id)) {
          if (a.id) activitySeen.add(a.id);
          const argHint = summarizeCall(a.name, a.input) + (a.truncated?.args ? ' …' : '');
          err(cyan(`  ⚙ ${a.name}`) + (argHint ? dim(' ' + argHint) : '') + '\n');
        }
        // ⎿ result body on the terminal transition (Cursor carries `result` on completed/error).
        if (terminal) {
          if (a.id) activitySeen.delete(a.id);
          const raw = a.output == null ? '' : (typeof a.output === 'string' ? a.output : JSON.stringify(a.output));
          const text = raw.replace(/\s+$/, '');
          const paint = a.status === 'error' ? red : dim;
          if (!text) { if (a.status === 'error') err(paint('    ⎿ (error)\n')); spinner.start(); return; }
          const lines = text.split('\n');
          const shown = lines.slice(0, previewLines());
          err(paint('    ⎿ ' + clip(shown[0])) + '\n');
          for (const ln of shown.slice(1)) err(dim('      ' + clip(ln)) + '\n');
          const more = lines.length - shown.length;
          if (more > 0 || a.truncated?.result) err(dim(`      … (${more > 0 ? `+${more} more line${more > 1 ? 's' : ''}` : 'truncated by backend'}${a.truncated?.result && more > 0 ? ', truncated' : ''} · ctrl+o expands)\n`));
        }
        // Self-executing backend (Cursor): the model is still working between tool cards — keep the
        // spinner ticking so mid-turn type-ahead rides its self-clearing tail (line 2798) instead of
        // being left as bare stash residue the NEXT card collides with. Mirrors postToolUse's start().
        spinner.start();
        return;
      }
      // Structural events (tool_use/tool_result/tool_result_image/turn_start) have dedicated renderers
      // (displayHooks ⚙ chrome) — never dump their raw `kind` as a `· notice`, or the
      // result preview gets sandwiched between junk `· tool_use`/`· tool_result` labels.
      // agentx's own tool calls (Read, Grep…) as stream-json tool_activity, the shape a delegated runtime's tools already use —
      // without it a host showed nothing while the tools ran, so a turn read as stuck after "Let me go." (#304).
      if (streamJson && (e.kind === 'tool_use' || e.kind === 'tool_result')) {
        const t = e as { kind: string; id: string; name?: string; input?: unknown; output?: string; isError?: boolean };
        if (t.kind === 'tool_use') toolNames.set(t.id, t.name ?? '');
        const name = toolNames.get(t.id);
        if (t.kind === 'tool_result') toolNames.delete(t.id);
        const line = t.kind === 'tool_use' ? { type: 'tool_activity', id: t.id, name: t.name, input: t.input, status: 'running' }
          : { type: 'tool_activity', id: t.id, ...(name ? { name } : {}), output: typeof t.output === 'string' ? t.output.slice(0, 4000) : t.output, status: t.isError ? 'error' : 'completed' };
        process.stdout.write(JSON.stringify(line) + '\n');
        return;
      }
      if (!('message' in e)) return;
      // A step retry (model stalled / returned nothing / connection dropped) — a host shows it live, else it reads as a hang.
      if (streamJson && e.kind === 'retry') process.stdout.write(JSON.stringify({ type: 'notice', kind: 'retry', text: e.message }) + '\n');
      flushLookupGroup();
      closeReasonLine(); // close an open reasoning line so the notice starts fresh (not glued mid-word)
      flushText(); // finish the in-progress line before a notice
      err(dim(`  · ${e.message}\n`)); // tool-activity notices stay on stderr (human chrome) for every format
    },
    async confirm(prompt) {
      // arrow-select on a TTY; fall back to a typed y/N when piped (selectMenu → null)
      const v = await selectMenu(process.stderr, { title: `? ${prompt}`, items: [{ label: 'Yes', value: 'y' }, { label: 'No', value: 'n' }], current: 'n' });
      if (v !== null) return v === 'y';
      const io = createInterface({ input: keyInput, output: process.stderr });
      try { return /^y(es)?$/i.test((await io.question(yellow(`  ? ${prompt} [y/N] `))).trim()); } finally { io.close(); }
    },
    // Only offer `ask` when a human can actually SEE the prompt. Headless/piped (`-p`, redirected,
    // no TTY) → omit it: AskUserQuestion then parks the question and ends the run with
    // `needs_input`, handing it to the caller. Prompting into a void instead blocks forever —
    // parkHuman excludes the wait from --timeout, so only an external timeout ever reaps it, and
    // the whole turn is lost. A question nobody can answer must travel UP, not stall.
    ...(canPrompt ? {
      async ask(q: UserQuestion): Promise<string> {
        const title = `? ${q.header ? '[' + q.header + '] ' : ''}${q.question}`;
        spinner.stop(); // a question waits on the human, not the model — no "thinking…" under it
        const v = await selectMenu(process.stderr, { title, items: q.options.map((o) => ({ label: o.label, value: o.label, desc: o.description })) });
        if (v !== null) return v;
        const io = createInterface({ input: keyInput, output: process.stderr });
        try {
          const lines = [yellow('  ' + title)];
          q.options.forEach((o, i) => lines.push(`     ${i + 1}) ${o.label}${o.description ? dim(' — ' + o.description) : ''}`));
          const ans = (await io.question(lines.join('\n') + '\n  > ')).trim();
          const n = Number(ans);
          return Number.isInteger(n) && q.options[n - 1] ? q.options[n - 1].label : ans;
        } finally { io.close(); }
      },
    } : {}),
  };
}

/** Hooks that render tool activity to stderr: a preToolUse header, and for edits a colorized diff.
 *  `opts.background` marks the agent as a BACKGROUND one (duplex workers): its chrome lands async on
 *  top of a live prompt, so it must never drive the foreground spinner (the 'agentx › '/'⠹ thinking…'
 *  flicker), clears the prompt line before printing, and repaints via the callback after. `opts.gate`
 *  is evaluated per call — false renders nothing (minimal mode: task events only). */
/** One-line collapsed summary for read-only lookup results (CC parity — `⎿ read 42 lines` instead of the
 *  full body flooding the scrollback); null → tool keeps the N-line preview (shell output stays useful). */
export function summarizeResult(name: string, text: string): string | null {
  if (name !== 'Read' && name !== 'Glob' && name !== 'Grep') return null;
  if (text === '(no matches)' || text === '(empty file)') return text.slice(1, -1);
  // Read counts ALL lines (blank lines are content); Glob/Grep count nonempty (one path/hit per line)
  const n = name === 'Read' ? text.split('\n').length : text.split('\n').filter((l) => l.trim()).length;
  if (name === 'Read') return `read ${n} line${n === 1 ? '' : 's'}`;
  if (name === 'Glob') return `${n} file${n === 1 ? '' : 's'}`;
  return `${n} match${n === 1 ? '' : 'es'}`;
}

/** Foreground grouped-lookup flush seam — set by the active foreground displayHooks instance; the
 *  host's notify() and the turn-done line call it so a pending `read N files` summary prints before
 *  the text/done chrome it precedes. No-op until a foreground instance registers. */
let flushLookupGroup: () => void = () => {};

function displayHooks(fs?: IFilesystem, opts?: { gate?: () => boolean; background?: () => void; flush?: () => void }): Hooks {
  const EDIT = new Set(['Edit', 'MultiEdit', 'Write']);
  const LOOKUP = new Set(['Read', 'Glob', 'Grep']);
  const before = new Map<string, string>();
  const MAX = 64 * 1024; // skip diffing very large files
  const bg = opts?.background;
  const on = () => !opts?.gate || opts.gate();
  const flush = () => opts?.flush?.(); // commit a pending streamed-markdown tail before printing chrome
  const read = async (p: unknown): Promise<string> => {
    if (!fs || typeof p !== 'string') return '';
    try { return await fs.readFile(p); } catch { return ''; }
  };
  // ⚙ header, word-wrapped at the terminal edge with a hanging indent (long Shell commands used to
  // hard-break mid-word). Leading \n doubles as the closer for any open dimmed-reasoning line.
  const header = (call: ToolUse) => {
    const line = cyan(`  ⚙ ${call.name}`) + dim(' ' + summarizeCall(call.name, call.args));
    return '\n' + (process.stderr.isTTY ? wrapAnsi(line, process.stderr.columns ?? 80, '      ') : line) + '\n';
  };
  // CC-style grouping: CONSECUTIVE read-only lookups (Read/Glob/Grep) print nothing while running;
  // the run flushes as one dim line ("read 2 files · 1 glob") when anything else needs the terminal.
  // A single grouped call keeps the detailed two-line form (header + ⎿ summary) — names beat counts.
  let group: { name: string; header: string; summary: string }[] = [];
  let pendingHeader: string | null = null; // deferred ⚙ header for the in-flight lookup call
  const flushGroup = () => {
    if (!group.length) return;
    flush(); // commit a pending streamed-markdown tail BEFORE chrome (the glued-tail bug class)
    if (group.length === 1) {
      err(group[0].header + dim(`    ⎿ ${group[0].summary} (ctrl+o expands)\n`));
    } else {
      const counts = new Map<string, number>();
      for (const g of group) counts.set(g.name, (counts.get(g.name) ?? 0) + 1);
      const parts = [...counts].map(([name, c]) =>
        name === 'Read' ? `read ${c} file${c > 1 ? 's' : ''}` : `${c} ${name.toLowerCase()}${c > 1 ? 's' : ''}`);
      err('\n' + dim(`  ${parts.join(' · ')} (ctrl+o expands)\n`));
    }
    group = [];
  };
  if (!bg) flushLookupGroup = flushGroup; // foreground instance owns the module-level seam
  return {
    async preToolUse(call) {
      if (call.name === 'TodoWrite' && Array.isArray(call.args?.todos)) latestTodos = call.args.todos; // footer todo panel
      if (!on()) return;
      if (bg) err('\r\x1b[0J'); else { spinner.stop(); clearTypeaheadLine(); } // foreground: stop "thinking…" + wipe any mid-turn type-ahead so the ⚙ header doesn't glue onto it
      flush();
      if (!bg && !verboseOutput && LOOKUP.has(call.name)) { pendingHeader = header(call); return; } // defer — prints only if the result can't collapse
      flushGroup(); // anything non-collapsible ends the lookup run
      err(header(call));
      if (EDIT.has(call.name)) before.set(String(call.args?.path), await read(call.args?.path));
      bg?.();
    },
    onToolOutput(_call, chunk) {
      if (!verboseOutput || !on()) return; // Ctrl+O verbose: live-tail streaming tool output (default chrome stays calm)
      if (bg) err('\r\x1b[0J');
      for (const ln of String(chunk).split('\n')) if (ln.trim()) err(dim(`    ⋮ ${ln.length > 200 ? ln.slice(0, 200) + '…' : ln}\n`));
      bg?.();
    },
    async postToolUse(call, result) {
      if (!on()) return;
      // The spinner is already stopped while a tool runs, so mid-tool type-ahead paints via renderStashBuf
      // (a bare `› …` line, no newline). spinner.stop() only clears when spinning → it wouldn't wipe that
      // line, and the result would glue onto it ("[1 queued]⎿ result"). Clear it first; the finally's
      // spinner.start() repaints the type-ahead as its tail below the result.
      if (bg) err('\r\x1b[0J'); else { spinner.stop(); clearTypeaheadLine(); }
      try {
        if (pendingHeader) {
          const t = String(result).replace(/\s+$/, '');
          // Errors AND permission blocks ("Blocked by hook: …") must never collapse into a
          // "read N lines" count — that would render a denial as a successful lookup.
          const summary = /^(Error|Blocked)/i.test(t) ? null : summarizeResult(call.name, t);
          if (summary) { group.push({ name: call.name, header: pendingHeader, summary }); pendingHeader = null; return; }
          err(pendingHeader); pendingHeader = null; // error/blocked body → surface it via the normal path below
        }
        if (EDIT.has(call.name)) {
          const path = String(call.args?.path);
          const b = before.get(path) ?? '';
          const a = await read(path);
          before.delete(path);
          if (a !== b && b.length < MAX && a.length < MAX) {
            const ops = diffLines(b, a); // diff once → reuse for both the header stat and the body
            const { added, removed } = statOf(ops);
            err(dim(`    ⎿ ${path}  `) + green(`+${added}`) + ' ' + red(`-${removed}`) + '\n');
            const body = formatDiff(ops, { add: green, del: red, dim, context: 2, maxLines: 80 });
            if (body) err(body + '\n');
            return;
          }
        }
        const text = String(result).replace(/\s+$/, '');
        if (text && !/^Edited|^Wrote|^Applied/.test(text)) {
          // CC-style collapse: read-only lookups summarize to one ⎿ line (full body via ctrl+o / /transcript)
          const summary = verboseOutput ? null : summarizeResult(call.name, text);
          if (summary) { err(dim(`    ⎿ ${summary} (ctrl+o expands)\n`)); return; }
          const lines = text.split('\n');
          const shown = lines.slice(0, previewLines());
          for (const ln of shown) err(dim(`    ${ln.length > 200 ? ln.slice(0, 200) + '…' : ln}\n`));
          const more = lines.length - shown.length;
          if (more > 0) err(dim(`    … (+${more} more line${more > 1 ? 's' : ''} · ctrl+o expands)\n`));
        } else if (!text) {
          err(dim('    ⎿ (no output)\n')); // empty result → confirm completion so it doesn't look hung
        }
      } finally {
        if (bg) bg(); // background: repaint the live prompt below the chrome — NEVER the spinner
        else spinner.start(); // foreground: tool done → the model is thinking about the next step
      }
    },
  };
}

/** Synthetic task-event user messages ("[task t2 completed] <multi-KB worker dump>") aren't real user
 *  utterances — replaying the raw payload newline-collapsed + hard-cut at N chars garbles it (slices URLs
 *  mid-token). Condense to a one-line marker. Non-task text is returned unchanged. */
export function condenseReplay(t: string): string {
  // The synthetic greeting instruction ("[session started] First call QuickLook…") is an internal prompt,
  // not a user utterance — hide it on replay/export so resume shows the agent's greeting, not its script (CC parity).
  if (/^\[session (started|resumed)\]/.test(t)) return '';
  const m = t.match(/^\[task (t\d+) (completed|failed|progress|asks)\]\s*([\s\S]*)$/);
  if (!m) return t;
  const [, id, kind, rest] = m;
  const gist = rest.replace(/https?:\/\/\S+/g, '').replace(/[#>*`|_\[\]()]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 80);
  return `⦿ task ${id} ${kind}${gist ? ' — ' + gist + '…' : ''}`;
}

/** Truncate on a whitespace boundary so we never slice a URL or word mid-token (the ' …' marks the cut). */
const clipReplay = (t: string, n: number): string => (t.length > n ? t.slice(0, n).replace(/\S*$/, '').trimEnd() : t);

/** Render a resumed conversation (like CC) so the user sees the context they're continuing: user
 *  prompts, assistant narration, and a condensed line per tool action. Tool *results* are omitted
 *  (verbose) and inlined @-mention blocks are stripped from prompts. Pure → unit-testable. */
export function formatHistory(messages: Message[]): string {
  const shown = messages.filter((m) => m.role !== 'system');
  if (!shown.length) return '';
  const out: string[] = [dim('\n  ── prior conversation ──────────────────────\n')];
  for (const m of shown) {
    if (m.role === 'user') {
      const raw = contentText(m.content).split('\n\n--- @')[0].replace(/\n+/g, ' ').trim(); // drop inlined @file blocks
      const t = condenseReplay(raw);
      if (t) out.push('\n' + bold(cyan('  › ')) + (t.length > 1500 ? clipReplay(t, 1500) + dim(' …') : t) + '\n');
    } else if (m.role === 'assistant') {
      const at = contentText(m.content);
      // Render markdown to ANSI (headings/bold/bullets/code) so resumed history matches live output —
      // raw markers (`**`, `#`, ```` ``` ````) shouldn't wall the user on resume. Indent every line by 2.
      if (at.trim()) out.push(renderMdBlock(at.trim(), mdPalette, (process.stderr.columns ?? 80) - 2).replace(/^/gm, '  ') + '\n');
      for (const tc of m.tool_calls ?? []) {
        let args: any = {};
        try { args = JSON.parse(tc.function.arguments || '{}'); } catch { /* unparsed args */ }
        out.push(cyan('  ⚙ ') + tc.function.name + dim(' ' + summarizeCall(tc.function.name, args)) + '\n');
      }
    }
  }
  out.push(dim('  ────────────────────────────────────────────\n'));
  return out.join('');
}

/** Render the FULL transcript — user prompts, assistant text, every tool call AND its complete
 *  result body — for `/transcript` (the past-turn equivalent of Ctrl-O live verbose; CC's
 *  transcript-viewer parity). `lastTurns` bounds output to the most recent N user turns.
 *  Each tool result is capped at `resultLines` lines with an elision marker. Pure → unit-testable. */
export function formatTranscriptFull(messages: Message[], opts?: { lastTurns?: number; resultLines?: number }): string {
  const cap = opts?.resultLines ?? 200;
  let shown = messages.filter((m) => m.role !== 'system');
  if (opts?.lastTurns) {
    const userIdxs = shown.map((m, i) => (m.role === 'user' ? i : -1)).filter((i) => i >= 0);
    if (userIdxs.length > opts.lastTurns) shown = shown.slice(userIdxs[userIdxs.length - opts.lastTurns]);
  }
  if (!shown.length) return dim('  (empty transcript)\n');
  // index tool results by call id so each renders under its own ⚙ header
  const results = new Map<string, Message>();
  for (const m of shown) if (m.role === 'tool' && m.tool_call_id) results.set(m.tool_call_id, m);
  const clip = (s: string) => {
    const lines = s.split('\n');
    return lines.length > cap ? lines.slice(0, cap).join('\n') + `\n… (+${lines.length - cap} more lines)` : s;
  };
  const out: string[] = [dim('\n  ── transcript ──────────────────────────────\n')];
  for (const m of shown) {
    if (m.role === 'user') {
      const t = contentText(m.content).trim();
      if (t) out.push('\n' + bold(cyan('  › ')) + t.replace(/\n/g, '\n    ') + '\n');
    } else if (m.role === 'assistant') {
      const at = contentText(m.content).trim();
      if (at) out.push(renderMdBlock(at, mdPalette, (process.stderr.columns ?? 80) - 2).replace(/^/gm, '  ') + '\n');
      for (const tc of m.tool_calls ?? []) {
        let args: any = {};
        try { args = JSON.parse(tc.function.arguments || '{}'); } catch { /* unparsed args */ }
        out.push(cyan('  ⚙ ') + tc.function.name + dim(' ' + summarizeCall(tc.function.name, args)) + '\n');
        const r = results.get(tc.id);
        if (r) out.push(dim('    ' + clip(contentText(r.content).trimEnd()).replace(/\n/g, '\n    ')) + '\n');
      }
    }
  }
  out.push(dim('  ────────────────────────────────────────────\n'));
  return out.join('');
}

/** Render a transcript to portable Markdown (no ANSI) for `/export`. Same shape as formatHistory —
 *  user prompts + assistant narration + one quoted line per tool action; tool results omitted. Pure → unit-testable. */
export function exportMarkdown(
  meta: { id: string; title?: string; model?: string; turns?: number; created?: number; tokens?: number; costUsd?: number; costEstimated?: boolean },
  messages: Message[],
): string {
  const shown = messages.filter((m) => m.role !== 'system');
  const stamp = meta.created ? new Date(meta.created).toISOString() : '';
  const cost = meta.costUsd ? ` · ${meta.costEstimated ? '~' : ''}${fmtUsd(meta.costUsd)}` : '';
  const head = [
    `# ${meta.title || 'Conversation'}`,
    '',
    `- **Session:** \`${meta.id}\``,
    ...(meta.model ? [`- **Model:** ${meta.model}`] : []),
    ...(stamp ? [`- **Date:** ${stamp}`] : []),
    ...(meta.turns != null ? [`- **Turns:** ${meta.turns}`] : []),
    ...(meta.tokens ? [`- **Usage:** ${(meta.tokens / 1000).toFixed(1)}k tok${cost}`] : []),
    '', '---', '',
  ];
  const body: string[] = [];
  for (const m of shown) {
    if (m.role === 'user') {
      const t = condenseReplay(contentText(m.content).split('\n\n--- @')[0].trim()); // drop inlined @file blocks
      if (t) body.push('## 👤 User', '', t, '');
    } else if (m.role === 'assistant') {
      const parts: string[] = [];
      const at = contentText(m.content).trim();
      if (at) parts.push(at);
      for (const tc of m.tool_calls ?? []) {
        let args: any = {};
        try { args = JSON.parse(tc.function.arguments || '{}'); } catch { /* unparsed args */ }
        parts.push(`> 🔧 \`${tc.function.name}\` — ${summarizeCall(tc.function.name, args)}`.trimEnd());
      }
      if (parts.length) body.push('## 🤖 Assistant', '', parts.join('\n\n'), '');
    }
  }
  return head.concat(body).join('\n').replace(/\n{3,}/g, '\n\n').trimEnd() + '\n';
}

/** Replay a resumed conversation to stderr. */
function printHistory(messages: Message[]): void {
  const s = formatHistory(messages);
  if (s) err(s);
}

/** Cache-read/write price multipliers over the input rate, by provider (derived from the model
 *  prefix). Anthropic: write 1.25x / read 0.1x. OpenAI & Gemini auto-cache (no write surcharge),
 *  reads 0.5x / 0.25x. DeepSeek read 0.1x. Unknown → no discount (1x/1x, safe over-estimate). */
export function cacheMultipliers(model?: string): { read: number; write: number } {
  const p = (model ?? '').split('/')[0];
  switch (p) {
    case 'anthropic': return { read: 0.1, write: 1.25 };
    case 'openai': return { read: 0.5, write: 1 };
    case 'google': return { read: 0.25, write: 1 };
    case 'deepseek': return { read: 0.1, write: 1 };
    default: return { read: 1, write: 1 };
  }
}

/** USD cost from a model's per-1K pricing (ai.libx.js ModelPricing) + token usage. 0 if unpriced.
 *  Cache-aware: promptTokens includes cache reads/writes — priced at the provider's real multipliers
 *  (via `model`) so cached runs aren't overstated. Omitting `model` falls back to Anthropic's rates. */
export function costOf(
  pricing: { inputCostPer1K: number; outputCostPer1K: number } | undefined,
  promptTokens = 0, completionTokens = 0, cacheCreationTokens = 0, cacheReadTokens = 0, model?: string,
): number {
  if (!pricing) return 0;
  const mult = model ? cacheMultipliers(model) : { read: 0.1, write: 1.25 };
  const fresh = Math.max(0, promptTokens - cacheCreationTokens - cacheReadTokens);
  return (fresh / 1000) * pricing.inputCostPer1K
    + (cacheCreationTokens / 1000) * pricing.inputCostPer1K * mult.write
    + (cacheReadTokens / 1000) * pricing.inputCostPer1K * mult.read
    + (completionTokens / 1000) * pricing.outputCostPer1K;
}

/** `-m auto` tier models; `AGENTX_AUTO_MODELS='{"basic":…,"standard":…,"premium":…}'` overrides (e.g. another provider). */
const autoModels = (): ModelRouterOptions['models'] => ({ ...new ModelRouterOptions().models, ...JSON.parse(process.env.AGENTX_AUTO_MODELS || '{}') });

/** `-m auto`: route each turn through a ModelRouter whose state lives in the session meta, so
 *  `-c`/`--resume` keeps the main model + the reusable opus side-conversation. Undefined when off. */
function autoRouteSend(args: Args, agent: Agent, session: () => SessionData, pof?: ModelRouterOptions['providerOptionsFor']): ((c: MessageContent) => Promise<RunResult>) | undefined {
  if (!args.autoRoute) return undefined;
  const router = new ModelRouter({ costOf: turnCost, models: autoModels(), providerOptionsFor: pof });
  return async (c) => {
    const s = session();
    router.state = s.meta.router ?? {};
    try { return await router.send(agent, c); } finally { s.meta.router = router.state; }
  };
}

/** Cost of one turn at `model`'s rate (looks up ai.libx.js pricing). Subscription `claude-code/<alias>` models
 *  are priced at their API-equivalent Anthropic rate so auto-vs-pinned comparisons stay meaningful. */
export function turnCost(model: string, usage?: { promptTokens?: number; completionTokens?: number; cacheCreationTokens?: number; cacheReadTokens?: number }): number {
  const priced = model.startsWith('claude-code/') ? `anthropic/${resolveModelAlias(model.slice('claude-code/'.length))}` : model;
  return costOf(getModelInfo(priced)?.pricing, usage?.promptTokens ?? 0, usage?.completionTokens ?? 0, usage?.cacheCreationTokens ?? 0, usage?.cacheReadTokens ?? 0, priced);
}

/** Evaluate whether a goal condition has been met, based on recent transcript. */
async function evaluateGoal(ai: ChatLike, condition: string, transcript: Message[], log: (s: string) => void): Promise<{ met: boolean; reason: string }> {
  const recent = transcript
    .filter((m) => m.role === 'assistant')
    .slice(-8)
    .map((m) => {
      const text = typeof m.content === 'string' ? m.content : (m.content as ContentPart[]).filter((p: any) => p.type === 'text').map((p: any) => p.text).join(' ');
      return text.slice(0, 600);
    })
    .join('\n---\n');
  try {
    const r = await ai.chat({
      model: 'anthropic/claude-haiku-4-5',
      stream: false,
      messages: [
        { role: 'system', content: 'You judge whether a goal condition has been met based on conversation transcript. Respond ONLY with JSON: {"met": boolean, "reason": "one sentence"}. Be strict — only met:true if there is clear evidence in the transcript.' },
        { role: 'user', content: `Goal condition: ${condition}\n\nRecent assistant messages:\n${recent}` },
      ],
    }) as { content: string };
    const match = r.content.match(/\{[\s\S]*\}/);
    if (match) return JSON.parse(match[0]);
  } catch (e: any) { log(dim(`  (goal evaluator error: ${e?.message ?? e})\n`)); }
  return { met: false, reason: 'evaluation unclear' };
}

/** Normalize a thrown/returned error into the persisted forensic shape. */
function errInfo(e: any): { message: string; statusCode?: number; code?: string } {
  return { message: String(e?.message ?? e), statusCode: e?.statusCode, code: e?.code };
}

/** Format a USD amount: 2 decimals at $1+, 4 below (agent turns are sub-cent). */
export function fmtUsd(n: number): string {
  return n >= 1 ? `$${n.toFixed(2)}` : `$${n.toFixed(4)}`;
}

/** ~4 chars/token estimate over a transcript (matches the Agent's context-budget heuristic).
 *  Weighs images AS SENT (`sendBytes`): only the recent window's refs cost their file; stale ones cost
 *  the stub — otherwise every old screenshot would stay immortal and fake a "90% full" warning. */
/** Catalog vision flag for a model; undefined (unknown model) = assume it can see. */
const imageInputOf = (m: string): boolean | undefined => { const i = getModelInfo(m); return i ? !!i.imageInput || !!(i as any).vision : undefined; };

export function estimateTranscriptTokens(messages: Message[], keepRecentImages = 1, vision = true): number {
  return estimateTokens(messages, keepRecentImages, vision); // THE estimator — /compact budgets on this too
}

/** One-screen session status (model, dir, fs-mode, tools, permission posture, turns/tokens). Pure. */
export function formatStatus(s: {
  model: string; cwd: string; mode: string; tools: string[]; permissions: string; turns: number; tokens: number; sessionId: string;
  /** Whether the token count is estimated (any turn streamed without provider usage). Defaults to estimated. */
  estimated?: boolean;
}): string {
  const L = (k: string, v: string) => `  ${k.padEnd(12)} ${v}\n`;
  const m = (s.estimated ?? true) ? '~' : '';
  return (
    L('model', s.model) + L('directory', s.cwd) + L('fs mode', s.mode) +
    L('permissions', s.permissions) + L('tools', `${s.tools.length} — ${s.tools.join(', ')}`) +
    L('session', `${s.sessionId} · ${s.turns} turn${s.turns === 1 ? '' : 's'} · ${m}${(s.tokens / 1000).toFixed(1)}k tok`)
  );
}

/** Run a `!cmd` line and return its output — no model call. With `realShell` it shells out to a real
 *  /bin/sh (same executor as the agent's `Shell` tool, so `!open`/`!docker`/`!git` work); otherwise it
 *  runs over the wcli VFS emulator (sandbox/boddb — a real shell would escape the in-memory/db isolation). */
export async function runShellLine(fs: IFilesystem, cmd: string, opts?: { realShell?: boolean; cwd?: string }): Promise<string> {
  // The `!` escape is the user's MANUAL shell. In full-FS mode it must hit a REAL /bin/sh (same executor
  // as the agent's `Shell` tool) — else system binaries (open, docker, pkill, …) 127 in wcli's emulator.
  // Sandbox/boddb keep the wcli VFS emulator: a real shell would escape the in-memory/db isolation to disk.
  if (opts?.realShell) {
    const { makeRealShellTool } = await import('../src/tools.shell');
    // Prefer the user's $SHELL (CC parity: the bang escape is their shell), falling back to /bin/sh.
    const r = await makeRealShellTool({ cwd: opts.cwd ?? process.cwd(), shell: process.env.SHELL }).run({ command: cmd }, {} as any);
    return typeof r === 'string' ? r : r.text;
  }
  const exec = new CommandExecutor(fs);
  registerHeadlessCommands(exec);
  const r = await exec.execute(cmd);
  const out = (r.output ?? '').replace(/\n+$/, '');
  if (r.exitCode !== 0) return `[exit ${r.exitCode}]${r.error ? ' ' + r.error.trim() : ''}${out ? '\n' + out : ''}`;
  return out || '(no output)';
}

/** Resolve a memoryDir (string or string[]) to the primary (write) dir. */
function primaryMemDir(dir: string | string[] | undefined, fallback: string): string {
  return (Array.isArray(dir) ? dir[0] : dir) || fallback;
}

/** Append a `#note` to the memory index (creating the dir/file if needed). Returns the file path. */
export async function appendMemoryNote(fs: IFilesystem, dir: string, text: string): Promise<string> {
  await mkdirp(fs, dir);
  const idx = `${dir}/MEMORY.md`;
  let cur = '';
  try { cur = await fs.readFile(idx); } catch { /* new file */ }
  const head = cur ? cur.replace(/\n*$/, '\n') : '# Memory\n\n';
  await fs.writeFile(idx, head + `- ${text.replace(/\s+/g, ' ').trim()}\n`);
  return idx;
}

const ASK_MUTATING = ['bash', 'Shell', 'Write', 'Edit', 'MultiEdit', 'deleteFile'];
const RESULT_PREVIEW_LINES = 6; // how many lines of a tool result to echo (vs the old single line)
// Ctrl+O (CC parity): toggle full tool-result output vs the N-line preview. Module-level so both
// REPLs, displayHooks and the duplex host read the live value.
let verboseOutput = false;
const previewLines = () => (verboseOutput ? Number.MAX_SAFE_INTEGER : RESULT_PREVIEW_LINES);
const toggleVerbose = (): string => {
  verboseOutput = !verboseOutput;
  err(dim(`  ⌃O verbose output ${verboseOutput ? 'on — full tool results' : 'off — previews'}\n`));
  return verboseOutput ? 'verbose' : 'preview';
};

/** Can we put an interactive prompt in front of a human? (TTY in + err; stdout may be piped). */
const canPrompt = !!(process.stdin.isTTY && process.stderr.isTTY);

export interface PermMode { gate: 'ask' | 'allow'; notice?: string }

/**
 * Default permission posture — CC-inspired: **ask when a human can answer, allow when unattended.**
 * Pure + exported so the decision is unit-testable without a TTY. Precedence:
 *   --yes  → allow (explicit trust, no prompts)
 *   --ask  → ask   (explicit gate; parseArgs rejects it up front when no terminal is attached,
 *                   so this branch always has a human behind it)
 *   interactive (TTY & not json) → ask  (the safe default)
 *   else (headless/piped) → allow, with a one-line notice so it's never silent.
 */
export function resolvePermMode(args: { yes?: boolean; ask?: boolean; outputFormat?: string }, interactiveCapable: boolean): PermMode {
  if (args.yes) return { gate: 'allow' };
  if (args.ask) return { gate: 'ask' };
  if (interactiveCapable && args.outputFormat === 'text') return { gate: 'ask' }; // json/stream-json ⇒ headless
  return { gate: 'allow', notice: 'unattended: mutating tools auto-approved — pass --ask to gate, --vfs to sandbox, --yes to silence' };
}

/** Interactive permission resolver: Yes / No / Always-allow / Always-deny. "Always" persists a rule
 *  to `.agent/permissions.json` (and the policy remembers it for the session). Falls back to a typed
 *  y/N when only stdout is piped, and — with no terminal at all — to a fail-closed deny (a config
 *  `ask:` rule must never block a scripted run on a prompt nobody can see). */
function makeAskResolver(cwd: string) {
  return async (call: ToolUse): Promise<{ decision: 'allow' | 'deny'; remember?: boolean } | undefined> => {
    // Nobody to ask (detached: no TTY on stdin/stderr) → fail closed, per PermissionOptions' contract
    // ("ask with no host ⇒ deny"). Never fall through to the prompts below: with a /dev/null stdin the
    // picker returns null and readline's question() never resolves, so the call blocks until an
    // EXTERNAL timeout reaps the whole turn (parkHuman excludes the wait from --timeout). A config
    // `ask:` rule in a scripted run must deny in milliseconds, not hang for minutes.
    if (!canPrompt) {
      err(dim(`  ⚠ ${call.name} needs approval but no terminal is attached — denied. Use --yes, or an allow rule in .agent/permissions.json.\n`));
      return { decision: 'deny' };
    }
    // Deferred MCP routes through McpCall({name}) — ask/remember by the UNDERLYING tool name, or the
    // user can't see what they're approving and "Always allow" would blanket-approve every MCP tool.
    const name = call.name === 'McpCall' && typeof call.args?.name === 'string' ? String(call.args.name) : call.name;
    const tgt = call.args?.path ? ` on ${call.args.path}` : (call.args?.command ? `: ${String(call.args.command).slice(0, 60)}` : '');
    // The rich picker needs BOTH streams to be a TTY (it draws to stderr, reads keys from stdin).
    // When stderr is captured (e.g. agentx run as a child) we fall back to a typed y/N below —
    // NOT to the picker, whose `null` we reserve strictly for "user cancelled" (Esc = deny).
    const interactive = !!(process.stderr.isTTY && process.stdin.isTTY);
    if (interactive) {
      const v = await selectMenu(process.stderr, {
        title: `? Allow ${name}${tgt}?`,
        items: [
          { label: 'Yes (once)', value: 'allow' },
          { label: 'No', value: 'deny' },
          { label: `Always allow ${name}`, value: 'always-allow' },
          { label: `Always deny ${name}`, value: 'always-deny' },
        ],
        current: 'allow',
      });
      if (v === null) return { decision: 'deny' }; // Esc / Ctrl-C on the picker → deny (CC parity), don't re-ask
      if (v === 'always-allow') { persistRule(cwd, 'allow', name); err(dim(`  ✓ remembered: always allow ${name}\n`)); return { decision: 'allow', remember: true }; }
      if (v === 'always-deny') { persistRule(cwd, 'deny', name); err(dim(`  ✓ remembered: always deny ${name}\n`)); return { decision: 'deny', remember: true }; }
      return { decision: v as 'allow' | 'deny' };
    }
    // Typed y/N fallback (no picker) — abort-aware: a mid-prompt turn-abort (Esc/Ctrl-C flips the
    // signal) rejects io.question so the turn unwinds instantly, instead of hanging (the old
    // "⚠ still cancelling" wedge). Anything that isn't an explicit yes — incl. abort — denies.
    const io = createInterface({ input: keyInput, output: process.stderr });
    try {
      const answer = await io.question(yellow(`  ? Allow ${name}${tgt}? [y/N] `), { signal: activeTurn?.signal });
      return { decision: /^y(es)?$/i.test(answer.trim()) ? 'allow' : 'deny' };
    } catch { return { decision: 'deny' }; } // aborted (or read error) → deny
    finally { io.close(); }
  };
}

/** Adaptive browser trimming, forwarded to Agent.browserTrim. A browser MCP tool returns huge per-call
 *  results (full DOM text + screenshots); the cumulative `maxTokens` budget kill-switch (totalTokens −
 *  0.9·cacheRead ≥ 200k) trips after enough steps no matter how the per-turn SENT context is bounded —
 *  so the fix attacks PER-STEP token GROWTH: crop each oversized browser result to a small stub/spill
 *  (resultBytes) and keep only the most-recent browser bodies verbatim (keepOutputs). The Agent engages
 *  these ONLY after a browser tool is actually invoked (use-based, not config-presence) — so a globally
 *  configured-but-unused browser-bridge never degrades a normal coding session. Config can override. */
const BROWSER_TRIM = { resultBytes: 8_000, keepOutputs: 2 };

function optsFor(args: Args, ai: ChatLike, cfg: Partial<AgentConfig> = {}, extraTools: AgentTool[] = []): CliOptions {
  const perm = resolvePermMode(args, canPrompt);
  if (perm.notice) err(dim(`  ⚠ ${perm.notice}\n`));
  // Persisted config rules (deny>allow>ask) take precedence; the run's ask-default fills the rest.
  // Config rules apply even under --yes, so a `deny Read(./.env)` can't be flag-bypassed.
  const configRules = parsePermRules(cfg.permissions);
  // --allowedTools / --disallowedTools: per-run grants, highest precedence (deny wins, then allow).
  const flagRules = [...parsePermRules({ deny: args.disallowedTools }, true), ...parsePermRules({ allow: args.allowedTools }, true)];
  const askRules = perm.gate === 'ask' ? ASK_MUTATING.map((t) => ({ tool: t, decision: 'ask' as const })) : [];
  const rules = [...flagRules, ...configRules, ...askRules];
  if (configRules.length) err(dim(`  ⊕ ${configRules.length} permission rule(s) from config\n`));
  const model = resolveModelOrNewest(args.model ?? cfg.model);
  return {
    ai,
    model,
    // The real context window drives the context % and `/compact`'s budget (never `maxTokens` — that's spend).
    contextWindow: getModelInfo(model)?.contextWindow ?? 0,
    imageInput: imageInputOf,
    cwd: args.cwd,
    tools: cfg.tools,
    extraTools,
    sandbox: args.vfs,
    boddb: args.boddb,
    seed: args.seed,
    realShell: args.shell,  // undefined → core.ts defaults (on for disk, off for sandbox/boddb)
    osSandbox: args.harden ? { network: !!args.hardenNet } : undefined,
    scratch: args.scratch,
    appendSystemPrompt: args.appendSystemPrompt,
    addDirs: args.addDirs,
    stream: args.stream,
    host: makeHost(args.outputFormat, { stream: true }),
    planMode: args.plan || cfg.planMode || false,
    permissions: rules.length
      ? new PermissionPolicy({ rules, default: 'allow', host: makeHost(), ask: makeAskResolver(resolve(args.cwd ?? process.cwd())) })
      : undefined,
    subagents: args.subagents || cfg.subagents || false,
    maxSteps: args.maxSteps ?? cfg.maxSteps,
    reasoning: args.reasoning ?? cfg.reasoning,
    // kill-switches: CLI flag > config > Agent default
    maxTokens: args.maxTokens ?? cfg.maxTokens,
    timeoutMs: args.timeoutMs ?? cfg.timeoutMs,
    maxRepeats: cfg.maxRepeats,
    maxToolCalls: cfg.maxToolCalls,
    keepToolOutputs: cfg.keepToolOutputs,
    maxContextTokens: cfg.maxContextTokens,
    // Adaptive browser trimming (engages on actual browser tool USE, inside the Agent — see BROWSER_TRIM).
    // Config can tune the values; null-coalesced so a config 0/value still wins over the default.
    browserTrim: cfg.browserTrim ?? BROWSER_TRIM,
    learnFromMistakes: cfg.learnFromMistakes,
    // Forwarded to cursor/* delegations for environment parity (chat-model providers ignore it).
    // Raw config (pre-OAuth): unresolved-oauth http servers are skipped by the cursor mapper.
    mcpServers: cfg.mcpServers,
    // Gates + powers default-on WebSearchAnthropic. Same precedence as the main client (env wins).
    anthropicKey: apiKeysFromEnv().anthropic ?? cfg.apiKeys?.anthropic,
    anthropicWebSearch: cfg.anthropicWebSearch,
  };
}

/** Build the agent and attach fs-aware display hooks (+ any config-declared shell hooks). */
async function makeAgent(args: Args, ai: ChatLike, cfg: Partial<AgentConfig>, extraTools: AgentTool[] = [], hostMcpServers?: string[]): Promise<Agent> {
  const virtual = args.vfs || !!args.boddb;
  if (virtual && args.shell) err(yellow(`  ⚠ --shell ignored in ${args.boddb ? '--boddb' : '--vfs sandbox'} (a real shell would escape to disk)\n`));
  if (virtual && args.addDirs?.length) err(yellow(`  ⚠ --add-dir ignored in ${args.boddb ? '--boddb' : '--vfs sandbox'} (mounted real dirs would escape isolation)\n`));
  if (args.addDirs?.length && !virtual) err(dim(`  + mounted ${args.addDirs.length} extra dir(s): ${args.addDirs.join(', ')}\n`));
  if (args.vfs) err(dim('  ⊝ sandbox (VFS): operating on an in-memory copy — the real disk will not be modified\n'));
  else if (args.boddb) err(dim(`  ⊝ boddb: files live in a database at ${args.boddb}${args.seed ? ' (seeded from cwd on first run)' : ''} — the real disk will not be modified\n`));
  else if (args.shell === false) err(dim('  ⊖ --no-shell: VFS bash only (no real /bin/sh)\n'));
  if (args.harden && !virtual) err(dim(`  ⛨ hardened shell: writes confined to cwd+tmp${args.hardenNet ? '' : ', network blocked'} (sandbox-exec/bwrap)\n`));
  const opts = { ...optsFor(args, ai, cfg, extraTools), hostMcpServers };
  const agent = await buildAgent(opts);
  const display = displayHooks(agent.options.fs, { flush: (agent.options.host as { flushText?: () => void } | undefined)?.flushText });
  agent.options.hooks = cfg.hooks ? composeHooks(display, hooksFromConfig(cfg.hooks)) : display;
  return agent;
}

/** For each `auth:'oauth'` http server, inject a cached bearer token (refreshing if needed). A server whose
 *  token can't be resolved is left as-is — it'll fail mount with a needs-auth hint pointing at `/mcp login`. */
async function resolveOAuth(servers: Record<string, McpServerConfig> = {}, oauth: McpOAuth): Promise<Record<string, McpServerConfig>> {
  const out: Record<string, McpServerConfig> = {};
  for (const [name, cfg] of Object.entries(servers)) {
    if (cfg?.auth === 'oauth' && cfg.url && !cfg.bearerToken) {
      try { out[name] = { ...cfg, bearerToken: await oauth.tokenFor(cfg.url) }; }
      catch { err(yellow(`  MCP "${name}" needs auth — run /mcp login ${name}\n`)); out[name] = cfg; }
    } else out[name] = cfg;
  }
  return out;
}

/** Mount configured MCP servers (one-line summary to stderr); returns the mounted servers. */
async function mountMcp(cfg: Partial<AgentConfig>, oauth?: McpOAuth): Promise<MountedMcp[]> {
  const servers = oauth ? await resolveOAuth(cfg.mcpServers, oauth) : cfg.mcpServers;
  const mounted = await mountMcpServers(servers);
  const n = mounted.reduce((s, m) => s + m.tools.length, 0);
  if (n) err(dim(`  + ${n} MCP tool(s) from ${mounted.map((m) => m.name).join(', ')}\n`));
  return mounted;
}

/** Past this many tools, raw MCP mounts bloat EVERY request's schema (42 browser-bridge tools ≈ 80k
 *  tok/turn on a trivial task) — switch to the deferred ToolSearch/McpCall pair (2 bounded tools). */
const MCP_DEFER_AT = 12;

/** The agent-facing tool surface for mounted MCP servers: raw tools for small sets, the deferred
 *  ToolSearch/McpCall pair beyond MCP_DEFER_AT. Rebuilt on /mcp add/remove/reconnect (the pair's
 *  catalog snapshots `mounted` at build time). */
function mcpAgentTools(mounted: MountedMcp[], opts?: { quiet?: boolean }): AgentTool[] {
  const n = mounted.reduce((s, m) => s + m.tools.length, 0);
  if (n <= MCP_DEFER_AT) return mounted.flatMap((m) => m.tools);
  const { tools } = makeMcpToolSearchFromMounted(mounted);
  if (!opts?.quiet) err(dim(`  ∴ ${n} MCP tools deferred behind ToolSearch (prompt stays lean · /mcp tools lists them)\n`));
  return tools;
}

/** Best-effort shutdown of mounted MCP servers (kills stdio children). */
async function closeMcp(mounted: MountedMcp[]): Promise<void> {
  await Promise.all(mounted.map((m) => m.client.close().catch((e) => log.debug('mcp close failed', e))));
}

const IMG_EXT: Record<string, string> = { '.png': 'image/png', '.jpg': 'image/jpeg', '.jpeg': 'image/jpeg', '.gif': 'image/gif', '.webp': 'image/webp' };

/** Extract `@ref` mentions: bare `@path` (no spaces) or quoted `@"path with spaces"` — the quoted
 *  form is what lets a drag-dropped macOS screenshot ("Screenshot 2026-06-11 at ….png") attach at all.
 *  Bare refs get trailing sentence punctuation stripped (e.g. "@a.ts." / "@a.ts?"). */
export function mentionRefs(line: string): string[] {
  return [...line.matchAll(/(?:^|\s)@(?:"([^"]+)"|(\S+))/g)]
    .map((m) => m[1] ?? m[2].replace(/[?!.,;:)\]}'">]+$/, ''))
    .filter(Boolean);
}

/** `~/` is shell syntax — expand it for every user-typed path (mentions, images, pastes). */
const untilde = (p: string) => (p.startsWith('~/') ? join(homedir(), p.slice(2)) : p);

/** Read `@image.png` mentions from `line` as image REFERENCE parts — the path, not the payload. The
 *  base64 is materialized only in the outgoing request (Agent.trimContext → expandImagesForSend), so the
 *  transcript and session JSON never carry megabytes. Missing/unreadable images are skipped
 *  (expandMentions reports the missing @ref separately). */
export function readImageParts(cwd: string, line: string): ContentPart[] {
  const refs = mentionRefs(line);
  const parts: ContentPart[] = [];
  for (const ref of refs) {
    const mime = IMG_EXT[extname(ref).toLowerCase()];
    if (!mime) continue;
    const abs = ref.startsWith('~/') ? untilde(ref) : resolve(cwd, ref);
    try { statSync(abs); } catch { continue; } // missing/unreadable — skip (same as before)
    parts.push(imageRefPart(abs, mime));
  }
  return parts;
}

/** Classify a pasted blob as a file attachment when it's a drag-dropped/typed path to an existing file.
 *  Returns an `@<abs>` ref the existing mention pipeline attaches (images) or inlines (other files); the
 *  in-buffer placeholder shows `[Image #N]` / `[File name #N]`. Returns null for anything not a clear single
 *  path (so ordinary text pastes are unaffected). Paths with spaces are skipped — the `@\S+` pipeline can't carry them. */
export function pastePathClassifier(cwd: string): PasteClassifier {
  return (text: string) => {
    let t = text.trim();
    if (!t || t.includes('\n')) return null;
    t = t.replace(/\\ /g, ' ').replace(/^['"]|['"]$/g, '');       // unescape drag-drop spaces / strip quotes
    if (!/^(\/|~\/|\.\/|\.\.\/)/.test(t)) return null;           // only obvious path-like pastes
    const abs = t.startsWith('~/') ? untilde(t) : resolve(cwd, t);
    try { if (!statSync(abs).isFile()) return null; } catch { return null; }
    const isImg = !!IMG_EXT[extname(abs).toLowerCase()];
    // spaced paths (default macOS screenshot names!) ride the quoted-mention form
    return { display: isImg ? 'Image' : `File ${basename(abs)}`, ref: /\s/.test(abs) ? `@"${abs}"` : '@' + abs };
  };
}

/** Inline `@path` file mentions: append referenced files' contents so the model sees them (missing → warn, leave as-is). */
/** Resolves an `@server:uri` MCP-resource mention to its text body; null = not an MCP ref (fall through
 *  to file resolution). Set by the REPL once servers are mounted; headless runs leave it unset. */
export let mcpMentionResolver: ((ref: string) => Promise<string | null>) | undefined;
export function setMcpMentionResolver(fn: typeof mcpMentionResolver): void { mcpMentionResolver = fn; }

export async function expandMentions(fs: IFilesystem, line: string): Promise<{ text: string; loaded: string[]; missing: string[] }> {
  const refs = mentionRefs(line);
  if (!refs.length) return { text: line, loaded: [], missing: [] };
  const loaded: string[] = [], missing: string[] = [], blocks: string[] = [];
  for (const ref of refs) {
    if (IMG_EXT[extname(ref).toLowerCase()]) continue; // images are attached as content parts, not inlined as text
    if (loaded.includes(ref) || missing.includes(ref)) continue; // dedupe
    if (ref.includes(':') && mcpMentionResolver) { // @server:uri → MCP resource (CC @-resource mentions)
      const body = await mcpMentionResolver(ref).catch((e) => { log.debug('mcp mention resolve failed', e); return null; });
      if (body != null) { blocks.push(`--- @${ref} ---\n${body}`); loaded.push(ref); continue; }
    }
    const path = untilde(ref);
    try {
      if (await fs.exists(path)) { blocks.push(`--- @${ref} ---\n${await fs.readFile(path)}`); loaded.push(ref); }
      else missing.push(ref);
    } catch { missing.push(ref); }
  }
  return { text: blocks.length ? `${line}\n\n${blocks.join('\n\n')}` : line, loaded, missing };
}

/** The headless `--output-format json` result object for a turn. */
export function jsonResult(res: RunResult, session: SessionData) {
  const lastUser = res.messages.map((m) => m.role).lastIndexOf('user');
  return {
    ok: res.finishReason === 'stop',
    finishReason: res.finishReason,
    ...(res.providerFinishReason ? { providerFinishReason: res.providerFinishReason } : {}),
    text: res.text,
    steps: res.steps,
    tools: res.messages.slice(lastUser).filter((m) => m.role === 'tool').length,
    usage: res.usage,
    ...(res.budget ? { budget: res.budget } : {}),
    ...(res.routing ? { costUsd: res.costUsd, routing: res.routing as unknown as Record<string, unknown> } : {}), // cast: tsup dts hoists a modelRouter import above the shebang
    sessionId: session.meta.id,
    // The parked question, machine-readable — the caller answers it and resumes `sessionId`.
    ...(res.finishReason === 'needs_input' && res.question ? { question: res.question } : {}),
    // 'timeout' too: a provider stall ("model stalled: no stream output for 60s") vs the between-steps idle ceiling (no error).
    ...((res.finishReason === 'error' || res.finishReason === 'empty' || res.finishReason === 'timeout') && res.error ? { error: (res.error as any)?.message ?? String(res.error) } : {}),
  };
}

/**
 * Read one logical input, supporting `\`-continuation for multi-line prompts: a line ending in
 * a backslash drops the `\` and keeps reading; the parts are joined with newlines. `readLine`
 * is injected (the REPL passes a readline `question`; tests pass a scripted reader).
 */
export async function readMultiline(readLine: (continuing: boolean) => Promise<string | null>): Promise<string | null> {
  const parts: string[] = [];
  for (;;) {
    const part = await readLine(parts.length > 0);
    if (part == null) return null; // EOF (Ctrl-D) → exit the REPL
    if (part.endsWith('\\')) { parts.push(part.slice(0, -1)); continue; } // trailing \ → keep going
    parts.push(part);
    return parts.join('\n');
  }
}

/** Send one turn (expanding @file mentions), print the footer, persist the session.
 *  `sendFn` overrides how the turn is dispatched (duplex routes through dx.send's mutex); `agent`
 *  stays the transcript-owning agent (the voice, in duplex) — mentions/fs/signal/cost read off it. */
export async function runTurn(agent: Agent, store: SessionStore, session: SessionData, task: string, cp?: Checkpoints, cwd = process.cwd(), sendFn?: (content: MessageContent) => Promise<RunResult>): Promise<{ ok: boolean; res?: RunResult }> {
  const t0 = Date.now();
  await cp?.begin(task); // open a checkpoint frame so this turn's edits can be /rewound
  const { text, loaded, missing } = await expandMentions(agent.options.fs!, task);
  if (loaded.length) err(dim(`  + inlined ${loaded.map((f) => '@' + f).join(' ')}\n`));
  if (missing.length) err(yellow(`  ⚠ not found: ${missing.map((f) => '@' + f).join(' ')}\n`));
  // @image.png mentions → attach as multimodal content parts (needs a vision model).
  const images = readImageParts(cwd, task);
  if (images.length) err(dim(`  + attached ${images.length} image(s)\n`));
  // Don't waste a model call when the input was ONLY unresolved @-mentions (e.g. "@asd").
  if (missing.length && !loaded.length && !task.replace(/(?:^|\s)@\S+/g, '').trim()) {
    err(dim('  (nothing to send — that file doesn\'t exist)\n'));
    return { ok: false };
  }
  // per-turn aborter: Esc / Ctrl-C / SIGINT flips agent.options.signal so the loop stops between steps.
  const ctrl = new AbortController();
  activeTurn = ctrl;
  agent.options.signal = ctrl.signal;
  // Raw mode stays on for the whole REPL session (the line editor owns it), so the Esc/Ctrl-C
  // keypress is delivered live during the turn — no per-turn stdin juggling needed here.
  const content = images.length ? [{ type: 'text' as const, text }, ...images] : text;
  let res: RunResult;
  spinner.turnStart = t0; // elapsed counter spans the whole turn (tool calls stop/restart the spinner)
  spinner.start(sendFn ? 'voice…' : undefined);
  // Mid-turn persistence: a long agentic turn (many tool steps) otherwise lives ONLY in memory until
  // the turn ends — a crash mid-turn loses the whole transcript. The Agent fires `turn_start` at every
  // step boundary; piggyback on it to flush the committed-so-far transcript to disk.
  const msgsBefore = agent.transcript.length; // for the silent-abort discriminator below
  // Discard any tally a previous turn left behind (the error path below returns before the done-line
  // reads it) — else a failed delegated turn would inflate the NEXT turn's tool count.
  (agent.options.host as { takeToolCount?: () => number } | undefined)?.takeToolCount?.();
  const host = agent.options.host;
  const origNotify = host?.notify?.bind(host);
  if (host && origNotify) host.notify = (e) => {
    if (e.kind === 'turn_start') {
      session.messages = agent.transcript;
      session.meta.updated = Date.now();
      try { store.save(session); } catch (ex) { log.debug('mid-turn session flush failed', ex); }
    }
    return origNotify(e);
  };
  try {
    res = await (sendFn ? sendFn(content) : agent.send(content));
  } catch (e: any) {
    spinner.stop();
    err(red(`  error: ${e?.message ?? e}\n`));
    // record the hard-throw turn too (this path used to persist nothing → no forensic trace)
    (session.meta.events ??= []).push({ ts: t0, durationMs: Date.now() - t0, model: agent.options.model, finishReason: 'error', steps: 0, tools: 0, error: errInfo(e) });
    session.meta.updated = Date.now();
    try { store.save(session); } catch { /* non-fatal */ }
    return { ok: false };
  } finally {
    spinner.stop();
    spinner.turnStart = 0;
    // Only release what is still OURS: duplex turns overlap (a voice turn queued behind this one — e.g. the
    // re-finalization that replaced it — already installed its own controller; clearing it would make that
    // turn un-interruptible).
    if (activeTurn === ctrl) activeTurn = null;
    if (agent.options.signal === ctrl.signal) agent.options.signal = undefined;
    if (host && origNotify) host.notify = origNotify; // unwrap — turns must not stack flush wrappers
  }
  (agent.options.host as { flushText?: () => void } | undefined)?.flushText?.(); // emit any buffered markdown tail
  flushLookupGroup(); // a turn ending on read-only lookups still owes their grouped summary line
  const secs = ((Date.now() - t0) / 1000).toFixed(1);
  const cost = res.costUsd ?? turnCost(agent.options.model, res.usage);
  const m = res.usageEstimated ? '~' : ''; // exact usage (provider-reported) drops the tilde
  const tok = res.usage?.totalTokens ? `${m}${(res.usage.totalTokens / 1000).toFixed(1)}k tok · ${cost > 0 ? m + fmtUsd(cost) + ' · ' : ''}` : '';
  // Both agentx-dispatched tools AND a delegated runtime's own tools (claude-code/cursor) now land in
  // the transcript as `role:'tool'` messages, so the filter counts both. Still drain the host's
  // activity tally to reset it per-turn — its value is redundant now (same tools, same source).
  (agent.options.host as { takeToolCount?: () => number } | undefined)?.takeToolCount?.();
  // Anchor on msgsBefore, NOT the last `user` message: the loop itself pushes user-role messages
  // mid-turn (the delegated closure nudge, injection drains), so a lastIndexOf('user') slice starts
  // AFTER the turn's real work and reported "0 tools" for a turn that ran six.
  const tools = res.messages.slice(msgsBefore).filter((m) => m.role === 'tool').length;
  const ok = res.finishReason === 'stop';
  const shortId = session.meta.id.replace(/^\d{8}-/, ''); // drop YYYYMMDD- prefix → HHMMSS-folder
  // clear the (multi-line) ticking footer first — otherwise its tail glues onto this line ("· 3.5s · e; keep chatting")
  // newline FIRST, then clear-below: the cursor may sit at the end of a streamed text line (voice
  // mode) — clearing from it would erase the reply's last line; clearing from a fresh line only
  // removes footer residue below.
  // Barge-in aborts with zero tokens are normal turn-taking noise — suppress the footer
  // "Did the model do any work?" must be judged by transcript growth, NOT token count: providers
  // that don't report usage (cursor/*) show 0 tokens even after a long tool-heavy turn, and the
  // token heuristic silently dropped a whole aborted transcript (20260611-234609-test, 27min lost).
  const didWork = agent.transcript.slice(msgsBefore).some((m) => m.role === 'assistant' || m.role === 'tool');
  const silentAbort = res.finishReason === 'aborted' && !res.usage?.totalTokens && !didWork;
  if (!silentAbort)
    // `needs_input` is a PAUSE, not a failure — it reads yellow `?`, never a red `✗`. Rendering it as
    // a failure trains the caller to retry blind (tighter caps, same prompt) instead of answering.
    err('\n' + (process.stderr.isTTY ? '\r\x1b[0J' : '') + (ok ? green('  ✓ done') : res.finishReason === 'needs_input' ? yellow('  ? needs input') : red(`  ✗ ${res.finishReason}`)) + dim(` · ${res.steps} steps · ${tools} tools · ${tok}${secs}s · ${shortId}\n`));
  // Long turn finished → terminal bell, so a user who tabbed away gets pinged (CC parity).
  if (!silentAbort && process.stderr.isTTY && Date.now() - t0 > 10_000) err('\x07');
  // on a failed turn, show the provider's message so a billing/quota 400 isn't mistaken for a bad request
  if (res.finishReason === 'error' && res.error) {
    const e = res.error as any;
    err(red(`    ${e?.message ?? e}`) + (e?.statusCode ? dim(` (${e.statusCode}${e.code ? ' ' + e.code : ''})`) : '') + '\n');
  }
  // Skip persisting zero-token aborts (barge-in noise — no model work, no transcript change)
  if (silentAbort) return { ok: false, res };
  // persist (non-fatal on failure)
  session.messages = agent.transcript;
  session.meta.turns += 1;
  session.meta.tokens = (session.meta.tokens ?? 0) + (res.usage?.totalTokens ?? 0); // cumulative for /cost
  session.meta.costUsd = (session.meta.costUsd ?? 0) + cost; // cumulative USD (per-turn model rate)
  if (res.usageEstimated) session.meta.costEstimated = true; // sticky: any estimated turn → session total is estimated
  session.meta.updated = Date.now();
  session.meta.model = agent.options.model;
  const ev: TurnEvent = { ts: t0, durationMs: Date.now() - t0, model: agent.options.model, finishReason: res.finishReason, steps: res.steps, tools, tokens: res.usage?.totalTokens, costUsd: cost, estimated: res.usageEstimated };
  if (res.finishReason === 'error' && res.error) ev.error = errInfo(res.error);
  (session.meta.events ??= []).push(ev);
  if (!session.meta.title) { session.meta.title = titleOf(agent.transcript); if (session.meta.title) setTermTitle(`agentx · ${session.meta.title}`); }
  try { store.save(session); } catch (e: any) { err(dim(`  (session not saved: ${e?.message ?? e})\n`)); }
  return { ok, res };
}

/** Re-grant image-ref capabilities for a session loaded from disk, so the recent screenshot is visible
 *  again after a resume. The stored path is untrusted (the model can Write the session file) — only the
 *  session's own images dir and the cwd/jail root are allowed, realpath-checked inside `remintImageRefs`. */
const remintSessionImages = (data: SessionData, store: SessionStore, cwd: string): Message[] =>
  remintImageRefs(data.messages, [join(store.dir, `${data.meta.id}.images`), cwd]);

/** Resolve the starting session: resume an existing one (seeding the transcript) or start fresh. */
function startSession(args: Args, store: SessionStore, agent: Agent, cwd: string): SessionData {
  if (args.resume || args.cont) {
    const data = args.resume ? (store.load(args.resume) ?? globalSessionLoad(args.resume)) : store.latestData();
    if (data) {
      data.messages = elideStaleImages(remintSessionImages(data, store, cwd), 1);
      agent.transcript = data.messages;
      if (args.fork) { // --fork-session: branch into a NEW id so the source session is left untouched
        const now = Date.now();
        const forked: SessionData = { meta: { ...data.meta, id: args.sessionId ?? store.newId(now, cwd), created: now, updated: now, turns: data.meta.turns }, messages: data.messages };
        err(dim(`  forked ${data.meta.id} → ${forked.meta.id} (${data.meta.turns} turns)\n`));
        if (!args.task) printHistory(data.messages);
        return forked;
      }
      err(dim(`  resumed session ${data.meta.id} (${data.meta.turns} turns) — ${data.meta.title}\n`));
      if (!args.task) printHistory(data.messages); // replay the convo for an interactive resume (skip for headless -c "task")
      return data;
    }
    err(yellow(`  no session to resume — starting fresh\n`));
  }
  const now = Date.now();
  const id = args.sessionId ?? store.newId(now, cwd);
  if (!args.task) err(dim(`  session ${id}\n`)); // surface the id up front so it's quotable when reporting an issue
  return { meta: { id, created: now, updated: now, cwd, model: agent.options.model, turns: 0, title: '' }, messages: [] };
}

const AGENTS_MD_TEMPLATE = `# ${'${name}'}

## Overview
<!-- What this project is, in 1-2 sentences. -->

## Conventions
- <!-- e.g. package manager, code style, module layout -->

## Commands
- build:
- test:
- run:

## Notes
<!-- Anything the agent should always keep in mind. -->
`;

/** Scaffold ./AGENTS.md if no project-instruction file exists yet. */
function initInstructions(cwd: string): void {
  for (const f of ['AGENTS.md', 'CLAUDE.md']) {
    if (existsSync(join(cwd, f))) { err(yellow(`  ${f} already exists — leaving it as-is\n`)); return; }
  }
  const path = join(cwd, 'AGENTS.md');
  writeFileSync(path, AGENTS_MD_TEMPLATE.replace('${name}', basename(cwd)));
  err(green(`  created ${path}\n`) + dim('  edit it, then it auto-loads into every run.\n'));
}

/** Persist a setting to ./.agent/settings.json so it survives restarts (loadConfig reads it back). */
function persistSetting(cwd: string, key: string, value: unknown): void {
  const path = join(cwd, '.agent', 'settings.json');
  try {
    const obj = existsSync(path) ? JSON.parse(readFileSync(path, 'utf8')) : {};
    if (obj[key] === value) return;
    obj[key] = value;
    mkdirSync(dirname(path), { recursive: true });
    writeFileSync(path, JSON.stringify(obj, null, 2) + '\n');
  } catch (e: any) {
    err(yellow(`  ⚠ couldn't persist ${key} to ${path} — ${e?.message ?? e}\n`));
  }
}
const persistModel = (cwd: string, model: string) => persistSetting(cwd, 'model', model);

// The @cursor/sdk provider streams over ConnectRPC/HTTP-2 (connect-node). Aborting a run surfaces as a
// *background* rejection that's never thrown into our for-await, so it can't be caught at the call site —
// either a clean cancel (ConnectError [canceled] "operation was aborted", cause DOMException ABORT_ERR)
// or, on a racing teardown, NGHTTP2_FRAME_SIZE_ERROR. Both are benign: they only happen because WE
// cancelled. Swallow exactly those; surface anything else.
const isCancelTeardown = (e: any): boolean => {
  if (!e) return false;
  if (e.code === 20 || e.cause?.code === 20) return true; // DOMException ABORT_ERR
  const blob = `${e.code ?? ''} ${e.name ?? ''} ${e.cause?.name ?? ''} ${e.rawMessage ?? ''} ${e.message ?? ''}`;
  return /NGHTTP2_FRAME_SIZE_ERROR|ERR_HTTP2_STREAM_ERROR|operation was aborted|\bAbortError\b/i.test(blob);
};
/** Process-level guards shared by BOTH REPLs (normal + duplex — a duplex CancelTask on a cursor worker
 *  fires the same background rejection): a rejection keeps the session alive, only a true uncaught
 *  exception is fatal, and the benign cursor cancel-teardown is dropped to a debug line. */
function installCancelGuards(mounted: MountedMcp[]): void {
  process.on('unhandledRejection', (e) => {
    if (isCancelTeardown(e)) { log.debug('suppressed unhandledRejection (cursor stream cancel)', e); return; }
    log.error('unhandledRejection', e);
  });
  process.on('uncaughtException', (e) => {
    if (isCancelTeardown(e)) { log.debug('suppressed uncaughtException (cursor stream cancel)', e); return; }
    console.error(e); void closeMcp(mounted); process.exit(1);
  });
}

async function repl(args: Args, ai: ChatLike, cfg: Partial<AgentConfig>, cwd: string) {
  const oauth = new McpOAuth({ storePath: join(cwd, '.agent', 'mcp-auth.json') });
  const mounted = await mountMcp(cfg, oauth);
  let mcpToolNames = [] as string[]; // current MCP tool surface on the agent (raw names OR the deferred pair) — for /mcp rebuilds
  const initialMcpTools = mcpAgentTools(mounted);
  mcpToolNames = initialMcpTools.map((t) => t.name);
  // In duplex mode this agent is the WORKER TEMPLATE — it never sends; its options (fs mode,
  // permissions, hooks, MCP tools) become the per-task worker options below.
  const agent = await makeAgent(args, ai, cfg, initialMcpTools, mounted.map((m) => m.name));

  // Non-duplex voice: let the model exit the session when the user says goodbye.
  // Duplex voice gets ExitSession via reflexOptions.tools (the agent here is the worker template — workers don't need it).
  if (args.voice && !args.duplex) agent.options.tools = [...(agent.options.tools ?? []), exitSessionTool(() => { exitRequested = true; })];

  // ── In-process scheduler: fires prompts on a timer while the session is alive.
  // When a job is due and no turn is running, it dispatches directly (like voice utterances).
  // When a turn IS running, it queues for drain after the turn finishes.
  const scheduleQueue: string[] = [];
  let scheduleTurn: ((prompt: string) => Promise<any>) | undefined; // bound once `turn` exists
  const scheduler = new Scheduler({
    fire: (job) => {
      if (!activeTurn && scheduleTurn) {
        err(dim(`  ⏰ scheduled › ${job.prompt.slice(0, 60)}${job.prompt.length > 60 ? '…' : ''}\n`));
        void scheduleTurn(job.prompt).then(() => editorRef?.redrawNow());
      } else {
        scheduleQueue.push(job.prompt);
      }
    },
    tickMs: 15_000,
  });
  // How an OS-fired job re-invokes this CLI: re-use the current interpreter + entry script (works
  // for dev `bun cli/cli.ts` and the installed bin shim alike); bare `agentx` as a last resort.
  const agentxInvocation = () => (process.argv[1] ? `${process.execPath} ${resolve(process.argv[1])}` : 'agentx');
  // OS-backend (mind/12): far one-offs (or explicit backend:'os') register with launchd/cron/at and
  // survive quitting — the fired job headless-resumes THIS session. sessionId reads lazily (the
  // session object is created below and can be swapped by /resume).
  const osSched = new OsScheduler({ agentx: agentxInvocation() });
  const osBackend = osSched.available() ? {
    get sessionId() { return session.meta.id; },
    cwd,
    route: (t: Trigger, hint?: string) => routeTrigger(t, hint),
    schedule: (spec: OsJobSpec) => osSched.schedule(spec),
    cancel: (id: string) => osSched.cancel(id),
    list: () => osSched.list(),
  } : undefined;
  agent.options.tools = [...(agent.options.tools ?? []), ...makeScheduleTools(scheduler, osBackend)];

  // ── RemoteTrigger: this session is live-triggerable via its unix socket (same-user only); the
  // injected prompt rides the scheduler's seam (direct when idle, queued mid-turn). The tool side
  // lets THIS agent trigger other sessions (live socket first, headless --resume fallback).
  const triggerServer = new TriggerServer((req) => {
    const tag = req.from ? `session ${req.from}` : 'remote trigger';
    if (!activeTurn && scheduleTurn) {
      err(dim(`  ⚡ ${tag} › ${req.prompt.slice(0, 60)}${req.prompt.length > 60 ? '…' : ''}\n`));
      void scheduleTurn(req.prompt).then(() => editorRef?.redrawNow());
    } else {
      scheduleQueue.push(req.prompt);
    }
  });
  agent.options.tools = [...(agent.options.tools ?? []), makeRemoteTriggerTool({ agentx: agentxInvocation(), selfId: () => session.meta.id })];

  // ── Duplex mode (`--duplex`): the REPL runs unchanged, but turns go through a fast VOICE agent
  // that answers instantly and delegates real work to background workers (re-voiced when done).
  // `face` = the transcript-owning agent the REPL drives (sessions, footer, Esc-abort, /compact);
  // `work` = the options that mean "the working agent" (/model, /reasoning, /tools, permissions —
  // in duplex these are the WORKERS' options; workers are constructed fresh per Act/Think).
  const duplex = args.duplex;
  let dx: DuplexAgent | undefined;
  let voiceIO: VoiceIO | undefined; // real voice I/O (--voice + keys): mic→STT in, text_delta→TTS out
  let voiceDiag: JsonlDiagSink | undefined; // per-session voice diagnostics → <session>.voice.jsonl (always on in voice mode)
  // Typed-decision model (Jev) when TYPESAFE_AI_KEY is set: judges suspected analysis leaks in the reflex's speech,
  // and powers the listener instinct below.
  const decider = duplex && process.env.TYPESAFE_AI_KEY ? new DecideClient({ apiKey: process.env.TYPESAFE_AI_KEY }) as unknown as Decider : undefined;
  // AI listener instinct (VOICE_INSTINCT=1 + TYPESAFE_AI_KEY): Jev decides backchannel / cut-in / yield /
  // resume instead of the rule-based gates. Actions are bound to the live voiceIO in startVoice.
  const instinct = decider && process.env.VOICE_INSTINCT === '1'
    ? new Instinct({
      decider,
      context: () => ({ agentSaid: dx?.recentExcerpt(1).replace(/^\w+: /, ''), recent: dx?.recentExcerpt(4) }),
      onDecision: ({ kind, ...d }) => {
        voiceDiag?.push({ t: performance.now(), kind: `instinct_${kind}`, ...d });
        // Live chrome: every Jev call, top choice first; ⚡ = it acted (clear + repaint like other async writers).
        const probs = Object.entries(d.probabilities).sort((a, b) => b[1] - a[1]).map(([k, p]) => `${k} ${Math.round(p * 100)}%`).join(' · ');
        const act = d.acted && !/stale|skipped/.test(d.acted) ? yellow(`⚡ ${d.acted}`) : dim(d.acted ?? 'no-op');
        err('\r\x1b[0J' + dim(`  🧠 jev ${kind.padEnd(12)} ${probs}  ${d.ms}ms  "${d.heard.slice(-40)}"  `) + act + '\n');
        editorRef?.redrawNow();
      },
    })
    : undefined;
  // Voice on-screen echo is APPEND-ONLY (no progressive climb-and-clear). In duplex many async writers
  // (task panels, the 🎤 utterance line, barge-in notices, editor repaints) interleave on stdout and
  // desync the MarkdownStream's in-place redraw — which then re-emits the whole in-progress line every
  // delta, producing the "replay garble" wall. Appending raw (markdown-stripped to match speech) is
  // immune to cursor desync. voiceLineOpen tracks whether a trailing newline is owed at turn end.
  let voiceLineOpen = false;
  // SINGLE-STREAM sequenced writes (mind/10-duplex.md root fix): the spoken echo MUST share the stream
  // the editor + all chrome (err) + the spinner use (stderr, see createLineEditor below). On stdout it
  // was a SECOND fd writing the same TTY with an independent buffer but a SHARED cursor — async chrome
  // (`\r\x1b[J` / spinner `\r\x1b[2K`) landing mid-echo on the other fd clobbered a span ("User's
  // naalidate"). A piped capture serializes the two fds (deterministic, clean) so it only bit live TTYs.
  // One fd = writes ordered by the single-threaded event loop = no cross-stream cursor race. Voice is
  // interactive-only (never piped), so nothing relied on the echo being on stdout.
  // Cartesia emotion control: the reflex/worker emit inline `[emotion]` tags → mapped to Cartesia inline
  // emotion tags for TTS. Off via VOICE_EMOTIONS=0; show tags in the on-screen echo (debug) via VOICE_SHOW_EMOTIONS=1.
  const emotionsOn = process.env.VOICE_EMOTIONS !== '0';
  let showEmotions = process.env.VOICE_SHOW_EMOTIONS === '1'; // debug: surface tags in the echo (opt-in; /voice-emotions toggles live)
  const voiceEcho = (text: string) => { const s = forSpeech(text); if (!s) return; err(s); voiceLineOpen = true; };
  const voiceEchoEnd = () => { if (voiceLineOpen) { err('\n'); voiceLineOpen = false; } };
  // Reflex reasoning (thinking_delta): VISIBLE (dim, distinct) but NEVER spoken — parity with the duplex
  // lab's collapsed pill. Off by default in voice (reasoning is verbose; the spoken answer is the product);
  // VOICE_SHOW_REASONING=1 opts in. Rendered as a continuous dim `  💭 …` stderr flow (newlines collapsed
  // so it stays an append-only line the editor redraw can't garble); closed before any spoken echo.
  let showReasoning = process.env.VOICE_SHOW_REASONING === '1'; // /voice-reasoning toggles live
  let voiceReasonOpen = false;
  const voiceReasonEnd = () => { if (voiceReasonOpen) { err('\n'); voiceReasonOpen = false; } };
  let toggleVoice: (() => Promise<void>) | undefined; // bound below (duplex + TTY): /voice flips mic on/off live
  let editorRef: LineEditor | undefined; // bound once the line editor exists — async chrome repaints the prompt via it
  // Emit async chrome (task events, ⦿ results) that may land ON TOP of a LIVE input line. A raw `\r\x1b[0J`
  // only clears from the cursor DOWN, leaving the input rows ABOVE the cursor as residue — each async event
  // then stamps another stale copy of the typed line (the "agentx › …" ghost stack). suspend() clears the
  // WHOLE block from the top (it climbs the editor's own row count); resume() repaints it below the chrome.
  // If the editor was already suspended (a spoken delta owns the terminal mid-stream), leave it that way.
  const emitChrome = (write: () => void) => {
    const wasSusp = editorRef?.isSuspended() ?? true;
    editorRef?.suspend(); // climb + clear the live input block from the top (no-op if already suspended)
    write();
    if (!wasSusp) editorRef?.resume(); // repaint the prompt cleanly below the chrome
  };
  // During a turn the user's type-ahead lives on a "stash ›" line (no active editor to own it). Async
  // chrome (streamed deltas, task events) lands on top of it — repaint the stash below, so it survives.
  let repaintStash: () => void = () => {};
  let workerOptions: AgentOptions | undefined;
  // Worker UI verbosity: 'full' = ⚙ tool chrome per worker step; 'minimal' = task events only
  // (started/progress/⦿ done). Voice defaults minimal (chrome is noise next to speech); /workers toggles live.
  let workerChrome: 'full' | 'minimal' = 'full';
  let duplexPersist: () => void = () => {}; // bound once the session exists (re-voice fires async)
  let duplexAccount: (data: any) => void = () => {}; // worker cost → session meta (bound below)
  // Workers are non-interactive: a permission 'ask' can't pop a menu mid-conversation (it would fight
  // the line editor for raw stdin). VOICE mode relays the ask through the conversation (park →
  // '[task asks]' re-voice → spoken yes/no via AnswerTask; timeout → deny). Text duplex (and the
  // relay's timeout path) auto-denies with a narratable reason — the worker adapts or reports back.
  let permSeq = 0;
  const duplexAsk = async (call: ToolUse): Promise<{ decision: 'allow' | 'deny' }> => {
    if (args.voice && dx) {
      const hint = summarizeCall(call.name, call.args).slice(0, 80);
      // Default: approve like a normal session — suspend the editor, pop an interactive picker
      // (Allow once / always / Deny). Set `voiceAskUi: 'relay'` to opt into the spoken park/relay flow.
      if ((cfg as any).voiceAskUi !== 'relay') {
        editorRef?.suspend();
        const v = await selectMenu(process.stderr, {
          title: `? background worker asks to run ${call.name} ${hint}`,
          items: [{ label: 'Allow once', value: 'y' }, { label: 'Allow always', value: 'a' }, { label: 'Deny', value: 'n' }],
          current: 'y',
        });
        editorRef?.resume();
        editorRef?.redrawNow();
        if (v === 'a') {
          // Remember a command-scoped allow: a live session rule (wins next ask; glob has no `*`
          // → exact-command match) + persist to .agent/permissions.json for future sessions.
          const cmd = typeof call.args?.command === 'string' ? call.args.command : null;
          work.permissions?.options.rules.unshift(cmd ? { tool: call.name, pathGlob: cmd, decision: 'allow' } : { tool: call.name, decision: 'allow' });
          persistRule(cwd, 'allow', cmd ? `${call.name}(${cmd})` : call.name);
        }
        return { decision: v === 'y' || v === 'a' ? 'allow' : 'deny' };
      }
      // NB: perm asks are keyed perm-N (PermissionPolicy.ask carries no task identity), so a
      // cancelled task can't clean its parked perm question — bounded by askTimeoutMs → deny.
      // (Chrome prints once via the task_ask notify below — no extra line here.)
      const id = `perm-${++permSeq}`;
      const a = await dx.parkQuestion(id, `Permission: may the background worker run ${call.name}${hint ? ` (${hint})` : ''}? Answer yes or no (you can also type it).`);
      const allow = /^\s*(y(es|ep|eah)?|sure|ok(ay)?|allow|go|approved?|do it)\b/i.test(a);
      err('\r\x1b[0J' + (allow ? green(`  ✓ allowed ${call.name}`) : yellow(`  ⊘ denied ${call.name}`)) + dim(` (${a.trim() || 'no answer'})\n`));
      editorRef?.redrawNow();
      return { decision: allow ? 'allow' : 'deny' };
    }
    err('\r\x1b[0J' + yellow(`  ⊘ worker asked to run ${call.name} — auto-denied (no interactive approval in duplex; use --yes or --allowedTools)\n`));
    editorRef?.redrawNow(); // background event at a live prompt — repaint below the notice
    return { decision: 'deny' };
  };
  if (duplex) {
    // Workers must not stream into the voice channel — strip the host/stream seam (display hooks stay:
    // worker tool activity prints as dim stderr chrome). `signal` is per-task (duplex.ts owns it).
    // Drop providerOptions too: they're computed for the MAIN agent's model (e.g. cursor's cwd/cursorSession)
    // and must never leak onto a worker whose model differs — DuplexAgent recomputes them per worker model
    // via providerOptionsFor below. (A cursor cursorSession leaking onto an anthropic worker is a hard 400.)
    const { host: _host, stream: _stream, signal: _signal, providerOptions: _po, ...wo } = agent.options;
    workerOptions = wo as AgentOptions;
    if (workerOptions.permissions)
      workerOptions.permissions = new PermissionPolicy({ ...workerOptions.permissions.options, host: undefined, ask: duplexAsk });
    workerOptions.planMode = false; // a worker's plan could never be approved (no host) — it would stall to maxSteps
    // Workers are BACKGROUND agents: rebuild their display hooks so chrome never drives the foreground
    // spinner (the 'agentx › '/'⠹ thinking…' flicker) and repaints the live prompt after each print.
    workerChrome = args.voice ? 'minimal' : (cfg.workerChrome ?? 'full');
    const workerDisplay = displayHooks(agent.options.fs, { gate: () => workerChrome === 'full', background: () => editorRef?.redrawNow() });
    workerOptions.hooks = cfg.hooks ? composeHooks(workerDisplay, hooksFromConfig(cfg.hooks)) : workerDisplay;
    // The single voice: markdown-rendered deltas on stdout = the SPOKEN channel. Everything else
    // (⚙ tool chrome, task events, worker results) is VISUAL chrome on stderr — when wired to a
    // real voice API, only text_delta becomes speech; the rest feeds the screen.
    const base = makeHost('text', { stream: true });
    const host: typeof base = {
      ...base,
      notify(e: any) {
        // In live VOICE mode the reflex's per-step headers (turn_start) are screen noise — suppress them.
        if (voiceIO && e.kind === 'turn_start') return;
        // Reflex reasoning (thinking_delta): the analysis channel ("User wants a table. Need weather…").
        // Never spoken. Off by default (verbose next to the spoken answer); VOICE_SHOW_REASONING=1 surfaces
        // it as a dim, continuous `  💭 …` stream — VISIBLE but distinct, mirroring the lab's collapsed pill.
        // Text-duplex (no mic) keeps its own rendering below (useful chrome when reading, not listening).
        if (voiceIO && e.kind === 'thinking_delta') {
          if (!showReasoning || typeof e.message !== 'string') return;
          spinner.stop(); // the 90ms spinner tick would erase this append-only line (same hazard as voiceEcho)
          editorRef?.suspend();
          if (!voiceReasonOpen) { err(dim('  💭 ')); voiceReasonOpen = true; }
          err(dim(e.message.replace(/\s*\n\s*/g, ' '))); // collapse newlines → one continuous dim flow
          return;
        }
        // SPOKEN channel tap (mind/10-duplex.md): only the voice's text_delta becomes speech.
        // Streaming text to stdout while the line editor is LIVE corrupts the terminal (the editor's
        // climb-and-clear redraws fire mid-paragraph and erase it) — so the editor is SUSPENDED for
        // the duration of a streaming voice turn and resumed (fresh repaint) at turn end.
        // A COMPLETE worker utterance (already split out of <spoken>…</spoken>) — serialize it into
        // the central speech queue as its OWN utterance (NOT speakDelta, which is the live reflex
        // stream). Echo it append-only to screen. In text/no-voice mode, surface it like a result.
        // Duplex-level diagnostics (speculation confirm/abort, …): sink-only, never screen or TTS.
        if (e.kind === 'diag') { voiceDiag?.push({ t: performance.now(), kind: String(e.message), ...(e.data ?? {}) }); return; }
        if (e.kind === 'speak_utterance') {
          if (voiceIO) {
            voiceReasonEnd(); // close any open dim reasoning line before the spoken echo lands
            spinner.stop();
            voiceIO.enqueueUtterance(e.message);
            editorRef?.suspend();
            voiceEcho(emotionsOn ? renderEmotions(e.message, { show: showEmotions }).display : e.message);
            voiceEchoEnd();
            editorRef?.resume();
            editorRef?.redrawNow();
          } else {
            err('\r\x1b[0J' + dim(`  ⦿ ${plainLine(e.message)}\n`));
            editorRef?.redrawNow();
            repaintStash();
          }
          return;
        }
        if (e.kind === 'text_delta' && voiceIO) {
          voiceReasonEnd(); // close any open dim reasoning line before the spoken reply streams in
          spinner.stop(); // CRITICAL: the 90ms spinner tick writes `\r\x1b[2K` — left running it ERASES the
                          // append-only echo line mid-reply (the "✓ done, nothing shown" voice regression).
                          // The base host stops it on every output event (below); this branch returns early, so stop here too.
          const echo = voiceIO.speakDelta(e.message); // returns the emotion-parsed display (same stream sent to TTS)
          editorRef?.suspend(); // no-op when already suspended
          voiceEcho(echo); // append-only screen echo — NOT base.notify's progressive md render (cursor-desync → replay garble)
          return;
        } else if (e.kind === 'text_delta' && stashText()) {
          // TEXT mode with type-ahead pending: lift the sacred stash line, let the markdown stream
          // land, then repaint the stash below it (else the streamed ack overwrites what's being typed).
          process.stdout.write('\r\x1b[K');
          base.notify!(e);
          repaintStash();
          return;
        }
        if (e.kind === 'hold_filler' && voiceIO) {
          voiceIO.cancelPendingAck(); // Hold turn = intentionally silent — a pending adaptive ack must not fire
          if (e.message) voiceIO.speakFiller(e.message);
          return;
        }
        if (e.kind === 'revoice_done') { // a re-voice turn ended outside runTurn's flush — drain the tail now
          if (voiceIO) voiceEchoEnd(); else { base.flushText(); process.stdout.write('\n'); } // voice = append-only; text-duplex = md flush
          voiceIO?.endSpeech(); // a re-voice turn is spoken too — close its TTS context (idempotent)
          duplexPersist(); // a re-voice turn changed the transcript with no send() in flight — save it too
          editorRef?.resume(); // repaint the prompt block below the streamed text
          editorRef?.redrawNow();
          return;
        }
        if (e.kind === 'task_done' && e.data?.text) { // show the worker's full result visually (the voice only summarizes it)
          const lines = String(e.data.text).split('\n');
          const shown = lines.slice(0, previewLines());
          // This lands ASYNC, often on top of the live prompt/footer — clear the current line first
          // (else footer residue glues onto the ⦿ header) and repaint the prompt block after.
          // \x1b[0J (clear to end of screen): the ticking footer is MULTI-line — clearing one line
          // leaves "…working in background…" residue glued to this header; redrawNow repaints below.
          emitChrome(() => {
            err('\n' + dim(`  ⦿ ${e.message}\n`) + shown.map((l) => dim(`    ${plainLine(l)}\n`)).join('')); // strip raw markdown markers in the preview
            if (lines.length > shown.length) err(dim(`    … (+${lines.length - shown.length} more lines)\n`));
          });
          duplexAccount(e.data); // worker tokens/cost are real spend — fold into /cost
          repaintStash(); // type-ahead survives the async ⦿ landing
          return;
        }
        // Remaining task_* events (started/progress/cancelled/error) land ASYNC at a live prompt —
        // same treatment as task_done: clear the prompt line, print, repaint (bare err() leaves
        // residue glued to 'agentx › '). task_done returned above; everything else falls through.
        if (typeof e.kind === 'string' && e.kind.startsWith('task_')) {
          spinner.stop();
          // asks are decisions, not chatter — make them stand out (the voice also speaks them)
          emitChrome(() => err(e.kind === 'task_ask' ? yellow(`  ? ${e.message} — answer by voice or type yes/no\n`) : e.kind === 'task_error' ? yellow(`  ✗ ${e.message}\n`) : dim(`  · ${e.message}\n`)));
          repaintStash(); // type-ahead survives the async task event landing
          return;
        }
        base.notify!(e); // makeHost always provides notify (the HostBridge type just marks it optional)
      },
    };
    // Conversational undo: the voice can roll back per-task checkpoint frames ("undo that").
    const rewindFilesTool: AgentTool = {
      name: 'RewindFiles',
      description: 'Undo file changes made by Act/Think tasks: roll back the last N task checkpoints (default 1). Use when the user asks to undo/revert what a task changed.',
      parameters: { type: 'object', properties: { steps: { type: 'number', description: 'how many task checkpoints to undo (default 1)' } } },
      run: async ({ steps }) => {
        if (!checkpoints.size) return 'No file checkpoints to rewind yet.';
        if ([...(dx?.tasks.values() ?? [])].some((t) => t.status === 'running'))
          return 'A task is still running — cancel it first (CancelTask), then rewind.';
        const n = Math.min(Math.max(1, Number(steps ?? 1)), checkpoints.size);
        const r = await checkpoints.rewindTo(checkpoints.size - n);
        return `Rewound ${n} task checkpoint(s): restored ${r.restored} file(s), deleted ${r.deleted}.`;
      },
    };
    dx = new DuplexAgent({
      ai,
      ...(instinct ? { bargeIntent: (t: string) => instinct.bargeIntent(t) } : {}),
      decider,
      fs: agent.options.fs,
      memoryDir: agent.options.memoryDir,
      memoryUserDir: agent.options.memoryUserDir,
      ...((args.voiceModel ?? cfg.reflexModel) ? { reflexModel: resolveModelOrNewest((args.voiceModel ?? cfg.reflexModel)!) } : {}),
      actModel: agent.options.model,
      actOptions: workerOptions,
      providerOptionsFor: (m) => providerOptionsFor(m, cwd, cfg.mcpServers, mounted.map((x) => x.name)),
      ...((args.thinkModel ?? cfg.thinkModel) !== undefined ? { thinkModel: (args.thinkModel ?? cfg.thinkModel) === false ? false : resolveModelOrNewest(String(args.thinkModel ?? cfg.thinkModel)) } : {}),
      host,
      ...(args.voice ? { voiceStyle: 'conversational' as const, progressUpdates: true, askRelay: true, emotionTags: emotionsOn, spokenBudgetChars: 350 } : {}), // voice: progress asides + worker questions relayed through the conversation; emotion tags taught only with a TTS that speaks them
      // Per-TASK checkpoint frames (the natural undo unit in duplex = one delegation): opened BEFORE
      // the worker spawns (post-spawn would race its first edits). `checkpoints` is bound below.
      onTaskStart: async (_id, label) => { await checkpoints.begin(label); },
      // The jail deny-lists .git/** (VCS internals can carry credentials), so the engine's fs-based
      // 'branch' lookup can't see it — supply it host-side (one safe read-only file).
      quickLook: {
        branch: () => {
          try {
            const head = readFileSync(join(cwd, '.git', 'HEAD'), 'utf8').trim();
            return head.startsWith('ref: refs/heads/') ? `branch: ${head.slice('ref: refs/heads/'.length)}` : `detached HEAD at ${head.slice(0, 12)}`;
          } catch { return 'not a git repository'; }
        },
        memory: async () => {
          const _adot = (s: string) => `${(agent.options.fs!.getCwd() === '/' ? '' : agent.options.fs!.getCwd())}/.agent/${s}`;
          const dirs = Array.isArray(agent.options.memoryDir) ? agent.options.memoryDir : [agent.options.memoryDir || _adot('memory')];
          const parts: string[] = [];
          for (const d of dirs) {
            try { const idx = await fs.readFile(`${d}/MEMORY.md`); if (idx.trim()) parts.push(idx.trim()); } catch { /* dir doesn't exist yet */ }
          }
          return parts.length ? parts.join('\n').slice(0, 2000) : '(no memory yet)';
        },
        // Cross-session recall: earlier conversations in this project (path = a session id / "previous").
        sessions: (which) => sessionRecap(store, session.meta.id, which),
      },
      // The voice runs on the REAL fs (it has no fs tools — harmless) so @mentions, !cmd and #note
      // resolve against the project; + CC-parity chrome for its own tool calls (⚙ Act …).
      reflexOptions: { fs: agent.options.fs, hooks: displayHooks(agent.options.fs), tools: [rewindFilesTool, exitSessionTool(() => { exitRequested = true; })] },
    });
    if (args.voiceSystemPrompt) dx.voice.options.systemPrompt += '\n\n' + args.voiceSystemPrompt;
  }
  const face: Agent = dx ? dx.voice : agent; // the transcript-owning agent the REPL drives
  const work: AgentOptions = workerOptions ?? agent.options; // "the working agent" options
  const sendVia = dx ? (c: MessageContent, o?: TurnOpts) => dx!.send(c, o) : undefined; // runTurn dispatch override
  const routeVia = autoRouteSend(args, agent, () => session, (m) => providerOptionsFor(m, cwd, cfg.mcpServers, mounted.map((x) => x.name))); // `-m auto` (non-duplex)

  // ── Permission posture (Shift+Tab cycles it live; CC parity). default=ask · acceptEdits=ask only shell · plan ──
  // Re-read persisted rules each rebuild so "Always allow/deny" choices made earlier THIS session survive a cycle.
  const baseRules = () => [
    ...parsePermRules({ deny: args.disallowedTools }, true),
    ...parsePermRules({ allow: args.allowedTools }, true),
    ...parsePermRules(mergePerms(loadPersistedRules(cwd), cfg.permissions)),
  ];
  const askFor = (tools: string[]) => tools.map((t) => ({ tool: t, decision: 'ask' as const }));
  // No 'plan' in duplex — workers are non-interactive, an ExitPlanMode could never be approved.
  const POSTURES: readonly Posture[] = duplex ? ['default', 'acceptEdits'] : ['default', 'acceptEdits', 'plan'];
  type Posture = 'default' | 'acceptEdits' | 'plan';
  let posture: Posture = (!duplex && (args.plan || cfg.permissionMode === 'plan')) ? 'plan' : cfg.permissionMode === 'acceptEdits' ? 'acceptEdits' : 'default';
  const postureLabel = () => posture === 'default' ? 'ask (default)' : posture === 'acceptEdits' ? 'accept edits' : 'plan mode';
  const applyPosture = (p: Posture) => {
    posture = p;
    const ask = p === 'acceptEdits' ? askFor(['bash', 'Shell']) : askFor(ASK_MUTATING);
    // In duplex this rebuilds the WORKERS' policy (work = workerOptions; workers spawn fresh per task)
    // with the auto-deny ask; in normal mode it's the live agent's policy with the interactive menu.
    work.permissions = new PermissionPolicy({ rules: [...baseRules(), ...ask], default: 'allow', host: duplex ? undefined : makeHost(), ask: duplex ? duplexAsk : makeAskResolver(cwd) });
    if (!duplex) {
      agent.options.planMode = p === 'plan';
      agent.reprepare(); // rebuild prompt/tools/plan-mode/permission hooks on the next send
    }
  };
  const cyclePosture = (): string => { applyPosture(POSTURES[(POSTURES.indexOf(posture) + 1) % POSTURES.length]); err(dim(`  ⇥ ${postureLabel()}\n`)); return postureLabel(); };
  if (!args.yes) applyPosture(posture); // --yes keeps makeAgent's allow-all (don't impose asks); Shift+Tab still opts in

  // ── Quick reasoning toggle (Alt+T): off → low → medium → high → off ── (duplex: the workers' reasoning)
  const REASONING_CYCLE = ['off', 'low', 'medium', 'high'] as const;
  const toggleReasoning = (): string => {
    const cur = String(work.reasoning ?? 'off');
    const next = REASONING_CYCLE[(Math.max(0, REASONING_CYCLE.indexOf(cur as any)) + 1) % REASONING_CYCLE.length];
    work.reasoning = next;
    err(dim(`  ~ reasoning → ${next}\n`));
    return next;
  };
  // Model switching targets the WORKER in duplex (the voice model is a --voice-model startup choice).
  // Recompute providerOptions on switch: they're model-specific (cursor cwd/cursorSession) and the
  // anthropic/openai adapters Object.assign them into the request body — stale ones are a hard 400.
  const setModel = (m: string) => { work.model = m; work.contextWindow = getModelInfo(m)?.contextWindow ?? 0; work.providerOptions = providerOptionsFor(m, cwd, cfg.mcpServers, mounted.map((x) => x.name)); if (dx) dx.options.actModel = m; persistModel(cwd, m); err(dim('  model → ' + m + '\n')); };
  // Per-tier appliers (duplex only) — shared by /model's tier picker and the /voice-model · /think-model shortcuts (DRY).
  const applyReflexModel = (id: string) => { const m = resolveModelOrNewest(id); dx!.options.reflexModel = m; dx!.voice.options.model = m; err(green(`  ✓ reflex model → ${m}\n`)); };
  const applyThinkModel = (id: string) => { const m = resolveModelOrNewest(id); dx!.setThinkModel(m); err(green(`  ✓ think model → ${m}\n`)); };
  // Duplex `/model` with no arg → choose a tier, then its model. Surfaces all three seats in one place
  // (else `/model` silently retargets only the worker). Think can also be disabled here.
  const pickModelByTier = async () => {
    const thinkLabel = dx!.options.thinkModel === false ? 'off' : String(dx!.options.thinkModel);
    const tier = await selectMenu(process.stderr, { title: 'Set model for which tier?', items: [
      { label: 'Reflex (voice)', value: 'reflex', desc: dx!.options.reflexModel },
      { label: 'Act (worker)', value: 'act', desc: work.model },
      { label: 'Think', value: 'think', desc: thinkLabel },
    ] });
    if (!tier) return;
    if (tier === 'think') {
      const sub = await selectMenu(process.stderr, { title: 'Think tier', items: [
        { label: 'Choose a model…', value: '__pick__', desc: thinkLabel === 'off' ? 'currently off' : `current: ${thinkLabel}` },
        { label: 'Disable Think tier', value: '__off__' },
      ] });
      if (!sub) return;
      if (sub === '__off__') { dx!.setThinkModel(false); err(green('  ✓ think tier disabled\n')); return; }
      const picked = await pickModel(dx!.options.thinkModel === false ? 'anthropic/claude-opus-4-6' : String(dx!.options.thinkModel));
      if (picked) applyThinkModel(picked);
      return;
    }
    const picked = await pickModel(tier === 'reflex' ? dx!.options.reflexModel : work.model);
    if (!picked) return;
    if (tier === 'reflex') applyReflexModel(picked); else setModel(picked);
  };
  // Tool mutations (/mcp add|remove|login) — duplex workers are constructed per spawn from work.tools.
  const addWorkTools = (ts: AgentTool[]) => { if (duplex) work.tools = [...(work.tools ?? []), ...ts]; else agent.addTools(ts); };
  const removeWorkTools = (names: string[]) => { if (duplex) work.tools = (work.tools ?? []).filter((t) => !names.includes(t.name)); else agent.removeTools(names); };
  // `mounted` changed (/mcp add/remove/reconnect/login) → swap the whole MCP tool surface in one go.
  // Raw-vs-deferred is re-decided per rebuild, and the deferred pair's catalog snapshot stays current.
  const remountMcpTools = () => {
    removeWorkTools(mcpToolNames);
    const ts = mcpAgentTools(mounted, { quiet: true });
    mcpToolNames = ts.map((t) => t.name);
    addWorkTools(ts);
  };

  const pendingImages: string[] = []; // clipboard images grabbed via /paste, attached to the next message
  const bangContext: string[] = []; // `!cmd` runs + output, folded into the next model turn (CC parity)
  // Grab an image off the OS clipboard → temp file → an `@<abs>` attachment ref. Shared by /paste and Cmd-V.
  const grabClipboardAttachment = (): { display: string; ref: string; path: string } | null => {
    // Session-scoped and durable: OS temp gets reaped, so a resumed session silently lost its images.
    // Alongside the session JSON they survive a resume (the ref path still resolves next process).
    const dir = join(store.dir, `${session.meta.id}.images`);
    try { mkdirSync(dir, { recursive: true }); } catch (e) { log.debug('paste image dir create failed', e); }
    const img = grabClipboardImage(dir, String(Date.now()));
    return img ? { display: 'Image', ref: '@' + img.path, path: img.path } : null;
  };
  // Hard teardown — used by the SIGINT path and the keypress force-quit escalation. Never let the
  // user get trapped: even if a turn's abort() fails to unwind (wedged stream/tool), this exits.
  const forceQuit = (code = 130) => {
    try { voiceIO?.stop(); } catch { /* mic/player may already be down */ }
    try { disposeCursorSessions(); } catch { /* reap warm cursor helpers, best-effort */ }
    try { disposeClaudeCodeSessions(); } catch { /* reap warm claude-code sessions, best-effort */ }
    void closeMcp(mounted);
    process.exit(code);
  };
  // Ctrl-C: cancel the in-flight turn if one's running; a second Ctrl-C while already cancelling
  // (i.e. the abort didn't unwind) force-quits. No active turn → clean up MCP children and exit.
  process.on('SIGINT', () => {
    if (activeTurn) {
      if (aborting) { err(red('\n  ⏻ force-quit\n')); forceQuit(); return; } // already cancelling → hard escape
      activeTurn.abort(); voiceIO?.interrupt(); return;
    }
    forceQuit();
  });
  installCancelGuards(mounted);
  // @server:uri prompt mentions → inline the MCP resource body (text contents; non-text noted).
  setMcpMentionResolver(async (ref) => {
    const i = ref.indexOf(':');
    if (i <= 0) return null;
    const m = mounted.find((x) => x.name === ref.slice(0, i));
    if (!m) return null;
    const r = await m.client.readResource(ref.slice(i + 1));
    const parts = Array.isArray(r?.contents) ? r.contents : [];
    const texts = parts.map((c: any) => c?.text ?? (c?.blob ? `[binary ${c.mimeType ?? 'blob'}]` : '')).filter(Boolean);
    return texts.length ? texts.join('\n') : JSON.stringify(r);
  });
  const store = new SessionStore(cwd);
  let session = startSession(args, store, face, cwd);
  triggerServer.start(session.meta.id); // live-triggerable from now on (re-bound on /resume and /clear)
  setTermTitle(`agentx · ${session.meta.title || cwd.split('/').pop() || 'session'}`);
  // Restore scheduled jobs from the resumed session (if any).
  if ((session.meta as any).scheduledJobs?.length) {
    scheduler.restore((session.meta as any).scheduledJobs);
    err(dim(`  ⏰ ${scheduler.size} scheduled job(s) re-armed\n`));
  }

  // File checkpoint/rewind — per TURN normally, per TASK in duplex (frames open via onTaskStart).
  // Disk mode → durable git-backed (whole tree incl. bash edits, survives sessions, CC parity).
  // Virtual modes (sandbox / boddb) → in-VFS stack over the same backing filesystem.
  const checkpoints: Checkpoints = (args.vfs || args.boddb)
    ? new CheckpointStack(agent.options.fs!)
    : new GitCheckpoints({ workTree: cwd, gitDir: join(cwd, '.agent', 'checkpoints.git'), addDirs: args.addDirs, sessionId: session.meta.id });
  const cpHooks = checkpoints.hooks?.();
  if (cpHooks) {
    agent.options.hooks = composeHooks(agent.options.hooks, cpHooks); // main REPL turns: the agent makes the edits
    work.hooks = composeHooks(work.hooks, cpHooks); // duplex: the workers make the edits — their hooks feed the frames
  }
  duplexPersist = () => {
    session.messages = face.transcript;
    session.meta.updated = Date.now();
    if (!session.meta.title) session.meta.title = titleOf(face.transcript);
    try { store.save(session); } catch (e: any) { err(dim(`  (session not saved: ${e?.message ?? e})\n`)); }
  };
  duplexAccount = (data) => { // worker runs never pass through runTurn — account their usage here
    if (!data?.usage?.totalTokens) return;
    session.meta.tokens = (session.meta.tokens ?? 0) + data.usage.totalTokens;
    session.meta.costUsd = (session.meta.costUsd ?? 0) + turnCost(work.model, data.usage);
    if (data.usageEstimated) session.meta.costEstimated = true;
    try { store.save(session); } catch { /* non-fatal */ }
  };

  // custom slash commands (same registry the model's SlashCommand tool uses) + skills.
  // Anchored at the FS working dir — in CC-parity disk mode root '/' is the real machine, so
  // .agent lives under the launch dir, not '/' (which would be the real filesystem root).
  const fs = agent.options.fs!; // the CLI always builds the agent with a concrete fs (buildAgent)
  const fsBase = fs.getCwd() === '/' ? '' : fs.getCwd();
  const adot = (sub: string) => `${fsBase}/.agent/${sub}`;
  // Search order lives in dotDirs() — same registry buildAgent's `dots()` uses. Unfiltered here:
  // paths are VFS-space, so disk existence can't be checked (loaders skip missing dirs themselves).
  const adots = (sub: string) => dotDirs(fsBase, sub);
  const cmds: CommandInfo[] = (await loadCommands(fs, adots('commands'))).commands;
  const skills: SkillInfo[] = (await loadSkills(fs, adots('skills'))).skills;

  // Mid-session catalog pickup (CC parity): skills/commands created or removed after launch are
  // detected per turn. The memoized system prompt stays frozen (cache-stable) — the delta reaches
  // the model as a <system-reminder> appended to the turn, exactly CC's mechanism. Also syncs the
  // REPL-side `skills`/`cmds` snapshots in place (tab-complete, /skills, /<cmd> dispatch).
  const refreshCatalogs = async (): Promise<string> => {
    const [freshSkills, freshCmds] = await Promise.all([scanSkills(fs, adots('skills')), scanCommands(fs, adots('commands'))]);
    const diff = <T extends { name: string; description: string }>(kind: string, old: T[], fresh: T[]) => {
      const had = new Set(old.map((x) => x.name)), has = new Set(fresh.map((x) => x.name));
      const added = fresh.filter((x) => !had.has(x.name)), removed = old.filter((x) => !has.has(x.name));
      old.splice(0, old.length, ...fresh); // sync the REPL snapshot in place
      return [
        ...added.map((x) => `+ ${kind} **${x.name}** — ${x.description}`),
        ...removed.map((x) => `- ${kind} ${x.name} (removed)`),
      ];
    };
    const lines = [...diff('skill', skills, freshSkills), ...diff('command', cmds, freshCmds)];
    if (!lines.length) return '';
    return `<system-reminder>\nThe skill/command catalog changed on disk since the session started:\n${lines.join('\n')}\nAdded entries are loadable now via the Skill/SlashCommand tools; removed ones are gone even if still listed in the system prompt.\n</system-reminder>`;
  };

  // persisted input history (best-effort)
  const histPath = join(cwd, '.agent', 'history');
  const history = existsSync(histPath)
    ? readFileSync(histPath, 'utf8').split('\n').filter(Boolean).reverse().slice(0, 500)
    : [];
  const remember = (line: string) => {
    try { mkdirSync(join(cwd, '.agent'), { recursive: true }); appendFileSync(histPath, line + '\n'); }
    catch (e) { log.debug('history write failed', e); } // best-effort; never blocks the prompt
  };

  // ---- interactive helpers (reuse the selectMenu picker) ----
  const ago = (t: number) => {
    const s = Math.max(0, (Date.now() - t) / 1000);
    return s < 60 ? 'just now' : s < 3600 ? `${Math.floor(s / 60)}m ago` : s < 86400 ? `${Math.floor(s / 3600)}h ago` : `${Math.floor(s / 86400)}d ago`;
  };
  const resumeInto = (data: SessionData) => {
    data.messages = elideStaleImages(remintSessionImages(data, store, cwd), 1);
    face.transcript = data.messages; session = data; checkpoints.use?.(data.meta.id); triggerServer.use(data.meta.id);
    const m = data.meta as any;
    goalCondition = m.goalCondition; goalTurns = m.goalTurns ?? 0; goalTokens = m.goalTokens ?? 0; goalLastReason = m.goalLastReason;
    if (m.scheduledJobs?.length) { scheduler.restore(m.scheduledJobs); err(dim(`  ⏰ ${scheduler.size} scheduled job(s) re-armed\n`)); }
    err(dim(`  resumed ${data.meta.id} (${data.meta.turns} turns)${data.meta.title ? ' — ' + data.meta.title : ''}\n`));
    if (goalCondition) err(dim(`  ◎ goal active: ${goalCondition} (${goalTurns} turns)\n`));
    printHistory(data.messages);
  };
  // Double-Esc rewind (CC parity): pick an earlier user message, then choose what to restore —
  // conversation, code, or both. Returns the message text to pre-fill the prompt (when the conversation
  // is rewound) for editing + resend, or undefined (cancelled, or code-only).
  const rewindToMessage = async (): Promise<string | undefined> => {
    const users = face.transcript.map((m, i) => ({ m, i })).filter((x) => x.m.role === 'user');
    if (!users.length) { err(dim('  (no earlier messages to jump back to)\n')); return undefined; }
    await checkpoints.refresh?.(); // sync size/list with the durable backend before mapping turns → frames
    // Newest-first (CC parity + consistent with /rewind). `value` = position among user turns.
    const items: SelectItem[] = users
      .map(({ m }, p) => {
        const t = contentText(m.content).split('\n\n--- @')[0].replace(/\n+/g, ' ').trim(); // drop inlined @file blocks
        return { label: t.length > 60 ? t.slice(0, 59) + '…' : t || '(empty)', value: String(p) };
      })
      .reverse();
    const pick = await selectMenu(process.stderr, { title: 'Jump back to a message', items });
    if (pick == null) return undefined;
    const p = Number(pick);
    const idx = users[p].i;
    // Map a user-turn position to a checkpoint frame index. Frames may cover only the most recent turns
    // (in-memory cap, or pruned git history), so offset by however many of the oldest turns lack one.
    const frame = p - (users.length - checkpoints.size);
    // Offer what to restore — only surface the code options when this turn still has a file checkpoint.
    // Duplex: conversation-only — frames are per TASK, not per turn, so the turn↔frame mapping is invalid
    // (code rollback is /rewind, /undo, or saying "undo that" to the voice).
    let mode = 'convo';
    if (!duplex && frame >= 0 && frame < checkpoints.size) {
      const m = await selectMenu(process.stderr, { title: 'Restore…', items: [
        { label: 'Conversation and code', value: 'both' },
        { label: 'Conversation only', value: 'convo' },
        { label: 'Code only', value: 'code' },
      ] });
      if (m == null) return undefined;
      mode = m;
    }
    const text = contentText(face.transcript[idx].content).split('\n\n--- @')[0].trim();
    if (mode === 'code' || mode === 'both') {
      try {
        const { restored, deleted } = await checkpoints.rewindTo(frame);
        err(green('  ⟲ code restored') + dim(` — ${restored} file(s)${deleted ? `, removed ${deleted} new file(s)` : ''}\n`));
      } catch (e: any) { err(red(`  ${e?.message ?? e}\n`)); }
    }
    if (mode === 'convo' || mode === 'both') {
      face.transcript = face.transcript.slice(0, idx); // rewind the conversation to before that message
      session.messages = face.transcript;
      try { store.save(session); } catch (e) { log.debug('session save after rewind failed', e); }
      err(green('  ⟲ jumped back') + dim(` — ${face.transcript.length} message(s) kept; edit + resend\n`));
      return text;
    }
    return undefined; // code-only: nothing to pre-fill
  };
  const pickSession = async (global = false) => {
    const list = global ? globalSessionList() : store.list();
    if (!list.length) { err(dim(`  (no saved sessions${global ? '' : ' in this project'} yet)\n`)); return; }
    const items: SelectItem[] = list.slice(0, 50).map((m) => {
      const where = global && m.cwd !== cwd ? ` · ${m.cwd.split('/').pop()}` : '';
      return { label: `${m.id}  ${m.title || '(untitled)'}`, value: m.id, desc: `${ago(m.updated)} · ${m.turns} turn${m.turns === 1 ? '' : 's'}${where}` };
    });
    const id = await selectMenu(process.stderr, { title: global ? 'Resume a session (all projects)' : 'Resume a session', items, current: session.meta.id });
    if (!id) return;
    const data = store.load(id) ?? globalSessionLoad(id);
    if (data) resumeInto(data); else err(red('  no such session\n'));
  };
  const announcedTasks = new Set<string>(); // task ids whose ◔ in-flow marker already printed (once each)
  // One seam for every interactive turn: duplex dispatches through dx.send (mutex + delegation) and
  // skips per-turn checkpoints (frames are per task, opened by the engine's onTaskStart).
  const replacedVoiceTurns = new Set<string>(); // voice turn ids superseded by a re-finalization (see onUtterance)
  const turn = async (task: string, opts?: TurnOpts) => {
    // Prepend any `!cmd` output accumulated since the last turn (CC: bash mode adds to context).
    if (bangContext.length) { task = `${bangContext.join('\n\n')}\n\n${task}`; bangContext.length = 0; }
    const delta = await refreshCatalogs().catch((e) => { log.debug('catalog refresh failed', e); return ''; });
    if (delta) { err(dim('  ⟳ skill/command catalog changed — delta attached to this turn\n')); task += '\n\n' + delta; }
    const r = await runTurn(face, store, session, task, duplex ? undefined : checkpoints, cwd, sendVia ? (c) => sendVia(c, opts) : routeVia);
    if (voiceIO) { voiceReasonEnd(); voiceEchoEnd(); editorRef?.resume(); } // close reasoning + append-only echo lines, then repaint below
    // Close the spoken turn's TTS context (idempotent; audio drains out; also clears a barge's interrupted
    // latch) — except for a REPLACED voice turn: that context now belongs to the turn replacing it.
    if (!(opts?.id && replacedVoiceTurns.delete(opts.id))) voiceIO?.endSpeech();
    // Duplex: a turn can END while work continues — mark it IN THE FLOW once per task (the "✓ done"
    // footer reads as "turn over"); after that the stacked ticking footer line carries the signal.
    if (dx) {
      const fresh = [...dx.tasks.values()].filter((t) => t.status === 'running' && !announcedTasks.has(t.id));
      fresh.forEach((t) => announcedTasks.add(t.id));
      if (fresh.length) err('\r\x1b[0J' + cyan(`  ◔ ${fresh.length === 1 ? `task ${fresh[0].id} (${fresh[0].label})` : `${fresh.length} tasks`} working in the background`) + dim(' — the result will appear here; keep chatting meanwhile\n'));
    }
    return r;
  };
  scheduleTurn = turn; // bind for the scheduler's fire callback
  const runSkill = async (sk: SkillInfo, extra = '') => {
    // expandEntry keeps the whole SKILL.md (frontmatter is part of the instructions) but fills
    // $ARGUMENTS/$1… and embeds @file refs — same expansion commands get (merged-namespace parity).
    try { await turn(await expandEntry(fs, sk.path, extra)); }
    catch (e: any) { err(red(`  couldn't load skill ${sk.name}: ${e?.message ?? e}\n`)); }
  };
  const runCommand = async (c: CommandInfo, extra = '') => { await turn(await expandCommand(fs, c, extra)); };
  const pickAndRun = async (kind: 'skill' | 'command') => {
    const pool = kind === 'skill' ? skills : cmds;
    if (!pool.length) { err(dim(`  (none — add ./.agent/${kind === 'skill' ? 'skills/<name>/SKILL.md' : 'commands/<name>.md'})\n`)); return; }
    const items: SelectItem[] = pool.map((p) => ({ label: p.name, value: p.name, desc: (p as { description?: string }).description }));
    const name = await selectMenu(process.stderr, { title: `Run a ${kind}`, items });
    if (!name) return;
    if (kind === 'skill') await runSkill(skills.find((s) => s.name === name)!);
    else await runCommand(cmds.find((c) => c.name === name)!);
  };

  const pickModel = async (current: string): Promise<string | null> => {
    const all = listModels();
    // group: provider → model line (by displayName prefix, e.g. "Claude Opus") → versions newest-first
    const byProvider = new Map<string, Map<string, { id: string; date: string; name: string }[]>>();
    for (const id of all) {
      const provider = getProviderFromModel(id) ?? 'other';
      const info = getModelInfo(id);
      const name = info?.displayName ?? id.slice(provider.length + 1);
      const line = name.replace(/\s+\d[\d.]*$/, '') || name;
      if (!byProvider.has(provider)) byProvider.set(provider, new Map());
      const lines = byProvider.get(provider)!;
      if (!lines.has(line)) lines.set(line, []);
      lines.get(line)!.push({ id, date: info?.releaseDate ?? '', name });
    }
    const providerItems: SelectItem[] = [];
    for (const [provider, lines] of Array.from(byProvider.entries()).sort((a, b) => a[0].localeCompare(b[0]))) {
      const lineItems: SelectItem[] = [];
      for (const [, versions] of Array.from(lines.entries()).sort((a, b) => a[0].localeCompare(b[0]))) {
        versions.sort((a, b) => b.date.localeCompare(a.date));
        const head = versions[0];
        if (versions.length === 1) {
          lineItems.push({ label: head.name, value: head.id });
        } else {
          const children = versions.slice(1).map((v) => ({ label: v.id.split('/').pop()!, value: v.id, desc: v.date || undefined }));
          lineItems.push({ label: head.name, value: head.id, desc: `+${children.length} older`, children });
        }
      }
      const dateOf = (it: SelectItem) => getModelInfo(it.value)?.releaseDate ?? '0';
      lineItems.sort((a, b) => dateOf(b).localeCompare(dateOf(a)));
      const total = Array.from(lines.values()).reduce((n, v) => n + v.length, 0);
      providerItems.push({ label: provider, value: '__provider__', desc: `${total} models`, children: lineItems });
    }
    const picked = await selectMenu(process.stderr, { title: `Select a model · current: ${current}`, items: providerItems, current, filterable: true });
    return picked && !picked.startsWith('__') ? picked : null;
  };

  let goalCondition: string | undefined = (session.meta as any).goalCondition;
  let goalTurns: number = (session.meta as any).goalTurns ?? 0;
  let goalTokens: number = (session.meta as any).goalTokens ?? 0;
  let goalLastReason: string | undefined = (session.meta as any).goalLastReason;
  const GOAL_MAX_TURNS = 50;
  const persistGoal = () => {
    const m = session.meta as any;
    m.goalCondition = goalCondition; m.goalTurns = goalTurns; m.goalTokens = goalTokens; m.goalLastReason = goalLastReason;
  };

  // ---- /goal: autonomous loop with a halting condition ----
  const goalLoop = async () => {
    while (goalCondition && !aborting && !exitRequested && goalTurns < GOAL_MAX_TURNS) {
      const result = await evaluateGoal(ai, goalCondition, face.transcript, err);
      goalLastReason = result.reason;
      if (result.met) {
        err(green(`  ✓ goal met: ${result.reason}\n`));
        goalCondition = undefined; goalLastReason = undefined;
        persistGoal();
        return;
      }
      err(dim(`  ◎ not yet (${result.reason}) — turn ${goalTurns + 1}\n`));
      aborting = false;
      const tokensBefore = session.meta.tokens ?? 0;
      await turn(`Continue working toward the goal: ${goalCondition}`);
      goalTokens += (session.meta.tokens ?? 0) - tokensBefore;
      goalTurns++;
      persistGoal();
      if (exitRequested) return;
    }
    if (goalTurns >= GOAL_MAX_TURNS) {
      err(yellow(`  ⚠ goal reached ${GOAL_MAX_TURNS} turns — pausing. /goal to check status, /goal clear to cancel\n`));
    }
  };

  // ---- slash builtins: name → handler. Returns true to exit the REPL. ----
  const builtins: Record<string, { desc: string; run: (a: string[]) => boolean | void | Promise<boolean | void> }> = {
    help: { desc: 'show this help', run: () => { err(HELP + '\n'); } },
    version: {
      desc: 'show CLI version + runtime',
      run: () => {
        const rt = (process.versions as any).bun ? `bun ${(process.versions as any).bun}` : `node ${process.versions.node}`;
        err(`  ${bold('agentx')} ${cyan('v' + VERSION)}${dim(` · ${duplex ? `reflex ${dx!.options.reflexModel} · act ${work.model}${dx!.options.thinkModel !== false ? ` · think ${dx!.options.thinkModel}` : ''}` : work.model} · ${rt}`)}\n`);
      },
    },
    tools: {
      desc: 'list available tools',
      run: () => {
        if (duplex) err(dim('  voice:  ' + face.options.tools.map((t) => t.name).join(', ') + '\n  worker: ' + (work.tools ?? []).map((t) => t.name).join(', ') + '\n'));
        else err(dim('  ' + agent.toolNames.join(', ') + '\n'));
      },
    },
    permissions: {
      desc: 'show the active permission rules + default posture',
      run: () => {
        const pol = work.permissions;
        const rules = pol?.options.rules ?? [];
        if (!rules.length) err(dim('  (no rules — default: ' + (pol?.options.default ?? 'allow') + ')\n'));
        else { err(dim('  default: ' + (pol!.options.default) + '\n')); for (const r of rules) err(dim('  · ' + describeRule(r) + '\n')); }
        err(dim('  add persisted rules under .agent/config → permissions: { allow|ask|deny: ["Tool(glob)"] }\n'));
      },
    },
    status: {
      desc: 'session status — model, dir, fs-mode, permissions, tools, usage',
      run: () => {
        const mode = args.vfs ? 'sandbox (VFS — disk untouched)' : args.boddb ? `boddb (database workspace at ${args.boddb} — disk untouched)` : args.shell ? 'disk + real /bin/sh' : 'disk (full real FS, like Claude Code)';
        const pol = work.permissions;
        const perm = !pol ? 'allow all (unattended)' : `${pol.options.rules.length} rule(s), default ${pol.options.default}`;
        const model = duplex ? `reflex ${dx!.options.reflexModel} · act ${work.model}${dx!.options.thinkModel !== false ? ` · think ${dx!.options.thinkModel}` : ''}` : work.model;
        err(formatStatus({ model, cwd, mode, tools: (duplex ? work.tools ?? [] : agent.options.tools).map((t) => t.name), permissions: perm, turns: session.meta.turns, tokens: session.meta.tokens ?? 0, sessionId: session.meta.id, estimated: session.meta.costEstimated ?? false }));
        if (duplex && dx!.tasks.size) err(dim(`  tasks: ${[...dx!.tasks.values()].map((t) => `${t.id}:${t.status}`).join(' ')}\n`));
      },
    },
    cost: {
      desc: 'cumulative cost + token usage for this session',
      run: () => {
        const t = session.meta.tokens ?? 0, usd = session.meta.costUsd ?? 0;
        const priced = getModelInfo(work.model)?.pricing;
        const est = session.meta.costEstimated ?? false; // false once every turn reported exact usage
        const m = est ? '~' : '';
        const note = priced ? (est ? ' (estimated — some turns streamed without usage)' : ' (exact — provider-reported usage)') : ` (no pricing for ${work.model})`;
        err(dim(`  ${usd > 0 ? m + fmtUsd(usd) + ' · ' : ''}${m}${(t / 1000).toFixed(1)}k tokens across ${session.meta.turns} turn(s)${note}\n`));
      },
    },
    context: {
      desc: 'context-window usage (messages + estimated tokens)',
      run: () => {
        const est = estimateTranscriptTokens(face.transcript, face.options.keepRecentImages, face.options.imageInput?.(face.options.model) !== false);
        const cap = AgentClass.contextCap(face.options);
        err(dim(`  ${face.transcript.length} message(s) · ~${(est / 1000).toFixed(1)}k tokens (~${Math.round((est / cap) * 100)}% of ${Math.round(cap / 1000)}k budget)\n`));
      },
    },
    transcript: {
      desc: 'full session transcript incl. complete tool results — /transcript [n] (last n turns), paged via less',
      run: async (a) => {
        const n = a[0] ? Math.max(1, Number(a[0]) || 1) : undefined;
        const text = formatTranscriptFull(face.transcript, { lastTurns: n });
        // Page through `less -R` on a TTY (ANSI-aware, q to exit); print directly when piped/short.
        if (tty && text.split('\n').length > (process.stderr.rows ?? 40)) {
          const wasRaw = process.stdin.isTTY && process.stdin.isRaw;
          if (wasRaw) process.stdin.setRawMode(false);
          try {
            const { spawnSync } = await import('node:child_process');
            const r = spawnSync('less', ['-R'], { input: text, stdio: ['pipe', 'inherit', 'inherit'] });
            if (r.error) err(text); // no less on PATH → plain dump
          } finally { if (wasRaw) process.stdin.setRawMode(true); }
        } else err(text);
      },
    },
    doctor: {
      desc: 'environment sanity check — keys, config, sessions, memory, MCP, model pricing',
      run: async () => {
        const ok = (s: string) => err(green('  ✓ ') + s + '\n');
        const warn = (s: string) => err(yellow('  ⚠ ') + s + '\n');
        const bad = (s: string) => err(red('  ✗ ') + s + '\n');
        err(dim(`  agentx v${VERSION} · bun ${process.versions.bun ?? '?'} · ${process.platform}\n`));
        // provider keys
        const keys = ['ANTHROPIC_API_KEY', 'OPENAI_API_KEY', 'GOOGLE_API_KEY', 'GROQ_API_KEY'].filter((k) => process.env[k]);
        keys.length ? ok(`provider keys: ${keys.join(', ')}`) : bad('no provider keys set (ANTHROPIC_API_KEY / OPENAI_API_KEY / GOOGLE_API_KEY / GROQ_API_KEY)');
        process.env.BODIFY_API_KEY && process.env.BODIFY_APP_ID
          ? ok(`bodify secrets: ${process.env.BODIFY_APP_ID}`)
          : warn('bodify secrets: not configured (set BODIFY_API_KEY + BODIFY_APP_ID)');
        // voice readiness — keys + audio deps + mic permission (so --voice issues surface before you try it)
        {
          const { spawnSync } = await import('node:child_process');
          const has = (cmd: string) => spawnSync('which', [cmd]).status === 0;
          if (!VoiceIO.available()) warn('voice: keys missing (SONIOX_API_KEY / CARTESIA_API_KEY / CARTESIA_VOICE_ID) — voice disabled');
          else {
            ok('voice: keys present');
            has('ffmpeg') ? ok('voice: ffmpeg installed') : bad('voice: ffmpeg missing — `brew install ffmpeg`');
            if (process.platform === 'darwin') {
              has('swiftc') ? ok('voice: swiftc present (AEC echo cancellation)') : warn('voice: no swiftc — `xcode-select --install` for echo cancellation (else heuristic tier)');
              const mic = micPermissionStatus();
              if (mic === 'authorized') ok('voice: microphone permission granted');
              else if (mic === 'denied') bad('voice: microphone permission DENIED — System Settings → Privacy & Security → Microphone, then restart your terminal');
              else if (mic === 'notDetermined') warn('voice: microphone permission not yet granted — you\'ll be prompted on first --voice');
            }
          }
        }
        // model + pricing
        const info = getModelInfo(work.model);
        info?.pricing ? ok(`model ${work.model} — priced (${info.pricing.inputCostPer1K}/${info.pricing.outputCostPer1K} per 1k in/out)`)
          : warn(`model ${work.model} — no pricing in the catalog (costs will show ~$0; verify the id)`);
        // config files
        const cfgFiles = ['ts', 'js', 'json'].flatMap((e) => [`${cwd}/.agent/config.${e}`, `${homedir()}/.agent/config.${e}`]).filter((p) => existsSync(p));
        cfgFiles.length ? ok(`config: ${cfgFiles.join(', ')}`) : warn('no .agent/config.* found (project or ~) — running on defaults');
        // session store writable
        try {
          const probe = `${cwd}/.agent/sessions/.doctor-probe`;
          mkdirSync(`${cwd}/.agent/sessions`, { recursive: true }); writeFileSync(probe, 'ok'); unlinkSync(probe);
          ok(`session store writable (${cwd}/.agent/sessions)`);
        } catch (e: any) { bad(`session store not writable: ${e?.message ?? e}`); }
        // memory
        const memDir = primaryMemDir(face.options.memoryDir, adot('memory'));
        try { const idx = await face.options.fs!.readFile(`${memDir}/MEMORY.md`); ok(`memory: ${memDir} (${idx.split('\n').filter((l) => l.startsWith('- ')).length} pointer(s))`); }
        catch { warn(`memory: ${memDir} — no MEMORY.md yet (save one with #<note>)`); }
        // MCP: configured vs mounted
        const conf = Object.keys(cfg.mcpServers ?? {});
        const up = new Set(mounted.map((m) => m.name));
        const down = conf.filter((n) => !up.has(n) && !cfg.mcpServers?.[n]?.disabled);
        if (!conf.length && !mounted.length) ok('mcp: none configured');
        else if (down.length) bad(`mcp: ${down.length} configured server(s) not mounted: ${down.join(', ')} (try /mcp reconnect <name>)`);
        else ok(`mcp: ${mounted.length} server(s) mounted${conf.length ? ` (${conf.length} configured)` : ''}`);
        // fs / checkpoints / hooks
        ok(`fs: ${args.vfs ? 'sandbox (VFS)' : args.boddb ? `boddb (${args.boddb})` : 'disk'} · checkpoints: ${args.vfs || args.boddb ? 'in-memory/db' : 'git-backed'}`);
        const hookCount = Object.values(cfg.hooks ?? {}).reduce((n: number, v: any) => n + (Array.isArray(v) ? v.length : 0), 0);
        if (hookCount) ok(`hooks: ${hookCount} configured`);
      },
    },
    reload: {
      desc: 'rescan skills/commands dirs and rebuild the system prompt (one cache miss) — picks up entries created mid-session',
      run: async () => {
        await refreshCatalogs().catch((e) => { log.debug('catalog refresh failed', e); });
        face.reprepare(); // next send rebuilds prompt + tools (incl. Skill/SlashCommand catalogs)
        err(green(`  ✓ reloaded — ${skills.length} skill(s), ${cmds.length} command(s); system prompt rebuilds on next message\n`));
      },
    },
    cwd: {
      desc: 'print the working directory (to switch, relaunch with -C <dir>)',
      run: (a) => {
        if (a[0]) { err(yellow(`  mid-session dir change isn't supported (fs + MCP are bound at startup) — relaunch:\n`) + dim(`    agentx -C ${a[0]}\n`)); return; }
        err(dim('  ' + cwd + '\n'));
      },
    },
    sandbox: {
      desc: 'show filesystem access mode (disk = full real FS like Claude Code · sandbox = in-memory copy); to switch, relaunch with/without --sandbox',
      run: () => {
        const access = args.boddb
          ? `boddb — files live in a database at ${args.boddb} (persists across runs), the real disk is never modified`
          : args.vfs
          ? 'sandbox — in-memory copy of cwd, the real disk is never modified'
          : 'disk — full real filesystem (root / = machine root), like Claude Code';
        const virtual = args.vfs || !!args.boddb;
        err(dim(`  fs access: ${access}\n`));
        err(dim(`  checkpoints: ${virtual ? (args.boddb ? 'in-database (persists)' : 'in-memory (per session)') : 'durable git-backed — whole tree incl. bash edits, survives sessions'} · /rewind /undo\n`));
        err(yellow(`  switching mid-session isn't supported (the filesystem is bound at startup) — relaunch:\n`) + dim(`    agentx ${virtual ? '' : '--sandbox '}…\n`));
      },
    },
    model: {
      desc: 'switch model — /model <id> (duplex: the worker), or /model alone for a picker (duplex: pick a tier first)',
      run: async (a) => {
        if (a[0]) { setModel(a[0]); return; } // arg form always targets the worker (scripting/muscle-memory)
        if (duplex) { await pickModelByTier(); return; } // no-arg in duplex → reflex/act/think submenu
        const picked = await pickModel(work.model);
        if (picked) setModel(picked);
        else err(dim('  ' + work.model + '\n'));
      },
    },
    ...(duplex ? { workers: {
      desc: 'duplex worker chrome — /workers <full|minimal>: per-step ⚙ tool activity vs task events only',
      run: async (a: string[]) => {
        if (a[0] === 'full' || a[0] === 'minimal') { workerChrome = a[0]; err(green(`  ✓ worker chrome → ${a[0]}\n`)); return; }
        err(dim(`  worker chrome: ${workerChrome} (use /workers full|minimal)\n`));
      },
    }, voice: {
      desc: 'toggle live voice I/O on/off mid-session (or say "voice off"; needs SONIOX/CARTESIA keys + a TTY)',
      run: async () => {
        if (!toggleVoice) { err(dim('  (voice needs --duplex on a TTY)\n')); return; }
        await toggleVoice();
      },
    }, 'voice-emotions': {
      desc: 'show/hide the [emotion] debug tags in the echo — /voice-emotions <on|off> (TTS emotion control stays on regardless)',
      run: async (a: string[]) => {
        if (!emotionsOn) { err(dim('  (emotion control is off — VOICE_EMOTIONS=0)\n')); return; }
        const v = a[0]?.toLowerCase();
        if (v === 'on' || v === 'off') { showEmotions = v === 'on'; voiceIO?.setShowEmotions(showEmotions); err(green(`  ✓ emotion tags ${showEmotions ? 'shown' : 'hidden'} in echo\n`)); return; }
        err(dim(`  emotion tags in echo: ${showEmotions ? 'shown' : 'hidden'} (use /voice-emotions on|off)\n`));
      },
    }, 'voice-reasoning': {
      desc: 'show/hide the reflex reasoning stream in voice mode — /voice-reasoning <on|off> (never spoken)',
      run: async (a: string[]) => {
        const v = a[0]?.toLowerCase();
        if (v === 'on' || v === 'off') { showReasoning = v === 'on'; if (!showReasoning) voiceReasonEnd(); err(green(`  ✓ reasoning ${showReasoning ? 'shown' : 'hidden'} in voice\n`)); return; }
        err(dim(`  reasoning in voice: ${showReasoning ? 'shown' : 'hidden'} (use /voice-reasoning on|off)\n`));
      },
    }, 'voice-model': {
      desc: 'switch the reflex (voice) model — /voice-model <id>, or alone for a picker',
      run: async (a: string[]) => {
        if (a[0]) { applyReflexModel(a[0]); return; }
        const picked = await pickModel(dx!.options.reflexModel);
        if (picked) applyReflexModel(picked);
        else err(dim(`  reflex ${dx!.options.reflexModel}\n`));
      },
    }, 'think-model': {
      desc: 'switch the think (premium) model, or /think-model off to disable',
      run: async (a: string[]) => {
        if (a[0] === 'off' || a[0] === 'false') {
          dx!.setThinkModel(false); // live: removes the Think tool from the voice agent
          err(green(`  ✓ think tier disabled\n`));
          return;
        }
        if (a[0]) { applyThinkModel(a[0]); return; }
        const current = dx!.options.thinkModel === false ? undefined : dx!.options.thinkModel;
        const picked = await pickModel(current ?? 'anthropic/claude-opus-4-6');
        if (picked) applyThinkModel(picked);
        else err(dim(`  think ${dx!.options.thinkModel === false ? 'off' : dx!.options.thinkModel}\n`));
      },
    }, act: {
      desc: 'spawn a standard worker — /act <brief>',
      run: async (a: string[]) => {
        if (!a.length) { err(dim('  usage: /act <what to do>\n')); return; }
        const id = await dx!.dispatch(a.join(' '), 'act');
        err(dim(`  → task ${id} started\n`));
      },
    }, think: {
      desc: 'spawn a deep-reasoning worker — /think <question>',
      run: async (a: string[]) => {
        if (!a.length) { err(dim('  usage: /think <what to reason about>\n')); return; }
        const off = dx!.options.thinkModel === false; // dispatch silently downgrades — tell the user
        const id = await dx!.dispatch(a.join(' '), 'think');
        err(dim(`  → task ${id} ${off ? '(think tier off — running as act)' : '(think)'} started\n`));
      },
    }, tasks: {
      desc: 'background tasks — /tasks [cancel <id>], or alone for a picker (↵ inspects output; running tasks can be cancelled)',
      run: async (a: string[]) => {
        const all = [...dx!.tasks.values()];
        if (!all.length) { err(dim('  no background tasks\n')); return; }
        if (a[0]?.toLowerCase() === 'cancel') {
          if (!a[1]) { err(yellow('  usage: /tasks cancel <id>\n')); return; }
          err(dim(`  ${dx!.cancelTask(a[1])}\n`)); return;
        }
        const mark = (s: string) => (s === 'running' ? cyan('◔ running') : s === 'done' ? green('✓ done') : s === 'cancelled' ? yellow('⊘ cancelled') : red(`✗ ${s}`));
        const inspect = (t: TaskRecord) => {
          err(`  ${t.id}  ${mark(t.status)}  ${dim(t.label)}\n`);
          for (const l of t.tail.slice(-20)) err(dim(`    ${l}\n`));
          if (t.result) err(dim(`    ⦿ ${t.result.split('\n')[0].slice(0, 160)}\n`));
          if (!t.tail.length && !t.result) err(dim('    (no activity yet)\n'));
        };
        if (!process.stderr.isTTY || !process.stdin.isTTY) { // piped: no menu — plain dump (cancel via /tasks cancel <id>)
          for (const t of all) inspect(t);
          return;
        }
        const items: SelectItem[] = all.map((t) => ({ label: `${t.id}  ${t.label.slice(0, 60)}`, value: t.id, desc: mark(t.status) + ' · ↵ inspect' }));
        const id = await selectMenu(process.stderr, { title: 'Background tasks · ↵ inspect · esc close', items });
        if (!id) return;
        const t = dx!.tasks.get(String(id))!;
        inspect(t);
        if (t.status === 'running') {
          const v = await selectMenu(process.stderr, { title: `Cancel ${t.id}?`, items: [{ label: 'Keep running', value: 'keep' }, { label: 'Cancel the task', value: 'cancel' }], current: 'keep' });
          if (v === 'cancel') err(dim(`  ${dx!.cancelTask(t.id)}\n`));
        }
      },
    } } : {}),
    reasoning: {
      desc: 'extended thinking — /reasoning <off|low|medium|high|tokens>, or alone for an interactive picker (duplex: the workers\')',
      run: async (a) => {
        const current = String(work.reasoning ?? 'off');
        let next: ReasoningEffort;
        if (a[0]) {
          try { next = parseReasoning(a[0]); } catch (e: any) { err(yellow('  ' + (e?.message ?? e) + '\n')); return; }
        } else {
          const items: SelectItem[] = [
            { label: 'off', value: 'off', desc: 'no extended thinking' },
            { label: 'low', value: 'low', desc: 'minimal reasoning (~2k tokens)' },
            { label: 'medium', value: 'medium', desc: 'balanced (~8k tokens)' },
            { label: 'high', value: 'high', desc: 'maximal reasoning (~24k tokens)' },
          ];
          const picked = await selectMenu(process.stderr, { title: `Reasoning effort · current: ${current}`, items, current });
          if (!picked) { err(dim('  ' + current + '\n')); return; }
          next = picked as ReasoningEffort;
        }
        work.reasoning = next;
        if (next !== 'off' && getModelInfo(work.model)?.reasoning === false)
          err(yellow(`  note: ${work.model} has no reasoning capability — setting may be ignored\n`));
        err(dim('  reasoning → ' + next + '\n'));
      },
    },
    config: {
      desc: 'view/change settings — model, reasoning, permission posture, streaming, editor mode',
      run: async () => {
        for (;;) {
          const items: SelectItem[] = [
            { label: 'model', value: 'model', desc: work.model },
            { label: 'reasoning', value: 'reasoning', desc: String(work.reasoning ?? 'off') },
            { label: 'permission posture', value: 'posture', desc: postureLabel() + '  (Shift+Tab)' },
            // streaming is the voice's lifeblood in duplex (always on) — only a normal-mode knob
            ...(duplex ? [] : [{ label: 'streaming', value: 'stream', desc: agent.options.stream ? 'on' : 'off' }]),
            { label: 'editor mode', value: 'editor', desc: cfg.editorMode === 'vim' ? 'vim' : 'normal' },
            { label: 'update check', value: 'updateCheck', desc: cfg.updateCheck === false ? 'off' : 'on' },
          ];
          const pick = await selectMenu(process.stderr, { title: 'Settings · ↵ change · esc close', items });
          if (!pick) return;
          if (pick === 'model') { const m = await pickModel(work.model); if (m) setModel(m); }
          else if (pick === 'reasoning') { await builtins.reasoning.run([]); persistSetting(cwd, 'reasoning', work.reasoning ?? 'off'); }
          else if (pick === 'posture') { cyclePosture(); persistSetting(cwd, 'permissionMode', posture); }
          else if (pick === 'stream') { agent.options.stream = !agent.options.stream; persistSetting(cwd, 'stream', agent.options.stream); err(dim('  streaming → ' + (agent.options.stream ? 'on' : 'off') + '\n')); }
          else if (pick === 'editor') { cfg.editorMode = cfg.editorMode === 'vim' ? 'normal' : 'vim'; persistSetting(cwd, 'editorMode', cfg.editorMode); err(dim('  editor → ' + cfg.editorMode + '\n')); }
          else if (pick === 'updateCheck') { cfg.updateCheck = cfg.updateCheck === false ? true : false; persistSetting(cwd, 'updateCheck', cfg.updateCheck); err(dim('  update check → ' + (cfg.updateCheck ? 'on' : 'off') + '\n')); }
        }
      },
    },
    rename: {
      desc: 'rename the current session — /rename <title>',
      run: (a) => {
        const t = a.join(' ').trim();
        if (!t) { err(dim('  title: ' + (session.meta.title || '(none)') + '\n')); return; }
        session.meta.title = t;
        try { store.save(session); } catch { /* non-fatal */ }
        setTermTitle(`agentx · ${t}`);
        err(dim('  renamed → ' + t + '\n'));
      },
    },
    compact: {
      desc: 'summarize older context to free up the window — /compact [what to preserve]',
      run: (a) => {
        const focus = a.join(' ').trim() || undefined;
        const r = face.compactNow(12, focus);
        session.messages = face.transcript; try { store.save(session); } catch { /* ignore */ }
        const pct = (t: number) => `${Math.round((t / r.cap) * 100)}%`;
        const k = (t: number) => `${(t / 1000).toFixed(1)}k`;
        const freed = r.tokensBefore - r.tokensAfter;
        if (freed <= 0) { err(dim(`  nothing to compact — ~${k(r.tokensAfter)} tok (${pct(r.tokensAfter)} of window) is already at the floor\n`)); return; }
        err(dim(`  compacted — ~${k(r.tokensBefore)} → ~${k(r.tokensAfter)} tok (${pct(r.tokensBefore)} → ${pct(r.tokensAfter)} of window), freed ~${k(freed)}; folded ${r.removed} message(s)${focus ? ` (preserving: ${focus})` : ''}\n`));
      },
    },
    copy: {
      desc: 'copy the last reply to the clipboard — /copy code = last code block only',
      run: (a) => {
        const last = [...face.transcript].reverse().find((m) => m.role === 'assistant' && contentText(m.content).trim());
        if (!last) { err(dim('  (nothing to copy yet)\n')); return; }
        let text = contentText(last.content).trim();
        if (a[0] === 'code') {
          const fences = [...text.matchAll(/```[^\n]*\n([\s\S]*?)```/g)];
          if (!fences.length) { err(dim('  (no code block in the last reply)\n')); return; }
          text = fences[fences.length - 1][1].trimEnd();
        }
        err(dim(copyTextToClipboard(text) ? `  ✓ copied ${text.length} chars\n` : '  no clipboard tool found (pbcopy/wl-copy/xclip)\n'));
      },
    },
    diff: {
      desc: 'show all file changes this session (oldest checkpoint → now)',
      run: async () => {
        if (!checkpoints.diff) { err(dim('  (diff not supported by this checkpoint backend)\n')); return; }
        await checkpoints.refresh?.();
        if (!checkpoints.size) { err(dim('  (no checkpoints yet — make a turn first)\n')); return; }
        const d = (await checkpoints.diff()).trim();
        if (!d) { err(dim('  (no file changes this session)\n')); return; }
        err(d.split('\n').map((l) => l.startsWith('+') && !l.startsWith('+++') ? green(l) : l.startsWith('-') && !l.startsWith('---') ? red(l) : dim(l)).join('\n') + '\n');
      },
    },
    memory: {
      desc: 'open the memory index in $EDITOR (.agent/memory/MEMORY.md)',
      run: async () => {
        const dir = primaryMemDir(face.options.memoryDir, adot('memory'));
        const idx = `${dir}/MEMORY.md`;
        const fs = face.options.fs!;
        if (args.vfs || args.boddb) { // virtual fs: $EDITOR can't see it — print instead
          try { err(dim(await fs.readFile(idx)) + '\n'); } catch { err(dim('  (no memory yet — save one with `#<note>`)\n')); }
          return;
        }
        try { await fs.readFile(idx); } catch { try { await mkdirp(fs, dir); await fs.writeFile(idx, '# Memory\n\n'); } catch (e: any) { err(red(`  can't create ${idx}: ${e?.message ?? e}\n`)); return; } }
        const ed = process.env.VISUAL || process.env.EDITOR || 'vi';
        const wasRaw = process.stdin.isTTY && process.stdin.isRaw;
        if (wasRaw) process.stdin.setRawMode(false);
        try {
          const { spawnSync } = await import('node:child_process');
          spawnSync(ed, [idx], { stdio: 'inherit' });
        } finally { if (wasRaw) process.stdin.setRawMode(true); }
        err(dim(`  ✎ ${idx}\n`));
      },
    },
    rewind: {
      desc: 'undo file edits back to before an earlier turn (interactive)',
      run: async () => {
        await checkpoints.refresh?.();
        const list = checkpoints.list();
        if (!list.length) { err(dim('  (no checkpoints yet — edits are captured per turn)\n')); return; }
        const items: SelectItem[] = list.map((c) => ({ label: c.label, value: String(c.index), desc: ago(c.at) + (c.files ? ` · ${c.files} file${c.files === 1 ? '' : 's'}` : '') }));
        const pick = await selectMenu(process.stderr, { title: 'Rewind to before…', items });
        if (pick == null) return;
        try {
          const { restored, deleted } = await checkpoints.rewindTo(Number(pick));
          err(green(`  ⟲ rewound`) + dim(` — restored ${restored} file(s)${deleted ? `, removed ${deleted} new file(s)` : ''}\n`) + dim('  (files only; the conversation is unchanged)\n'));
        } catch (e: any) { err(red(`  ${e?.message ?? e}\n`)); }
      },
    },
    undo: {
      desc: 'undo the file edits from the most recent turn',
      run: async () => {
        await checkpoints.refresh?.();
        if (!checkpoints.size) { err(dim('  (nothing to undo)\n')); return; }
        const { restored, deleted } = await checkpoints.rewindTo(checkpoints.size - 1);
        err(green(`  ⟲ undid last turn`) + dim(` — restored ${restored} file(s)${deleted ? `, removed ${deleted} new file(s)` : ''}\n`));
      },
    },
    clear: {
      desc: 'start a fresh conversation (and clear the screen)',
      run: () => { face.transcript = []; bangContext.length = 0; session = startSession({ ...args, cont: false, resume: undefined }, store, face, cwd); triggerServer.use(session.meta.id); err('\x1bc'); },
    },
    sessions: {
      desc: 'pick a saved session to resume — /sessions [all] (all = across all projects)',
      run: (a) => pickSession(a[0] === 'all'),
    },
    resume: {
      desc: 'resume a session — /resume <id>, or /resume alone to pick from a list',
      run: async (a) => {
        if (!a[0]) { await pickSession(); return; }
        const data = store.load(a[0]) ?? globalSessionLoad(a[0]);
        if (data) resumeInto(data); else err(red(`  no such session\n`));
      },
    },
    agents: {
      desc: 'list subagent types (./.agent/agents) — /agents new <name> scaffolds one',
      run: async (a) => {
        const fs = face.options.fs!;
        if (a[0] === 'new') {
          let name = a[1];
          if (!name) {
            const io = createInterface({ input: keyInput, output: process.stderr });
            try { name = (await io.question(yellow('  agent name: '))).trim(); } finally { io.close(); }
          }
          name = (name ?? '').replace(/[^A-Za-z0-9_-]/g, '-').replace(/^-+|-+$/g, '');
          if (!name) { err(yellow('  usage: /agents new <name>\n')); return; }
          const dir = adot('agents'); const p = `${dir}/${name}.md`;
          if (await fs.exists(p)) { err(yellow(`  ${p} already exists\n`)); return; }
          await mkdirp(fs, dir);
          await fs.writeFile(p, [
            '---',
            `description: What "${name}" is for — the Task tool picks agents by this line`,
            '# model: anthropic/claude-haiku-4-5    # optional per-agent model override',
            '# tools: Read, Grep, Glob              # optional tool allowlist (omit = all tools)',
            '---',
            '',
            `You are the "${name}" subagent.`,
            '',
            'Describe the persona, scope, and output contract here — this body becomes the',
            "child agent's system prompt when a Task is delegated with agentType: \"" + name + '".',
            '',
          ].join('\n'));
          err(green(`  ✓ ${p}`) + dim(` — used via Task(agentType:"${name}")${args.subagents ? '' : ' (enable with --subagents)'}\n`));
          return;
        }
        const seen = new Map<string, { def: AgentDef; from: string }>();
        for (const d of adots('agents')) {
          try { for (const def of (await loadAgents(fs, d)).agents) if (!seen.has(def.name)) seen.set(def.name, { def, from: d }); }
          catch (e) { log.debug(`loadAgents(${d}) failed`, e); }
        }
        if (!seen.size) { err(dim('  (no subagents defined — scaffold one with /agents new <name>)\n')); return; }
        for (const { def, from } of seen.values()) {
          const extras = [def.model, def.tools?.length ? `tools: ${def.tools.join(',')}` : ''].filter(Boolean).join(' · ');
          err(`  ${cyan(def.name)} ${dim(`— ${def.description || '(no description)'}${extras ? ` (${extras})` : ''} · ${from}`)}\n`);
        }
        err(dim(`  delegated via the Task tool (agentType)${args.subagents ? '' : ' — enable with --subagents'} · /agents new <name> to scaffold\n`));
      },
    },
    commands: { desc: 'pick a custom slash command to run (./.agent/commands)', run: () => pickAndRun('command') },
    skills: { desc: 'pick a skill to run (./.agent/skills)', run: () => pickAndRun('skill') },
    mcp: {
      desc: 'manage MCP servers — /mcp [add <name> <cmd|url>] [login <name>] [reconnect <name>] [remove <name>] [tools [name]] [resources [name]]',
      run: async (a) => {
        const sub = a[0]?.toLowerCase();
        if (sub === 'login') {
          const name = a[1];
          const target = name ? cfg.mcpServers?.[name] : undefined;
          if (!name || !target?.url) { err(yellow('  usage: /mcp login <name>  (name must be a configured http server)\n')); return; }
          try {
            err(dim(`  opening browser to authorize "${name}"…\n`));
            await oauth.register(target.url);
            err(green(`  ✓ authorized "${name}"`) + dim(' — remounting with the new token\n'));
            // remount this server with the freshly-minted bearer token
            const idx = mounted.findIndex((m) => m.name === name);
            if (idx >= 0) await mounted.splice(idx, 1)[0].client.close().catch(() => {});
            const m = await mountMcpServer(name, { ...target, bearerToken: await oauth.tokenFor(target.url) });
            mounted.push(m); remountMcpTools();
            err(green(`  ✓ ${m.name}`) + dim(` — ${m.tools.length} tool(s)\n`));
          } catch (e: any) { err(red(`  login failed: ${e?.message ?? e}\n`)); }
          return;
        }
        if (sub === 'add') {
          const name = a[1]; const target = a.slice(2).join(' ');
          if (!name || !target) { err(yellow('  usage: /mcp add <name> <command ...> | <url>\n')); return; }
          if (mounted.find((m) => m.name === name)) { err(yellow(`  MCP "${name}" already mounted\n`)); return; }
          const cfg: McpServerConfig = target.startsWith('http://') || target.startsWith('https://') ? { url: target } : { command: target.split(' ')[0], args: target.split(' ').slice(1) };
          try {
            const m = await mountMcpServer(name, cfg);
            mounted.push(m);
            remountMcpTools();
            err(green(`  ✓ ${m.name}`) + dim(` — ${m.tools.length} tool(s)${m.serverInfo?.name ? ` from ${m.serverInfo.name}` : ''}\n`));
          } catch (e: any) { err(red(`  failed to mount "${name}": ${e?.message ?? e}\n`)); }
          return;
        }
        if (sub === 'reconnect') {
          const name = a[1];
          if (!name) { err(yellow('  usage: /mcp reconnect <name>\n')); return; }
          const idx = mounted.findIndex((m) => m.name === name);
          const conf = idx >= 0 ? mounted[idx].config : cfg.mcpServers?.[name]; // mounted config survives ad-hoc /mcp add
          if (!conf) { err(yellow(`  MCP "${name}" not found (not mounted and not in config)\n`)); return; }
          if (idx >= 0) {
            const old = mounted.splice(idx, 1)[0];
            await old.client.close().catch((e) => log.debug('mcp close failed', e));
          }
          try {
            const m = await mountMcpServer(name, conf);
            mounted.push(m); remountMcpTools();
            err(green(`  ✓ reconnected "${name}"`) + dim(` — ${m.tools.length} tool(s)\n`));
          } catch (e: any) { err(red(`  reconnect failed: ${e?.message ?? e}\n`)); }
          return;
        }
        if (sub === 'remove') {
          const name = a[1];
          if (!name) { err(yellow('  usage: /mcp remove <name>\n')); return; }
          const idx = mounted.findIndex((m) => m.name === name);
          if (idx < 0) { err(yellow(`  MCP "${name}" not found\n`)); return; }
          const m = mounted.splice(idx, 1)[0];
          remountMcpTools();
          await m.client.close().catch((e) => log.debug('mcp close failed', e));
          err(dim(`  removed "${name}"\n`));
          return;
        }
        if (sub === 'tools') {
          const filter = a[1];
          const targets = filter ? mounted.filter((m) => m.name === filter) : mounted;
          if (!targets.length) { err(dim(`  (no ${filter ? `MCP server "${filter}"` : 'MCP servers'} found)\n`)); return; }
          for (const m of targets) {
            err(`  ${cyan(m.name)} ${dim(`(${m.tools.length} tools)`)}\n`);
            for (const t of m.tools) err(dim(`     - ${t.name.replace(`mcp__${m.name}__`, '')}${t.description ? ' — ' + t.description.split('\n')[0] : ''}\n`));
          }
          return;
        }
        if (sub === 'resources') {
          const filter = a[1];
          const targets = filter ? mounted.filter((m) => m.name === filter) : mounted;
          if (!targets.length) { err(dim(`  (no ${filter ? `MCP server "${filter}"` : 'MCP servers'} found)\n`)); return; }
          for (const m of targets) {
            try {
              const resources = await m.client.listResources();
              if (!resources.length) { err(dim(`  ${m.name}: (no resources)\n`)); continue; }
              err(`  ${cyan(m.name)} ${dim(`(${resources.length} resources)`)}\n`);
              for (const r of resources) err(dim(`     - ${r.uri}${r.name ? ' — ' + r.name : ''}${r.mimeType ? ' [' + r.mimeType + ']' : ''}\n`));
            } catch (e: any) { err(dim(`  ${m.name}: resources not supported (${e?.message})\n`)); }
          }
          return;
        }
        // default: interactive menu — server-first, each server drills into its own actions.
        const items: SelectItem[] = mounted.map((m) => {
          const ver = m.serverInfo?.name ? ` · ${m.serverInfo.name}${m.serverInfo.version ? ' v' + m.serverInfo.version : ''}` : '';
          return {
            label: `${green('✓')} ${cyan(m.name)}`,
            value: `__srv_${m.name}`,
            desc: `${m.tools.length} tools${ver}`,
            children: [
              { label: 'tools', value: `tools:${m.name}`, desc: 'list this server\'s tools' },
              { label: 'resources', value: `resources:${m.name}`, desc: 'list server resources' },
              { label: 'reconnect', value: `reconnect:${m.name}`, desc: 'remount (hung/restarted)' },
              ...(m.config?.url ? [{ label: 'login', value: `login:${m.name}`, desc: 'authenticate (OAuth)' }] : []),
              { label: 'remove', value: `remove:${m.name}`, desc: 'unmount this server' },
            ],
          };
        });
        items.push({ label: `${dim('+')} add server`, value: 'add', desc: 'mount a new MCP server' });
        const picked = await selectMenu(process.stderr, { title: `MCP servers (${mounted.length})`, items });
        if (!picked) return;
        // picked is either 'add' or '<action>:<server-name>'
        const ci = picked.indexOf(':');
        if (ci >= 0) { a = [picked.slice(0, ci), picked.slice(ci + 1)]; return builtins.mcp.run(a); }
        a = [picked];
        if (picked === 'add') {
          const io = createInterface({ input: keyInput, output: process.stderr });
          try {
            const name = (await io.question(yellow('  name: '))).trim();
            const target = (await io.question(yellow('  command or url: '))).trim();
            if (!name || !target) { err(yellow('  cancelled\n')); return; }
            a = ['add', name, ...target.split(' ')];
          } finally { io.close(); }
        }
        // fall through to re-run with the assembled args
        return builtins.mcp.run(a);
      },
    },
    init: { desc: 'scaffold ./AGENTS.md project instructions', run: () => initInstructions(cwd) },
    paste: {
      desc: 'attach an image from the clipboard (macOS, = Ctrl-V) — /paste [message] sends now, /paste alone attaches to your next message',
      run: async (a) => {
        const att = grabClipboardAttachment();
        if (!att) { err(yellow('  no image on the clipboard') + dim(' — copy or screenshot one, then /paste\n')); return; }
        const msg = a.join(' ').trim();
        if (msg) await turn(`${msg} ${att.ref}`); // grab + send in one go
        else { pendingImages.push(att.path); err(green(`  ✓ image attached (#${pendingImages.length})`) + dim(' — type your message to send it\n')); }
      },
    },
    export: {
      desc: 'save this conversation to Markdown — /export [path] (default ./.agent/exports/<id>.md)',
      run: (a) => {
        const shown = face.transcript.filter((m) => m.role !== 'system');
        if (!shown.length) { err(dim('  (nothing to export yet)\n')); return; }
        const md = exportMarkdown(session.meta, shown);
        // Write to REAL disk (node fs) so the file is findable even in --vfs sandbox mode.
        const name = a[0] ? (extname(a[0]) ? a[0] : a[0] + '.md') : join('.agent', 'exports', `${session.meta.id}.md`);
        const path = resolve(cwd, name);
        try {
          mkdirSync(dirname(path), { recursive: true });
          writeFileSync(path, md);
          err(green(`  ✓ exported → ${path}\n`) + dim(`  ${shown.length} message(s) · ${md.length} chars\n`));
        } catch (e: any) { err(red(`  export failed: ${e?.message ?? e}\n`)); }
      },
    },
    goal: {
      desc: 'autonomous loop — /goal <condition> | /goal (status) | /goal clear',
      run: async (a) => {
        if (!a.length) {
          if (!goalCondition) { err(dim('  no active goal\n')); return; }
          const tokStr = goalTokens > 1000 ? `${(goalTokens / 1000).toFixed(1)}k` : String(goalTokens);
          err(`  ${bold('◎ goal:')} ${goalCondition}\n` + dim(`  ${goalTurns} turn${goalTurns === 1 ? '' : 's'} · ${tokStr} tokens${goalLastReason ? ` · last: ${goalLastReason}` : ''}\n`));
          return;
        }
        if (a[0] === 'clear') {
          if (!goalCondition) { err(dim('  no active goal\n')); return; }
          goalCondition = undefined; goalTurns = 0; goalTokens = 0; goalLastReason = undefined;
          persistGoal();
          err(green('  ✓ goal cleared\n'));
          return;
        }
        goalCondition = a.join(' ');
        goalTurns = 0; goalTokens = 0; goalLastReason = undefined;
        persistGoal();
        err(green(`  ◎ goal set: ${goalCondition}\n`) + dim('  working… (Esc to pause)\n'));
        const tokensBefore = session.meta.tokens ?? 0;
        await turn(goalCondition);
        goalTokens += (session.meta.tokens ?? 0) - tokensBefore;
        goalTurns++;
        persistGoal();
        if (!exitRequested) await goalLoop();
        if (exitRequested) return true;
      },
    },
    update: {
      desc: 'check for updates and install if available',
      run: async () => {
        err(dim('  checking…\n'));
        const msg = await checkForUpdate(VERSION).catch(() => null);
        if (!msg) { err(green('  ✓ up to date\n')); return; }
        err(yellow(`  ${msg.replace(/Run .+$/, 'Installing…')}\n`));
        if (installUpdate()) err(green('  ✓ updated — restart agentx to use the new version\n'));
        else err(red('  ✗ update failed — run `bun add -g @livx.cc/agentx` manually\n'));
      },
    },
    exit: { desc: 'quit', run: () => true },
    quit: { desc: 'quit', run: () => true },
  };

  // Banner lines word-wrap with a hanging indent on a TTY (long cwd/model strings used to hard-break mid-word).
  const banner = (line: string) => err((process.stderr.isTTY ? wrapAnsi(line, process.stderr.columns ?? 80, '  ') : line) + '\n');
  banner(bold('agentx') + cyan(' v' + VERSION) + dim(` — ${work.model} · ${cwd}`));
  banner(dim('Type a task, or /help. Type / or @ for live suggestions (↑/↓ ⏎). Esc cancels/clears; double-Esc jumps back; Ctrl-D exits.'));
  if (dx) banner(dim(`◑ duplex — reflex: ${dx.options.reflexModel} · act: ${work.model}${dx.options.thinkModel !== false ? ` · think: ${dx.options.thinkModel}` : ''} (real work runs in background tasks, re-voiced when done)`));
  if (args.updateCheck !== false && cfg.updateCheck !== false) checkForUpdate(VERSION).then((msg) => { if (msg) err(yellow(`  ${msg}\n`)); }).catch(() => {});
  // Live suggestions: file/dir entries from the real cwd; command/skill descriptions for the menu.
  const listDir: DirLister = (absDir) => {
    try {
      return readdirSync(join(cwd, absDir.replace(/^\/+/, '')), { withFileTypes: true })
        .map((d) => ({ name: d.name, dir: d.isDirectory() }));
    } catch (e) { log.debug('completion readdir failed', absDir, e); return null; } // not a dir / unreadable
  };
  // Mid-prompt, a `/name` is prose the MODEL reads — so only offer what it can run via its
  // SlashCommand/Skill tools. REPL builtins (/exit, /model…) never reach the model.
  const agentCommandNames = () => [...cmds.map((c) => c.name), ...skills.map((s) => s.name)];
  const commandNames = () => [...Object.keys(builtins).filter((k) => k !== 'quit'), ...agentCommandNames()];
  const describe = (hit: string): string | undefined =>
    hit.startsWith('/') ? (builtins[hit.slice(1)]?.desc ?? cmds.find((c) => c.name === hit.slice(1))?.description ?? skills.find((s) => s.name === hit.slice(1))?.description) : undefined;
  const suggest = (lineToCursor: string) => {
    const [hits, token] = completeLine(lineToCursor, { commands: commandNames(), agentCommands: agentCommandNames(), listDir, bangHistory: history });
    return { hits, token, describe };
  };

  const editor = createLineEditor(process.stderr);
  editorRef = editor; // async chrome (⦿ results, re-voice) repaints the live prompt through this
  let aborting = false;       // first Esc of a turn aborts; a second Esc means "…and jump back to edit"
  let pendingRewind = false;  // set by double-Esc during a run → REPL opens the jump-back picker once the turn unwinds
  // Esc / Ctrl-C cancel a RUNNING turn (raw mode stays on between prompts, so the key arrives live).
  // Double-Esc while running = cancel + jump back to edit a message (parity with double-Esc at the prompt).
  const classifyPaste = pastePathClassifier(cwd); // drag-dropped/pasted file paths → image/file attachments
  if (process.stdin.isTTY) {
    // Mid-turn type-ahead: a REAL editor (backspace/arrows/kill-keys + bracketed paste collapsing to
    // one placeholder), not a bare append-only buffer. Multi-line pastes land as ONE queued item.
    const sEd = new EditorState(() => ({ hits: [], token: '' }), [], classifyPaste, grabClipboardAttachment);
    stashEd = sEd;
    const inverse = (x: string) => `\x1b[7m${x}\x1b[0m`;
    const stashView = (): string => {
      const v = sEd.buf.replace(/\n/g, '⏎');
      const c = Math.min(sEd.cursor, v.length);
      const at = c < v.length ? String.fromCodePoint(v.codePointAt(c)!) : ' '; // whole code point — v[c] alone can split an emoji
      const q = inputStash.length ? dim(` [${inputStash.length} queued]`) : '';
      return `${dim('› ')}${v.slice(0, c)}${inverse(at)}${v.slice(c + at.length)}${q}`;
    };
    spinner.tail = () => (sEd.buf ? stashView() : ''); // typed text rides the spinner frame (never erased)
    const renderStashBuf = () => {
      if (spinner.active) return; // the next 90ms tick paints it as the spinner tail (self-clearing)
      err(`\r\x1b[K${sEd.buf ? '  ' + stashView() : ''}`);
      typeaheadOnLine = !!sEd.buf; // this line now holds the typed text (no newline) → chrome must clear it before printing
    };
    repaintStash = renderStashBuf; // async chrome repaints the type-ahead line through this

    keyInput.on('keypress', (_s, key) => {
      if (isMenuActive()) return; // a live picker (permission ask) owns the keyboard
      if (!activeTurn && !dispatchPending) return;
      if (key?.ctrl && key?.name === 'o') { toggleVerbose(); return; }
      const k = key?.name;
      // Esc / Ctrl-C cancel the RUNNING turn immediately — typed-ahead text SURVIVES (carried into
      // the next prompt). Esc again → jump-back; Ctrl-C again → force-quit. (CC: Esc always interrupts.)
      if (!sEd.pasting && (k === 'escape' || (key?.ctrl && k === 'c'))) {
        if (!aborting) {
          aborting = true; activeTurn?.abort(); voiceIO?.interrupt();
          err(yellow('\n  ⎋ cancelling…') + dim(' (Ctrl-C again to force-quit)\n'));
          // Watchdog: if the turn hasn't unwound shortly, the abort is wedged — surface the escape hatch.
          setTimeout(() => {
            if (activeTurn) err(red('  ⚠ still cancelling — press Ctrl-C to force-quit\n'));
          }, 4000).unref?.();
        } else if (key?.ctrl && k === 'c') {
          err(red('\n  ⏻ force-quit\n')); forceQuit(); // already cancelling + another Ctrl-C → never trapped
        } else if (k === 'escape' && !pendingRewind) { pendingRewind = true; err(dim('  ⎋⎋ jumping back to edit…\n')); }
        return;
      }
      const action = applyKey(sEd, key ?? {}, _s);
      if (action === 'submit') {
        const text = sEd.expand().trim();
        sEd.reset();
        typeaheadOnLine = false; // the typed line is being consumed (steered/stashed) → no longer owns the row
        if (text) {
          const view = text.replace(/\n+/g, ' ⏎ '); // one-line preview (queued pastes carry real newlines)
          // CC parity: Enter mid-turn STEERS the running turn — the text lands as a user message at the
          // next step boundary (alongside the next tool result). `dispatchPending` (not yet `activeTurn`)
          // means the turn is starting but its aborter isn't set yet — inject() queues onto the agent and
          // the loop drains it at the first step boundary either way, so cover that race window too.
          // Duplex turns run on background workers (not `agent`) → fall back to the post-turn stash queue.
          if ((activeTurn || dispatchPending) && !duplex) {
            agent.inject(text);
            // Colored like the input prompt (bold cyan) so a mid-turn steer reads as YOUR message in the flow.
            err(`\r\x1b[K${bold(cyan('  ↳ steering ›'))} ${cyan(`${view.slice(0, 60)}${view.length > 60 ? '…' : ''}`)}\n`);
          } else {
            inputStash.push(text);
            err(`\r\x1b[K${green('  ✓ stashed')} ${dim(`#${inputStash.length}: ${view.slice(0, 50)}${view.length > 50 ? '…' : ''}`)}\n`);
          }
        }
        return;
      }
      if (sEd.pasting) return; // accumulate silently; one render on paste-end
      if (action === 'eof' || action === 'rewind') return; // Ctrl-D / double-Esc have no meaning mid-turn
      renderStashBuf();
    });
  }
  const promptStr = bold(cyan('agentx › '));
  const contPrompt = dim('  … › '); // `\`-continued lines
  const releaseStdin = () => { releaseKeyInput(); if (process.stdin.isTTY) { err('\x1b[?2004l'); try { process.stdin.setRawMode(false); } catch { /* not a tty */ } } process.stdin.pause(); };

  let prefill: string | undefined; // set by double-Esc jump-back → pre-fills the next prompt
  let tick = 0; // footer spinner frame counter (advances on each ticked re-render)
  /** One dispatch seam for a submitted line — typed (REPL loop) and spoken (voiceIO.onUtterance)
   *  share it, so voice gets !cmd/#note//commands/mentions/persistence identically. Returns 'quit'
   *  when a builtin asked to exit. */
  const dispatchLine = async (line: string, opts?: TurnOpts): Promise<'quit' | void> => {
    if (!opts?.id) takeCut(voiceIO, false); // a typed/scheduled line moves on: the last voice barge cut is stale now
    if (/^[!#/]/.test(line)) dispatchPending = false; // not a model turn — don't capture picker/question keys
    history.unshift(line.replace(/\n+/g, ' ⏎ ')); // make this turn ↑-recallable in-session
    remember(line.replace(/\n+/g, ' ⏎ ')); // persist (one entry per line)

    // `!cmd` — run a shell command inline (no model call), like CC's bash mode.
    if (line.startsWith('!')) {
      const cmd = line.slice(1).trim();
      // Mirror the agent's own Shell availability: real /bin/sh when on disk (unless --no-shell),
      // else wcli's VFS emulator (sandbox/boddb, or --no-shell opt-out).
      if (cmd) {
        const out = await runShellLine(agent.options.fs!, cmd, { realShell: !(args.vfs || args.boddb) && args.shell !== false, cwd });
        err(dim(out + '\n'));
        // CC parity: the command + its output join the conversation context so the model can reason
        // about it on the next turn (folded into that turn's message — avoids a dangling user turn).
        bangContext.push(`<bash-input>${cmd}</bash-input>\n<bash-output>\n${out}\n</bash-output>`);
      }
      return;
    }
    // `#note` — jot a memory, like CC. Writes to the memory index (created if absent).
    if (line.startsWith('#')) {
      const note = line.slice(1).trim();
      if (note) { const where = await appendMemoryNote(agent.options.fs!, primaryMemDir(agent.options.memoryDir, adot('memory')), note); err(green(`  ✎ remembered → ${where}\n`)); }
      return;
    }

    if (line.startsWith('/')) {
      const [name, ...a] = line.slice(1).split(/\s+/);
      if (!name) { err(red('  / needs a command name\n') + dim('  (try /help)\n')); return; } // bare "/" or "/  "
      const b = builtins[name];
      if (b) { if (await b.run(a)) return 'quit'; return; }
      // not a builtin → pick up any skill/command created mid-session before resolving, so a
      // freshly-authored /<name> dispatches without needing a model turn or /reload first.
      await refreshCatalogs().catch((e) => { log.debug('catalog refresh failed', e); });
      // …a skill? invoke it by feeding its SKILL.md (plus any args) as the turn's prompt.
      // Skills resolve BEFORE commands — same precedence as the model's `Skill` tool, and a
      // SKILL.md is the richer/canonical form, so a skill supersedes a same-named legacy command.
      const sk = skills.find((x) => x.name === name);
      if (sk) { await runSkill(sk, a.join(' ')); return; }
      // …or a user-defined custom command?
      const c = cmds.find((x) => x.name === name);
      if (c) { await runCommand(c, a.join(' ')); return; }
      // unknown → enumerate what IS available
      const known = Object.keys(builtins).filter((k) => k !== 'quit').map((k) => '/' + k);
      const custom = [...cmds.map((x) => x.name), ...skills.map((x) => x.name)].map((n) => '/' + n);
      err(red(`  unknown command /${name}\n`) + dim('  builtins: ' + known.join(' ') + '\n') + (custom.length ? dim('  custom:   ' + custom.join(' ') + '\n') : '') + dim('  (or /help)\n'));
      return;
    }

    // Attach any clipboard images grabbed via /paste to this message (reuses the @ref pipeline).
    const task = pendingImages.length ? `${line} ${pendingImages.map((p) => '@' + p).join(' ')}` : line;
    pendingImages.length = 0;
    await turn(task, opts);
    if (goalCondition && !aborting && !exitRequested) { goalTurns++; persistGoal(); await goalLoop(); }
    if (exitRequested) return 'quit';
  };

  // ── Voice I/O (`--voice` + SONIOX/CARTESIA keys on a TTY): spoken utterances enter the SAME
  // dispatch as typed lines (the dx.send mutex serializes); the voice's text_delta stream is
  // spoken via the host tap above. Missing keys → conversational text mode, one-line note.
  let voicePartial = ''; // live partial transcript, rendered in the prompt footer
  let interjectNote = ''; // one-shot model note for an instinct cut-in turn (consumed by onUtterance)
  // Continuity: the instinct's interruption ack (pickAck) is spoken before the reply — a barge turn's note says so,
  // so the reflex doesn't ack a second time. Only barge turns: their dispatch already waits on Jev; others can't.
  // Claimed only once the engine actually spoke it (instinct.ackFor ← ack_fired, bindAckInstinct): that waits for the
  // ack's due time (adaptiveAckMs), ~150-250ms past the pick — while the ack itself is playing.
  const ackedNote = (said: string | null | undefined) => (said ? `\n[you already said "${said}" out loud to open this reply — do not acknowledge again; go straight on.]` : '');
  // Spoken turns are NAMED so the engine's fuller re-finalization of one (meta.supersedes) replaces it in the
  // duplex instead of running a second turn; the replacement reuses the replaced turn's barge/cut-in context.
  let voiceSeq = 0;
  let lastVoiceTurn: { id: string; text: string; note: Promise<string>; cutIn: string } | undefined;
  let partialRedraw: ReturnType<typeof setTimeout> | null = null;
  // Spin VoiceIO up live (launch with --voice, or /voice mid-session). `greet` opens with a spoken
  // greeting turn (launch only); a manual toggle just turns the mic on quietly. Returns true if voice
  // is now live. Duplex + TTY only — bound to `toggleVoice` below so /voice can flip it off again.
  const startVoice = async (greet: boolean): Promise<boolean> => {
    if (voiceIO) return true;
    if (!duplex || !process.stdin.isTTY) { err(dim('  (voice needs --duplex on a TTY)\n')); return false; }
    // Test seam: AGENTX_VOICE_FAKE=<file> drives the REAL render path with a file-fed STT + silent
    // TTS/player (no mic/audio) — see fakeVoiceParts. Supplying stt+player also skips the AEC helper.
    // It also bypasses the key gate below (the fakes never touch Soniox/Cartesia, so a render test
    // needs only the reflex model's key, not voice-API keys).
    if (!process.env.AGENTX_VOICE_FAKE && !VoiceIO.available()) {
      err(dim('  (voice I/O off — set SONIOX_API_KEY, CARTESIA_API_KEY, CARTESIA_VOICE_ID to talk)\n'));
      return false;
    }
    const fakeVoice = process.env.AGENTX_VOICE_FAKE ? fakeVoiceParts(process.env.AGENTX_VOICE_FAKE) : null;
    // Per-session voice diagnostics: every engine/provider decision event, one JSON line each, next
    // to the session file (<id>.voice.jsonl). Always on (tiny, buffered); a write failure disables
    // the sink silently — it can never take the session down. Debugging a voice session starts here.
    voiceDiag = new JsonlDiagSink(join(store.dir, `${session.meta.id}.voice.jsonl`));
    voiceIO = new VoiceIO({
        ...(fakeVoice ?? {}),
        onDiag: (ev) => voiceDiag?.push(ev),
        emotions: emotionsOn,
        showEmotions, // local is authoritative (a /voice-emotions before mic-on still applies)
        // No FIXED ack phrase by default: a fixed "Mm-hm," every turn reads robotic, and the
        // conversational register already opens with a natural reaction. Instead the ADAPTIVE ack
        // (VoiceIOOptions.adaptiveAckMs, default 600ms — a safety net, not the voice) speaks a short varied ack only when the reflex
        // is actually slow — cancelled by the first delta, a Hold, or a barge-in.
        // When it fires, tell the duplex the turn already spoke, so the dead-air repair (ackIfSilent)
        // doesn't stack a second ack on top.
        onAdaptiveAck: () => dx?.noteExternalSpeech(),
        // Speculative reflex start (VoiceIOOptions.speculativeMs; VOICE_SPECULATIVE=0 disables): a
        // stable partial starts the reflex EARLY with its output held; the final in onUtterance below
        // flows through dispatchLine → dx.send, which confirms (flush, ~500-850ms TTFT saved) or
        // aborts it. Command-shaped partials never speculate (they'd never reach dx.send).
        onSpeculate: (text) => { if (dx && !/^[!#/]/.test(text.trim())) dx.speculate(text); },
        onSpeculateAbort: () => dx?.abortSpeculation(),
        onState: () => editorRef?.redrawNow(),
        // Throttled: each redraw clears the screen below the prompt — a partial-per-token storm
        // (fast speech, or echo bleed if AEC degrades) would continuously erase streamed text.
        onPartial: (text) => {
          if (text === voicePartial) return; // Soniox emits frequent no-change partials — repainting on them flickers the idle prompt
          voicePartial = text;
          if (!partialRedraw) partialRedraw = setTimeout(() => { partialRedraw = null; editorRef?.redrawNow(); }, 250);
        },
        // voiceEchoEnd closes the open echo line; '\r\x1b[0J' wipes the stale prompt/footer before the
        // notice — every other async-chrome writer does this, and without it "✋ interrupted" overprints
        // the footer's leading chars (the "interrupted% ctx" glue).
        // Barge-in also parks any in-flight task's spoken delivery so a superseded topic can't keep talking
        // over the user's new one (debt-after-jokes regression) — the result still lands in the transcript.
        onBargeIn: (phase) => { activeTurn?.abort(); dx?.parkInFlightDeliveries(); voiceReasonEnd(); voiceEchoEnd(); if (phase === 'speaking') err('\r\x1b[0J' + yellow('  ✋ interrupted\n')); },
        onUtterance: (text, meta?: { supersedes?: string }) => {
          voicePartial = '';
          if (!text.trim()) return;
          // Spoken "voice off": deterministic keyword match BEFORE the reflex (no inference, no TTS
          // context leak). Speak a short ack, let it drain, then tear voice down via the /voice toggle.
          if (matchVoiceCommand(text) === 'off') {
            dx?.abortSpeculation(); // no model turn will follow — a held speculation would wedge until its timeout
            err(`\r\x1b[K  ${bold(cyan('🎤 ›'))} ${text}\n`);
            // Capture the instance: /voice during the ~1s ack drain swaps voiceIO — a blind toggle
            // would then flip the user's NEW choice (off→on inversion). Only tear down OUR instance.
            const v = voiceIO!;
            void (async () => { v.speakFiller('Voice off.'); await v.awaitIdle(); if (voiceIO === v) await toggleVoice?.(); })();
            return;
          }
          // Barge-in context: the cut-off reply is in the transcript but the user never HEARD its tail. Hand
          // the model its ORIGINAL intent (full reply + unheard part) and how to proceed — resume after an
          // aside, adapt/drop on a steer. With the instinct on, Jev classifies the interruption (~300ms);
          // otherwise the model decides from the same note.
          // A cut worker DELIVERY (the queued utterances the barge stopped + the unheard rest of the one playing) is parked
          // instead: dx.send's barge-intent judge then resumes it, answers an aside first, or drops it on a steer/stop.
          const replaces = replacedVoiceTurn(lastVoiceTurn, meta); // fuller re-final of the last utterance — only if it IS the last turn
          const { reply: cut, delivery, heard } = takeCut(voiceIO, !replaces); // a replacing re-final consumes the cut too — it is the replaced turn's
          if (delivery || cut) dx?.parkCutDelivery(delivery, !!cut, heard); // a cut reflex reply: its "go on" is the reflex's to answer
          const cutReply = cut && cut.full.length - cut.heard.length > 40 ? cut : null;
          const noteP: Promise<string> = replaces ? replaces.note : !cutReply ? Promise.resolve('') : (instinct
            ? instinct.bargeIntent(text).catch((e) => { log.warn(`barge intent failed — model decides: ${(e as Error).message}`); return undefined; })
            : Promise.resolve(undefined)).then(async (intent) => interruptionNote(cutReply, intent) + ackedNote(await instinct?.ackFor(text)));
          // Kill a pending speculation on paths where dx.send will never see the BARE final text:
          // command lines (no model turn at all) and turns whose content gets augmented (barge note,
          // pasted images) — the speculative call ran without that context, so it must not confirm.
          const cutIn = replaces ? replaces.cutIn : interjectNote; interjectNote = ''; // set only when the instinct chose to cut in
          const id = `v${++voiceSeq}`;
          lastVoiceTurn = { id, text, note: noteP, cutIn };
          if (replaces) replacedVoiceTurns.add(replaces.id);
          if (/^[!#/]/.test(text.trim()) || cutReply || cutIn || replaces || pendingImages.length) dx?.abortSpeculation();
          // Only model-bound turns open a TTS context — a `!`/`#`/`/` line produces no deltas and
          // would leak an open context (state stuck in 'thinking', ack spoken into silence).
          if (!/^[!#/]/.test(text.trim())) voiceIO!.beginSpeech(true); // context + micro-ack NOW (LLM deltas continue it)
          err(`\r\x1b[K  ${bold(cyan('🎤 ›'))} ${text}\n`);
          void noteP.then((note) => dispatchLine(text + note + cutIn, { id, replaces: replaces?.id })).then(async (r) => { if (r === 'quit') { await voiceIO?.awaitIdle(); editorRef?.abort(); } }).finally(() => editorRef?.redrawNow());
        },
    });
    if (instinct) { // AI listener instinct: observe partials/overlaps, act through the live engine
      const v = voiceIO, o = v.options;
      const { onPartial } = o;
      // meta.supersedes: bod-voice ≥ e585034 (the published 0.3.7 types don't carry it yet)
      const onUtterance = o.onUtterance as (text: string, meta?: { supersedes?: string }) => void;
      const listening = () => voiceIO === v && v.state === 'listening' && !v.audible;
      const cutIns = new CutInAbsorber(); // the partial we cut in on — its later endpointed final (and re-finals of it) are absorbed, not re-answered
      o.onPartial = (text) => { if (listening()) instinct.hear(text); onPartial(text); };
      // Overlap: the engine HOLDS a short overlap ("Oh, that") for Jev's yield/keep_talking instead of ceding on its
      // fast ≥2-word rule, which always beat Jev by ~300ms (live 20260923-125143) — see bindOverlapInstinct.
      bindOverlapInstinct(v, instinct, () => voiceIO === v);
      // Adaptive ack: Jev picks a situational ack (or silence) from ACK_BANK at dispatch, keyed on the barge intent
      // after an interruption — instead of the engine's shape-random pools ("Sure, moment." → "Okay, bye.").
      bindAckInstinct(v, instinct);
      o.onUtterance = (text: string, meta?: { supersedes?: string }) => {
        instinct.reset();
        if (cutIns.absorbs(text, meta)) { voicePartial = ''; voiceDiag?.push({ t: performance.now(), kind: 'instinct_final_absorbed', text }); return; }
        onUtterance(text, meta);
      };
      Object.assign(instinct.options, {
        // Engine blip path: throwaway TTS context, no floor claim. Private in bod-voice — upstream a public
        // `backchannel()` if this graduates from the spike. AEC only (the blip would deafen half-duplex).
        onBackchannel: () => { if (!listening() || !v.usingAec) return false; (v as unknown as { fireBackchannel(): void }).fireBackchannel(); },
        onInterject: (heard: string) => {
          if (!listening()) return false;
          cutIns.interjected(heard);
          interjectNote = `\n[you are CUTTING IN — the user is still mid-sentence and has said only this so far. Do it the way a person would: one short natural interjection (a correction, the word they're reaching for, or the answer), then stop and let them continue.]`;
          onUtterance(heard);
        },
      });
    }
    // Mic permission denied → the duplex engine produces no audio either way; tear voice down (kills the
    // STT reconnect spin) and surface the fix. The Settings pane is already opened by the source.
    { // session-start header: models + the engine knobs that shape every decision below
      const vo = voiceIO.options;
      voiceDiag.push({
        t: performance.now(), kind: 'session_start', sessionId: session.meta.id,
        reflexModel: dx?.options.reflexModel, actModel: dx?.options.actModel, thinkModel: dx?.options.thinkModel,
        engine: {
          adaptiveAckMs: vo.adaptiveAckMs, utteranceMergeMs: vo.utteranceMergeMs, incompleteMergeMs: vo.incompleteMergeMs,
          bargeGraceMs: vo.bargeGraceMs, bargeIn: vo.bargeIn, overlapPause: vo.overlapPause, overlapResumeMs: vo.overlapResumeMs, overlapDecideMs: vo.overlapDecideMs,
          speculativeMs: vo.speculativeMs, backchannelMs: vo.backchannelMs, emotions: vo.emotions, ackPick: !!vo.pickAck,
        },
      });
    }
    voiceIO.onFatal = (msg) => {
      err(yellow(`\n  ⚠ voice off — ${msg}\n`));
      if (voiceIO) { voiceIO.stop(); voiceIO = undefined; voicePartial = ''; editorRef?.redrawNow(); }
    };
    try {
      await voiceIO.start();
      // Only the AEC path captures the macOS default input verbatim; the ffmpeg fallback name-picks a real
      // mic (skips BlackHole), so reporting the system default there would be misleading.
      const inDev = voiceIO.usingAec ? detectedInputDevice() : null;
      const inNote = inDev ? `, in: ${inDev.name}` : '';
      err(dim(`  🎤 voice on (${voiceIO.usingAec ? 'echo-cancelled' : 'heuristic echo — headphones recommended'}${inNote}) — just talk; speak over it to interrupt; say "voice off" to stop\n`));
      // No hardware AEC (heuristic tier) → the agent can hear its own TTS. If it's only for lack of swiftc, say so.
      if (!voiceIO.usingAec) { const hint = aecUnavailableHint(); if (hint) err(yellow(`  ⚠ no echo cancellation — ${hint}\n`)); }
      // A virtual/loopback default input (BlackHole, Aggregate, Teams…) carries no mic signal → STT hears silence.
      if (inDev?.virtual) err(yellow(`  ⚠ default input "${inDev.name}" looks like a virtual device — if it can't hear you, switch your Mac input to a real mic\n`));
      if (greet) {
        // Greeting: the agent makes the first turn — spoken, personalized from what it can see.
        // Straight to turn() (not dispatchLine): the synthetic prompt must not enter ↑-history.
        const where = cwd.split('/').pop();
        const resumed = session.messages.length > 0;
        // Resume ≠ cold start: the prior conversation (incl. the memory QuickLook result) is already in
        // context, so DON'T re-fetch memory and DON'T re-open with "what would you like to do" — that
        // resets the thread and reads as a brand-new session. Continue where it left off instead.
        const prompt = resumed
          ? `[session resumed] You're rejoining a live voice conversation already in progress — the full history, including the user's memory/preferences, is already in context, so do NOT call QuickLook again. ` +
            `In one short spoken line, welcome the user back and pick the thread up where it left off: if your last turn ended on an open question or offer, continue it naturally rather than asking what they'd like to do.`
          : `[session started] First call QuickLook with what:"memory" — if it knows the user's name or preferences, use them. ` +
            `Then greet the user warmly in one or two short sentences, as the opener of a live voice conversation. ` +
            `Context: working directory "${where}". Personalize from whatever you learned (memory). Then ask what they'd like to do.`;
        void turn(prompt).finally(() => editorRef?.redrawNow());
      }
      return true;
    } catch (e: any) {
      err(yellow(`  ⚠ voice I/O failed to start: ${e?.message ?? e} — continuing text-only\n`));
      try { voiceIO.stop(); } catch { /* partial start: tear down whatever did come up (STT ws, TTS) */ }
      voiceIO = undefined;
      return false;
    }
  };
  // Child cleanup, registered ONCE (not per start — toggling on/off must not stack listeners). They
  // close over the live `voiceIO`, so they cover whichever instance is up. SIGHUP/SIGTERM (terminal
  // closed, kill) bypass 'exit' handlers by default — without these the mic/player children outlive
  // the CLI and hold the microphone (verified leak in PTY testing).
  if (duplex && process.stdin.isTTY) {
    process.on('exit', () => voiceIO?.stop());
    for (const sig of ['SIGHUP', 'SIGTERM'] as const) process.on(sig, () => { voiceIO?.stop(); process.exit(0); });
  }
  // /voice toggle: flip the mic on or off without leaving the session (kills STT/TTS children on off).
  if (duplex && process.stdin.isTTY) toggleVoice = async () => {
    if (voiceIO) { dx?.abortSpeculation(); voiceIO.stop(); voiceIO = undefined; voicePartial = ''; err(dim('  🔇 voice off — /voice to turn back on\n')); editorRef?.redrawNow(); return; }
    await startVoice(false);
    editorRef?.redrawNow();
  };
  // Launch with --voice: start now, with the spoken greeting (VOICE_GREETING=0 disables it — mirrors
  // the duplex-lab bridge's `greeting` option; the mic still opens, the agent just waits to be spoken to).
  if (args.voice && duplex && process.stdin.isTTY) await startVoice(process.env.VOICE_GREETING !== '0');

  let ctxWarned = 0; // highest context-pressure threshold (80/90) already warned about
  while (true) {
    // Double-Esc fired during the just-finished turn → open the jump-back picker now (turn has unwound).
    if (pendingRewind) { pendingRewind = false; const t = await rewindToMessage(); if (t !== undefined) prefill = t; }
    aborting = false;
    const carry = stashText() ? stashEd!.expand() : ''; stashEd?.reset(); // type-ahead typed (not Enter'd) during the turn → carry it forward
    err('\n'); // blank line before the prompt (the editor renders on one line)
    // Consume the pending jump-back text (once); else fall back to un-submitted type-ahead so a line
    // typed while the turn ran isn't lost — it lands editable in the fresh prompt (CC parity).
    const initial = prefill ?? (carry || undefined); prefill = undefined;
    // Dim status footer under the prompt (context% · cost). Constant while typing one line (transcript is
    // fixed until submit), so compute once per iteration. Hidden on a fresh REPL (no usage yet) to stay clean.
    const ctxTok = estimateTranscriptTokens(face.transcript, face.options.keepRecentImages, face.options.imageInput?.(face.options.model) !== false); // expensive: compute once per iteration (not per keystroke)
    const ctxCap = AgentClass.contextCap(face.options);
    // One-shot threshold warnings at 80% and 90% (CC parity: warn BEFORE the silent auto-trim bites).
    {
      const pct = Math.round((ctxTok / ctxCap) * 100);
      const step = pct >= 90 ? 90 : pct >= 80 ? 80 : 0;
      if (step > ctxWarned) { ctxWarned = step; err(yellow(`  ⚠ context ${pct}% full`) + dim(' — /compact folds older messages (oldest are auto-trimmed at the cap)\n')); }
      else if (!step) ctxWarned = 0; // dropped back under 80% (e.g. /compact) → re-arm

    }
    const usd = session.meta.costUsd ?? 0;
    // Cheap, mutable bits (posture/reasoning/tasks) are read LIVE so Shift+Tab/Alt+T update the footer instantly.
    const computeFooter = () => {
      const parts: string[] = [];
      if (voiceIO) {
        const glyph = { listening: '🎤', thinking: '💭', speaking: '🔊', idle: '·' }[voiceIO.state];
        parts.push(voicePartial && voiceIO.state === 'listening' ? `🎤 ${voicePartial.slice(-60)}` : `${glyph} ${voiceIO.state}`);
      }
      if (ctxTok > 400) parts.push(`${Math.round((ctxTok / ctxCap) * 100)}% ctx (~${(ctxTok / 1000).toFixed(1)}k/${Math.round(ctxCap / 1000)}k)`);
      if (usd > 0) parts.push(`${session.meta.costEstimated ? '~' : ''}${fmtUsd(usd)}`);
      if (posture !== 'default') parts.push(postureLabel());
      const r = work.reasoning; if (r && r !== 'off') parts.push(`reasoning:${r}`);
      if (verboseOutput) parts.push('verbose');
      if (goalCondition) parts.push(`◎ goal (${goalTurns} turns)`);
      if (latestTodos.length) { // compact TodoWrite panel: done-count + the current step (CC Ctrl-T-style)
        const done = latestTodos.filter((t) => t.status === 'completed').length;
        const cur = latestTodos.find((t) => t.status === 'in_progress')?.content;
        if (done < latestTodos.length) parts.push(`☑ ${done}/${latestTodos.length}${cur ? ` · ${cur.slice(0, 48)}` : ''}`);
      }
      if (scheduler.size) parts.push(`⏰ ${scheduler.size} scheduled`);
      if (inputStash.length) parts.push(`${inputStash.length} stashed (⌃S to pop)`);
      // Running background tasks: one STACKED line each, pinned to the prompt block, with an animated
      // spinner frame (statusTickMs re-renders this footer every second) — a slow worker is visibly
      // alive and visibly THERE. Finished tasks drop out (their ⦿ result lands in the flow).
      const taskLines: string[] = [];
      if (dx) {
        const frames = ['⠋', '⠙', '⠹', '⠸', '⠼', '⠴', '⠦', '⠧', '⠇', '⠏'];
        for (const t of dx.tasks.values()) if (t.status === 'running') taskLines.push(`◔ ${frames[tick % frames.length]} ${t.id} ${t.label} — working in background…`);
        if (taskLines.length) tick++;
      }
      return [...taskLines, parts.join(' · ')].filter(Boolean).join('\n');
    };
    // LIVE prompt: while a worker question is pending, the prompt itself becomes the question
    // (CC-parity for permission asks) — duplexAsk's redrawNow() flips it the moment an ask parks.
    const livePrompt = () => {
      const ask = dx?.pendingAsks.size ? dx.pendingAsks.values().next().value : undefined;
      if (!ask) return promptStr;
      const q = ask.question.replace(/\s+/g, ' ').slice(0, 64);
      return bold(yellow(`? ${q}${ask.question.length > 64 ? '…' : ''} ‹yes/no› `));
    };
    const result = await readMultiline((cont) => editor.readLine({
      prompt: cont ? contPrompt : livePrompt, suggest, history, classifyPaste, onEmptyPaste: grabClipboardAttachment,
      onSubmit: () => { dispatchPending = true; }, // claim keys buffered behind the Enter (turn isn't active yet)
      initial: cont ? undefined : initial, status: computeFooter, vimMode: cfg.editorMode === 'vim',
      statusTickMs: dx ? 1000 : undefined, // duplex: animate the running-task footer while idle at the prompt
      // Esc at the idle prompt with workers running → cancel them (CC-style: Esc stops the work).
      onEscapeIdle: dx ? () => {
        const running = [...dx.tasks.values()].filter((t) => t.status === 'running');
        if (!running.length) return false;
        for (const t of running) err('\r\x1b[0J' + yellow(`  ⊘ ${dx.cancelTask(t.id)}`) + '\n');
        return true;
      } : undefined,
      onCyclePosture: cyclePosture,
      onToggleThinking: toggleReasoning,
      onToggleVerbose: toggleVerbose,
      onPickModel: async () => { const picked = await pickModel(work.model); if (picked) setModel(picked); return picked; },
      onStash: (text) => { inputStash.push(text); err(`${green('  ✓ stashed')} ${dim(`#${inputStash.length}: ${text.slice(0, 50)}${text.length > 50 ? '…' : ''}`)}\n`); },
      onUnstash: () => { if (!inputStash.length) return undefined; const t = inputStash.pop()!; err(dim(`  ↑ unstashed${inputStash.length ? ` (${inputStash.length} left)` : ''}\n`)); return t; },
    }));
    if (result === null) break; // Ctrl-D / Ctrl-C at an empty prompt → exit
    if (result === REWIND) { prefill = await rewindToMessage(); continue; } // double-Esc → jump-back picker
    const line = result.trim();
    if (!line) continue;
    // Typed answer to a pending worker question: resolve it DIRECTLY (deterministic, no LLM hop) —
    // same UX as approving in a normal session. Anything else falls through to the conversation.
    if (dx?.pendingAsks.size && /^(y(es|ep|eah)?|n(o|ope)?|sure|ok(ay)?|allow|deny|go( ahead)?)[.!]?$/i.test(line)) {
      const [id, ask] = dx.pendingAsks.entries().next().value!;
      ask.resolve(line);
      err(dim(`  ↳ answered ${id}: ${line}\n`));
      continue;
    }
    let quit = await dispatchLine(line) === 'quit';
    // Drain stashed input (typed while a turn was running) — but ONLY after a real conversational
    // turn. A local meta-command (/cmd, !bash, #note) must not auto-fire the queue: it isn't a turn,
    // and /paste would even fold its just-grabbed clipboard image into the drained message. Queue waits.
    const wasLocal = /^[!#/]/.test(line);
    if (wasLocal) agent.clearInjections(); // a mid-command steer queued nothing to drain it → drop it (no leak into the next turn)
    while (!quit && !wasLocal && inputStash.length) {
      const next = inputStash.shift()!;
      const nview = next.replace(/\n+/g, ' ⏎ ');
      err(dim(`  ⏎ stashed › ${nview.slice(0, 60)}${nview.length > 60 ? '…' : ''}\n`));
      quit = await dispatchLine(next) === 'quit';
    }
    // Drain scheduled prompts that fired while the last turn was running (or idle at the prompt).
    while (!quit && scheduleQueue.length) {
      const prompt = scheduleQueue.shift()!;
      err(dim(`  ⏰ scheduled › ${prompt.slice(0, 60)}${prompt.length > 60 ? '…' : ''}\n`));
      await turn(prompt);
    }
    dispatchPending = false; // dispatch + drains settled — back to the editor owning the keys
    // Persist scheduler state into session (survives --resume).
    (session.meta as any).scheduledJobs = scheduler.snapshot();
    try { store.save(session); } catch { /* non-fatal */ }
    if (quit) break;
  }
  scheduler.destroy();
  triggerServer.stop(); // unlink this session's trigger socket (a stale one would mislead tier-1 routing)
  voiceIO?.stop(); // kill mic/player children + close STT/TTS sockets (they outlive the process otherwise)
  // Duplex: let in-flight workers finish (their re-voice persists the transcript) before tearing down.
  // ExitSession: the user said goodbye — kill workers immediately instead of draining.
  if (dx) {
    const running = [...dx.tasks.values()].filter((t) => t.status === 'running');
    if (exitRequested && running.length) {
      for (const t of running) { t.status = 'cancelled'; t.controller.abort(); }
      err(dim(`  … cancelled ${running.length} background task(s)\n`));
    } else if (running.length) {
      err(dim(`  … waiting for ${running.length} background task(s) (Ctrl-C to force quit)\n`));
      // stdin is still in raw mode here, so Ctrl-C arrives as a 0x03 byte (no SIGINT).
      // Race the drain against a raw Ctrl-C: on press, abort all workers and bail.
      let forced = false;
      let onCtrlC = () => {};
      const onByte = (b: Buffer) => {
        if (!b.includes(0x03)) return; // Ctrl-C
        forced = true;
        for (const t of running) { t.status = 'cancelled'; t.controller.abort(); }
        err(dim(`\n  … force-quit — cancelled ${running.length} background task(s)\n`));
        onCtrlC();
      };
      keyInput.on('data', onByte);
      await Promise.race([dx.idle(), new Promise<void>((res) => { onCtrlC = res; })]);
      keyInput.off('data', onByte);
      if (forced) {
        // User force-quit: tear down and hard-exit — don't trust the event loop to drain
        // (voice children / sockets / MCP handles can keep the process alive otherwise).
        voiceIO?.stop();
        releaseStdin();
        disposeCursorSessions();
        disposeClaudeCodeSessions();
        await closeMcp(mounted);
        process.exit(130);
      }
      (face.options.host as { flushText?: () => void } | undefined)?.flushText?.();
      duplexPersist();
    }
  }
  releaseStdin();
  disposeCursorSessions(); // reap any warm cursor helper (its stdout pipe would otherwise keep the loop alive → hang on exit)
  disposeClaudeCodeSessions(); // same for warm claude-code SDK subprocesses
  await closeMcp(mounted);
}

/** Read all of stdin to EOF (for `echo "prompt" | agentx -p`). */
/** Headless duplex (`--duplex|--voice --input-format stream-json --output-format stream-json`): the voice REPL's
 *  DuplexAgent, driven over NDJSON by a host app that owns its own mic/TTS (e.g. the Bare browser). Same agent build as
 *  the REPL (config, MCP, permissions, --disallowedTools, --append-system-prompt → the workers), same turn path (runTurn:
 *  persistence + the per-turn abort a barge fires), same barge semantics as the CLI's onBargeIn/onUtterance.
 *  stdin:  {type:'user', text, id?, replaces?, followUp?, cut?}  a user line; `id` names it so a later re-final `replaces` it
 *                                                     (`followUp` = its new words alone, or {lineId: words beyond it} for a chain — run instead of the merged text if that turn is kept);
 *                                                     `cut` = the host engine's takeInterruptedReply() (split here, splitCut)
 *          {type:'barge'}                             the user talked over the speech: abort the reflex turn, park deliveries
 *          {type:'cancel', task}                      cancel a background task · {type:'exit'} (or EOF) ends the session
 *  stdout: {type:'ready', sessionId, reflexModel, actModel} · {type:'text'|'thinking', text} (reflex; speak `text` live)
 *          {type:'speak', text} (a worker utterance — speak as its own utterance) · {type:'filler', text}
 *          {type:'task', kind: started|progress|done|error|cancelled|ask…, id, message, data} · {type:'revoice_done'}
 *          {type:'replaced', id, replaces, rolledBack, followed?} (false: the replaced line stays; followed = the kept line whose follow-up ran)
 *          {type:'turn_done', id, finishReason} · {type:'exit'} (the user said goodbye — the host may stop listening) */
async function duplexServe(args: Args, ai: ChatLike, cfg: Partial<AgentConfig>, cwd: string): Promise<void> {
  const out = (o: object) => process.stdout.write(JSON.stringify(o) + '\n');
  const mounted = await mountMcp(cfg, new McpOAuth({ storePath: join(cwd, '.agent', 'mcp-auth.json') }));
  installCancelGuards(mounted);
  const agent = await makeAgent(args, ai, cfg, mcpAgentTools(mounted, { quiet: true }), mounted.map((m) => m.name));
  // Worker template = the built agent minus its host/stream seam (workers never stream into the voice) — as in repl().
  const { host: _h, stream: _s, signal: _g, providerOptions: _po, ...wo } = agent.options;
  const host: HostBridge = {
    notify(e: any) {
      const k = String(e.kind);
      if (k === 'text_delta') out({ type: 'text', text: e.message });
      else if (k === 'thinking_delta') out({ type: 'thinking', text: e.message });
      else if (k === 'speak_utterance') out({ type: 'speak', text: e.message });
      else if (k === 'hold_filler') out({ type: 'filler', text: e.message });
      else if (k === 'turn_replace') out({ type: 'replaced', ...e.data });
      else if (k === 'revoice_done') { out({ type: 'revoice_done' }); persist(); }
      else if (k.startsWith('task_')) out({ type: 'task', kind: k.slice(5), id: e.data?.id, message: e.message, data: e.data });
    },
  };
  let persist = () => {};
  const dx = new DuplexAgent({
    ai,
    fs: agent.options.fs,
    memoryDir: agent.options.memoryDir,
    memoryUserDir: agent.options.memoryUserDir,
    ...((args.voiceModel ?? cfg.reflexModel) ? { reflexModel: resolveModelOrNewest((args.voiceModel ?? cfg.reflexModel)!) } : {}),
    actModel: agent.options.model,
    actOptions: { ...wo, planMode: false } as AgentOptions,
    providerOptionsFor: (m) => providerOptionsFor(m, cwd, cfg.mcpServers, mounted.map((x) => x.name)),
    ...((args.thinkModel ?? cfg.thinkModel) !== undefined ? { thinkModel: (args.thinkModel ?? cfg.thinkModel) === false ? false : resolveModelOrNewest(String(args.thinkModel ?? cfg.thinkModel)) } : {}),
    host,
    // --voice: the CLI voice register (progress asides, spoken budget). No emotion tags: the host's TTS may not speak them.
    ...(args.voice ? { voiceStyle: 'conversational' as const, progressUpdates: true, spokenBudgetChars: 350 } : {}),
    reflexOptions: { fs: agent.options.fs, tools: [exitSessionTool(() => out({ type: 'exit' }))] },
  });
  if (args.voiceSystemPrompt) dx.voice.options.systemPrompt += '\n\n' + args.voiceSystemPrompt;
  const store = new SessionStore(cwd);
  const session = startSession(args, store, dx.voice, cwd);
  persist = () => { session.messages = dx.voice.transcript; session.meta.updated = Date.now(); try { store.save(session); } catch (e) { log.warn('duplex session save failed', e); } };
  out({ type: 'ready', sessionId: session.meta.id, reflexModel: dx.options.reflexModel, actModel: dx.options.actModel });
  const done = new Promise<void>((resolveDone) => {
    const rl = createInterface({ input: process.stdin });
    rl.on('line', (line) => {
      let m: any; try { m = JSON.parse(line); } catch { return; }
      if (m.type === 'barge') { activeTurn?.abort(); dx.parkInFlightDeliveries(); }
      else if (m.type === 'cancel') out({ type: 'task', kind: 'cancel_result', id: m.task, message: dx.cancelTask(String(m.task)) });
      else if (m.type === 'exit') rl.close();
      else if (m.type === 'user' && typeof m.text === 'string' && m.text.trim()) {
        // The CLI's onUtterance, verbatim in effect: a cut worker delivery is parked (the barge-intent path resumes /
        // carries / drops it); a cut REFLEX reply rides the turn as the interruption note ("heard up to…").
        const { reply, delivery, heard } = splitCut(m.cut ?? null);
        if (delivery || reply) dx.parkCutDelivery(delivery, !!reply, heard);
        const note = reply && reply.full.length - reply.heard.length > 40 ? interruptionNote(reply) : '';
        const id = m.id ? String(m.id) : undefined;
        void runTurn(dx.voice, store, session, m.text + note, undefined, cwd, (c) => dx.send(c, { id, replaces: m.replaces ? String(m.replaces) : undefined, followUp: typeof m.followUp === 'string' || (m.followUp && typeof m.followUp === 'object') ? m.followUp : undefined }))
          .then(({ res }) => out({ type: 'turn_done', id, finishReason: res?.finishReason ?? 'error' }))
          .catch((e) => { log.error('duplex turn failed', e); out({ type: 'turn_done', id, finishReason: 'error', error: String(e?.message ?? e) }); });
      }
    });
    rl.on('close', () => resolveDone());
  });
  await done;
  for (const t of dx.tasks.values()) if (t.status === 'running') dx.cancelTask(t.id);
  activeTurn?.abort();
  persist();
  await closeMcp(mounted);
}

function readAllStdin(): Promise<string> {
  return new Promise((res) => {
    let data = '';
    process.stdin.setEncoding('utf8');
    process.stdin.on('data', (c) => (data += c));
    process.stdin.on('end', () => res(data));
    process.stdin.resume();
  });
}

async function main() {
  const args = parseArgs(process.argv.slice(2));
  if (args.version) { console.log('agentx ' + VERSION); return; }
  if (args.help) { console.log(HELP); return; }
  if (args.listModels) { printModels(); return; }
  if (args.update) {
    const msg = await checkForUpdate(VERSION).catch(() => null);
    if (!msg) { console.log('✓ up to date (v' + VERSION + ')'); return; }
    console.log(msg.replace(/Run .+$/, 'Installing…'));
    process.exit(installUpdate() ? 0 : 1);
  }
  if (args.debug) process.env.DEBUG ||= '*'; // --debug/--verbose → enable gated debug/verbose logs (libx forComponent)
  // bare `-p` with piped input → read the whole prompt from stdin (CC parity for `echo "…" | agentx -p`)
  if (args.print && !args.task && !process.stdin.isTTY) args.task = (await readAllStdin()).trim();
  // headless with nothing to do: never fall through to the REPL (stdin is already at EOF there, so it would hang forever).
  if (args.print && !args.task) { console.error(red('  -p: empty prompt (no task argument and nothing on stdin)')); process.exit(2); }
  // --worktree: enter an isolated git worktree before anything reads cwd
  let worktreeCleanup: (() => void) | undefined;
  if (args.worktree) {
    const { enterWorktree, exitWorktree } = await import('../src/worktree');
    const session = enterWorktree(args.worktree, resolve(args.cwd ?? process.cwd()));
    err(dim(`  ⊕ worktree: ${session.worktreePath} (branch ${session.branch})\n`));
    worktreeCleanup = () => {
      const r = exitWorktree();
      if (r?.removed) err(dim('  ⊖ worktree cleaned up (no changes)\n'));
      else if (r && !r.removed) err(dim(`  ⊕ worktree kept: ${r.reason}\n`));
    };
  }
  const cwd = resolve(args.cwd ?? process.cwd());
  const cfg = await loadConfig(cwd);
  // `auto` is the default model: taken when set explicitly in config, or when nothing is pinned and Jev can run here.
  if (!args.autoRoute && !args.model && (cfg.model === 'auto' || (!cfg.model && await jevAvailable()))) { args.autoRoute = true; args.model = autoModels().standard; }
  // fold in rules the user "Always allowed/denied" in earlier sessions (.agent/permissions.json)
  // fold in CC-compatible .claude/settings.json rules, our persisted "Always" rules, and the config block
  // (all merged — deny>allow>ask is decided per-call, not per-source, so concatenation is correct).
  cfg.permissions = mergePerms(loadClaudeSettings(cwd), mergePerms(loadPersistedRules(cwd), cfg.permissions));
  // directory-trust gate (CC-style): on first interactive use of a new dir, confirm once.
  if (canPrompt && !args.yes && !isTrusted(cwd)) {
    const ok = await makeHost().confirm?.(`Trust this directory? agent.libx will read and edit files in:\n  ${cwd}`);
    if (!ok) { console.error(red('  not trusted — exiting (re-run with --yes to skip this prompt).')); process.exit(1); }
    trustDir(cwd);
  }
  // bare `--resume`/`-r` (no id) → interactive session picker, like CC (needs a TTY + an id when piped).
  if (args.resume === '') {
    const picker = new SessionStore(cwd).list();
    if (!picker.length) { console.error(yellow('  no saved sessions in this directory.')); process.exit(1); }
    if (!canPrompt) { console.error(red('  --resume needs an id when not interactive: --resume <id> (latest: ' + picker[0].id + ')')); process.exit(1); }
    const id = await selectMenu(process.stderr, { title: 'Resume a session', items: picker.slice(0, 50).map((m) => ({ label: `${m.id}  ${m.title || '(untitled)'}`, value: m.id, desc: `${m.turns} turn${m.turns === 1 ? '' : 's'}` })) });
    if (!id) process.exit(0); // cancelled
    args.resume = id;
  }
  loadInstallEnv(); // fallback: pick up keys from agentx's own .env when launched elsewhere (won't override CWD/shell)
  await loadBodifySecrets(); // optional: pull secrets from a Bodify app (BODIFY_API_KEY + BODIFY_APP_ID)
  const apiKeys = { ...cfg.apiKeys, ...apiKeysFromEnv() }; // env wins
  if (!Object.keys(apiKeys).length) {
    console.error(red('No provider key found. Set ANTHROPIC_API_KEY (or OPENAI_API_KEY / GOOGLE_API_KEY / GROQ_API_KEY) in ~/.agent/.env, or set BODIFY_API_KEY + BODIFY_APP_ID to pull from Bodify.'));
    process.exit(1);
  }
  // AIClient is the concrete impl of the runtime's `ChatLike` seam (the CLI only needs that seam).
  // retry: transient 429/5xx blips auto-recover with backoff; surface it so a pause isn't a mystery.
  const ai = new AIClient({
    apiKeys,
    ...(cfg.baseUrls ? { baseUrls: cfg.baseUrls } : {}),
    retry: {
      onRetry: ({ attempt, delayMs, error }: { attempt: number; delayMs: number; error: any }) => {
        const why = error?.name === 'RateLimitError' || error?.status === 429 ? 'rate-limited' : 'service busy';
        err(yellow(`\n  ⟳ ${why} — retrying (#${attempt}) in ~${Math.max(1, Math.round(delayMs / 1000))}s…\n`));
      },
    },
  }) as unknown as ChatLike;

  if (args.task) {
    // one-shot / headless — still persisted so it can be --continue'd later. OAuth refresh works here
    // (no browser); only attended registration needs the REPL `/mcp login`.
    const mounted = await mountMcp(cfg, new McpOAuth({ storePath: join(cwd, '.agent', 'mcp-auth.json') }));
    const agent = await makeAgent(args, ai, cfg, mcpAgentTools(mounted), mounted.map((m) => m.name));
    const store = new SessionStore(cwd);
    const session = startSession(args, store, agent, cwd);
    process.once('SIGINT', () => activeTurn?.abort()); // Ctrl-C cancels the run; it still persists + exits cleanly
    const { ok, res } = await runTurn(agent, store, session, args.task, undefined, cwd, autoRouteSend(args, agent, () => session, (m) => providerOptionsFor(m, cwd, cfg.mcpServers, mounted.map((x) => x.name))));
    // opt-in: on a bad outcome, reflect once and persist a novel lesson for next session.
    // `needs_input` is excluded — it's a PAUSE awaiting an answer, not a mistake to learn from
    // (reflecting would bill a critique pass and risk persisting "don't ask questions" as a lesson).
    if (cfg.reflectOnFailure && !ok && res?.finishReason !== 'needs_input' && res && agent.options.memoryDir) {
      const _fsBase = agent.options.fs!.getCwd() === '/' ? '' : agent.options.fs!.getCwd();
      const slug = await reflectOnRun({ ai, model: agent.options.model, fs: agent.options.fs!, dir: primaryMemDir(agent.options.memoryDir, `${_fsBase}/.agent/memory`), result: res });
      if (slug) err(dim(`  ✎ learned a lesson → ${slug}\n`));
    }
    await closeMcp(mounted); // kill MCP child processes after the run (and any reflection)
    if (args.outputFormat === 'json' || args.outputFormat === 'stream-json') {
      const obj = res ? jsonResult(res, session) : { ok: false, finishReason: 'error', sessionId: session.meta.id };
      // json → the single result object; stream-json → a final {type:'result',…} line closing the NDJSON stream
      process.stdout.write(JSON.stringify(args.outputFormat === 'stream-json' ? { type: 'result', ...obj } : obj) + '\n');
    }
    worktreeCleanup?.();
    // exit 2 = needs_input: a distinct code so a shell caller can tell "it has a question for you"
    // (answer + --resume) from "it failed" (exit 1). Both stay non-zero, so `set -e` still trips.
    process.exit(ok ? 0 : res?.finishReason === 'needs_input' ? 2 : 1);
  }

  if (args.inputFormat) { await duplexServe(args, ai, cfg, cwd); worktreeCleanup?.(); process.exit(0); } // like -p: don't linger on provider handles
  await repl(args, ai, cfg, cwd); // --duplex is a mode of the same REPL (fast voice + delegated workers)
  worktreeCleanup?.();
}

// Only auto-run when invoked as the entry point — keeps the module importable in tests.
if (import.meta.main) main().catch((e) => { console.error(e?.message ?? e); process.exit(1); });
